import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloud, faServer, faDollarSign, faShieldHalved, faFileLines,
  faChartLine, faStop, faUndo, faPause, faEye, faEdit, faCopy,
  faBookmark, faShare, faPaperPlane, faShield, faDownload,
  faExclamationTriangle, faWrench, faBell, faRocket,
  faCode, faCog, faChartBar, faDatabase, faNetworkWired, faLock,
  faPlay, faSync, faHistory, faCheck, faTimes, faSpinner, faPlus,
  faTrash, faKey, faExternalLinkAlt, faClipboard, faSave, faUsers, faLightbulb,
  faChevronDown, faChevronRight, faClock, faBug, faLifeRing, faMicrochip, faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub, faJenkins } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { streamingEnhancer, EnhancedStreamingCallbacks } from './services/streamingEnhancer';
import AccountManager from './components/AccountManager';
import EKSWizard from './components/EKSWizard';
import ExecutionPlan from './components/ExecutionPlan';
import ClusterDetailsModal from './components/ClusterDetailsModal';
import UpgradeWizardModal from './components/UpgradeWizardModal';
import UpgradeProgressModal from './components/UpgradeProgressModal';
import { navigationState, ClusterView } from './utils/navigationState';
import { ClusterDetailsPage } from './pages/ClusterDetailsPage';
import { ClusterUpgradePage } from './pages/ClusterUpgradePage';
import InfrastructureTab from './tabs/infrastructure';
import MonitoringTab from './tabs/monitoring';
import CostAnalysisTab from './tabs/cost-analysis';
import SecurityTab from './tabs/security';
import ResourcesTab from './tabs/resources';
import GPUsTab from './tabs/gpus';
import SupportTab from './tabs/support';
import BYOIPTab from './tabs/byoip/BYOIPTab';
import { RecommendationsPanel } from './tabs/cost-analysis/components/RecommendationsPanel';
import { RecommendationsPanelV1 } from './tabs/cost-analysis/components/RecommendationsPanelV1';
import { DeployRecommendationModal } from './tabs/cost-analysis/components/DeployRecommendationModal';
import { PreApprovedDiscounts } from './tabs/cost-analysis/components/PreApprovedDiscounts';
import { SavingsDashboard } from './tabs/cost-analysis/components/SavingsDashboard';
import ProfileDropdown from './components/Profile';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
  tokens?: number;
  metadata?: any;
  structured?: {
    summary?: string;
    finalResult?: boolean;
    rawResponse?: string;
    sections?: Array<{
      title: string;
      status: string;
      items: Array<{
        text: string;
        status: string;
      }>;
    }>;
    actions?: Array<{
      label: string;
      type: string;
      icon: string;
    }>;
  };
  clarificationRequest?: {
    clarifications: string[];
    originalQuery: string;
    sessionId: string;
  };
  isGreeting?: boolean;
  errorCode?: number;
  suggestions?: Array<{ label: string; query: string }>;
  securityScanList?: Array<{
    scanId: string;
    scanTimestamp: string;
    summary: { totalVulnerabilities: number; severitySplit: { critical: number; high: number; medium: number; low: number } };
  }>;
  securityScanLabel?: string;
  securityScanTotal?: number;
  securityScanTrend?: { from: number; to: number };
  securityNoScans?: { label: string };
  securityScanInitiated?: boolean;
}

interface Conversation {
  id: string;              // conversationId (UUID from backend)
  title: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
  sessionId: string;       // Backend session ID (conv_{uuid}) for message storage
  messagesLoaded?: boolean; // Whether messages have been fetched from backend
  messageCount?: number;   // Backend-provided count (shown in sidebar before messages are loaded)
}

interface AccountStatus {
  aws: number;
  gcp: number;
  github: number;
  jenkins: number;
  total: number;
}

interface ConnectedAccount {
  id: string;
  name: string;
  type: 'aws' | 'gcp' | 'github' | 'jenkins';
  status: 'active' | 'inactive' | 'error';
  connectionMethod?: 'iam' | 'cloudformation' | 'oauth' | 'token';
  accountId?: string;
  region?: string;
  lastSync?: string;
  config?: any;
}

interface AWSAccountConfig {
  accessKey?: string;
  secretKey?: string;
  roleArn?: string;
  externalId?: string;
  region: string;
  accountId: string;
  organizationId?: string;
  subAccounts?: string[];
}

interface GCPAccountConfig {
  projectId: string;
  serviceAccountEmail: string;
  keyFile?: string;
  region: string;
}

interface GitHubAccountConfig {
  organization: string;
  token: string;
  repositories?: string[];
}

interface JenkinsAccountConfig {
  url: string;
  username: string;
  token: string;
  jobs?: string[];
}

interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'debug';
  source: 'frontend' | 'backend' | 'mcp';
  component?: string;
  message: string;
  data?: any;
}

interface VisualizationData {
  id: string;
  type: 'cost-chart' | 'resource-table' | 'instance-list' | 'cost-breakdown' | 'pie-chart' | 'error';
  title: string;
  data: any;
  timestamp: Date;
  query: string;
}

// Function to clean ANSI escape codes and format text
function cleanAnsiCodes(text: string): string {
  // Remove ANSI escape codes
  return text.replace(/\x1b\[[0-9;]*m/g, '').replace(/\[0m/g, '');
}

// Helper functions to extract specific parameters from queries
function extractTimePeriod(query: string): string {
  const lower = query.toLowerCase();

  // Specific months
  const months = ['january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'];
  for (const month of months) {
    if (lower.includes(month)) {
      return month.charAt(0).toUpperCase() + month.slice(1);
    }
  }

  // Short month names
  const shortMonths = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  for (let i = 0; i < shortMonths.length; i++) {
    if (lower.includes(shortMonths[i])) {
      return months[i].charAt(0).toUpperCase() + months[i].slice(1);
    }
  }

  // Time periods
  if (lower.includes('last month')) return 'last month';
  if (lower.includes('this month')) return 'this month';
  if (lower.includes('last quarter')) return 'last quarter';
  if (lower.includes('this quarter')) return 'this quarter';
  if (lower.includes('last year')) return 'last year';
  if (lower.includes('this year')) return 'this year';
  if (lower.includes('ytd') || lower.includes('year to date')) return 'year to date';

  return '';
}

function extractRegions(query: string): string[] {
  const lower = query.toLowerCase();
  const regions: string[] = [];

  // Specific region mentions
  const regionMap: { [key: string]: string } = {
    'us-east-1': 'us-east-1 (N. Virginia)',
    'us-east-2': 'us-east-2 (Ohio)',
    'us-west-1': 'us-west-1 (N. California)',
    'us-west-2': 'us-west-2 (Oregon)',
    'eu-west-1': 'eu-west-1 (Ireland)',
    'eu-central-1': 'eu-central-1 (Frankfurt)',
    'ap-southeast-1': 'ap-southeast-1 (Singapore)',
    'ap-northeast-1': 'ap-northeast-1 (Tokyo)',
    'virginia': 'us-east-1 (N. Virginia)',
    'ohio': 'us-east-2 (Ohio)',
    'oregon': 'us-west-2 (Oregon)',
    'california': 'us-west-1 (N. California)',
    'ireland': 'eu-west-1 (Ireland)',
    'frankfurt': 'eu-central-1 (Frankfurt)',
    'singapore': 'ap-southeast-1 (Singapore)',
    'tokyo': 'ap-northeast-1 (Tokyo)'
  };

  for (const [key, value] of Object.entries(regionMap)) {
    if (lower.includes(key)) {
      if (!regions.includes(value)) {
        regions.push(value);
      }
    }
  }

  // Generic region indicators
  if (lower.includes('all regions')) regions.push('all regions');
  if (lower.includes('us regions') || lower.includes('us-')) regions.push('US regions');
  if (lower.includes('eu regions') || lower.includes('europe')) regions.push('EU regions');
  if (lower.includes('ap regions') || lower.includes('asia')) regions.push('Asia Pacific regions');

  return regions;
}

function extractAccounts(query: string): string[] {
  const lower = query.toLowerCase();
  const accounts: string[] = [];

  // Account types
  if (lower.includes('production') || lower.includes('prod')) accounts.push('Production');
  if (lower.includes('staging') || lower.includes('stage')) accounts.push('Staging');
  if (lower.includes('development') || lower.includes('dev')) accounts.push('Development');
  if (lower.includes('test')) accounts.push('Test');

  // Multi-account indicators
  if (lower.includes('all accounts')) accounts.push('all accounts');
  if (lower.includes('multi account') || lower.includes('multiple accounts')) accounts.push('multiple accounts');

  return accounts;
}

// Helper functions for generating contextual text
function getRegionText(regions: string[]): string {
  if (regions.length === 0) return '';
  if (regions.includes('all regions')) return ' across all AWS regions (16+ regions)';
  if (regions.length === 1) return ` for ${regions[0]}`;
  return ` across ${regions.length} regions`;
}

function getRegionAnalysisText(regions: string[]): string {
  if (regions.length === 0) return ' by region';
  if (regions.includes('all regions')) return ' across all AWS regions (16+ regions)';
  if (regions.length === 1) return ` in ${regions[0]}`;
  return ` across ${regions.length} regions`;
}

function getAccountText(accounts: string[]): string {
  if (accounts.length === 0) return 's';
  if (accounts.includes('all accounts')) return 's (all accounts)';
  if (accounts.length === 1) return ` (${accounts[0]} account)`;
  return ` (${accounts.length} accounts)`;
}

// Detect known external service errors and return clean business error { code, message } or null
function getBusinessError(errorStr: string): { code: number; message: string } | null {
  if (!errorStr) return null;
  if (errorStr.includes('insufficient_quota') || errorStr.includes('exceeded your current quota') || errorStr.includes('Error code: 429')) {
    return { code: 9001, message: 'AI service temporarily unavailable. Please try again later or contact support.' };
  }
  if (errorStr.includes('invalid_api_key') || errorStr.includes('Incorrect API key') || errorStr.includes('Error code: 401')) {
    return { code: 9002, message: 'AI service configuration error. Please contact support.' };
  }
  return null;
}

// Function to analyze intent and create task breakdown
// Reconstruct chat history from MongoDB documents into frontend Message[] and VisualizationData[]
function reconstructChatHistory(
  dbMessages: Array<{ id: string; role: string; content: any; timestamp: string }>
): { messages: any[]; visualizations: any[] } {
  const messages: any[] = [];
  const visualizations: any[] = [];

  let currentTodos: any[] = [];
  let currentRawResponse = '';
  let currentUserQuery = '';
  let pendingFlush = false;

  const flushAssistant = () => {
    if (!pendingFlush) return;

    if (currentRawResponse || currentTodos.length > 0) {
      try {
        // Parse raw MCP content to rebuild green structured sections
        const { parsed, structured } = parseResponseContent(currentRawResponse, currentUserQuery, false);

        const sections: any[] = [];

        // Execution Plan from todos
        if (currentTodos.length > 0) {
          sections.push({
            title: '\ud83d\udccb Execution Plan',
            status: 'complete',
            items: [
              { text: 'Analyzing query and creating execution plan', status: 'complete' },
              { text: 'Connecting to AWS services', status: 'complete' },
              ...currentTodos.map((todo: any) => ({
                text: todo.todo || todo.task || todo,
                status: 'complete'
              }))
            ]
          });
        }

        // Cost Breakdown / Insights from parsed content
        if (structured?.sections) {
          structured.sections.forEach((section: any) => {
            if (!sections.some(s => s.title === section.title)) {
              sections.push(section);
            }
          });
        }

        messages.push({
          id: `hist_asst_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          type: 'assistant',
          content: parsed || currentRawResponse,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          structured: sections.length > 0 ? {
            summary: structured?.summary || currentUserQuery,
            rawResponse: currentRawResponse,
            sections,
            finalResult: structured || undefined
          } : undefined
        });
      } catch (err) {
        // If parsing fails, still show the raw response so messages aren't lost
        console.error('[reconstructChatHistory] flushAssistant parse error, falling back to raw:', err);
        messages.push({
          id: `hist_asst_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          type: 'assistant',
          content: currentRawResponse || '(Response could not be fully reconstructed)',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        });
      }
    }

    currentTodos = [];
    currentRawResponse = '';
    currentUserQuery = '';
    pendingFlush = false;
  };

  for (const doc of dbMessages) {
    switch (doc.role) {
      case 'user':
        flushAssistant();
        currentUserQuery = typeof doc.content === 'string' ? doc.content : '';
        messages.push({
          id: `hist_user_${doc.id}`,
          type: 'user',
          content: typeof doc.content === 'string' ? doc.content : JSON.stringify(doc.content),
          timestamp: new Date(doc.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        });
        pendingFlush = true;
        break;

      case 'todo':
        currentTodos = Array.isArray(doc.content) ? doc.content : [];
        break;

      case 'mcp_server':
        currentRawResponse += doc.content?.response || (typeof doc.content === 'string' ? doc.content : '');
        break;

      case 'thinker':
        currentRawResponse += typeof doc.content === 'string' ? doc.content : '';
        break;

      case 'assistant': {
        const asstContent = typeof doc.content === 'string' ? doc.content
          : (doc.content != null ? JSON.stringify(doc.content) : '');

        // Detect greeting messages — they are short, have no MCP data, and match known patterns
        const greetingPatterns = [
          "I'm your DevOpsX AI assistant",
          "How can I assist you with your cloud infrastructure",
          "Ready to help you optimize and manage your AWS",
          "I'm here to help with AWS cost analysis",
          "All systems operational"
        ];
        const isGreetingMsg = greetingPatterns.some(p => asstContent.includes(p));

        if (isGreetingMsg) {
          flushAssistant();
          messages.push({
            id: `hist_asst_${doc.id}`,
            type: 'assistant',
            content: asstContent,
            timestamp: new Date(doc.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isGreeting: true
          });
          pendingFlush = false;
        } else if (!currentRawResponse) {
          currentRawResponse = asstContent;
        }
        break;
      }

      case 'visualization': {
        const viz = doc.content;
        visualizations.push({
          id: `hist_viz_${doc.id}`,
          type: 'cost-breakdown',
          title: `Query Result: ${viz.query || 'Unknown'}`,
          data: viz.sseType === 'cost_comparison'
            ? { ...viz.data, queryType: 'cost_comparison' }
            : viz.data,
          timestamp: new Date(doc.timestamp),
          query: viz.query || ''
        });
        break;
      }
    }
  }

  // Flush last assistant turn
  flushAssistant();

  return { messages, visualizations };
}

function analyzeIntentAndCreateTasks(query: string): any {
  const lower = query.toLowerCase();
  console.log('🎯🎯🎯 ANALYZING INTENT FOR QUERY:', query);
  console.log('🎯 Lowercase query:', lower);

  // COST ANALYSIS INTENT - Check this FIRST before server listing
  // If query contains cost-related keywords, it's a cost query regardless of other keywords
  const isCostQuery = lower.includes('cost') || lower.includes('billing') || lower.includes('spend') ||
    lower.includes('price') || lower.includes('budget') || lower.includes('expense');
  console.log('💰 Is cost query?', isCostQuery);

  if (isCostQuery) {
    console.log('💰 Processing as cost analysis intent');

    // Extract specific parameters from query
    const timeperiod = extractTimePeriod(query);
    const regions = extractRegions(query);
    const accounts = extractAccounts(query);

    // Analyze user's specific intent for dynamic task generation
    const wantsTotal = lower.includes('total') || lower.includes('overall') || lower.includes('sum') || lower.match(/how much.*cost|what.*cost|cost.*total/);
    const wantsBreakdown = lower.includes('breakdown') || lower.includes('break down') || lower.includes('detailed') || lower.includes('itemized') || lower.includes('split');
    const wantsGroupBy = lower.includes('group by') || lower.includes('by service') || lower.includes('by account') || lower.includes('by region') || lower.includes('grouped');
    const wantsComparison = lower.includes('compare') || lower.includes('vs') || lower.includes('versus') || lower.includes('difference') || lower.includes('compared to');
    const wantsOptimization = lower.includes('optimize') || lower.includes('save') || lower.includes('reduce') || lower.includes('recommend') || lower.includes('cheaper');
    const wantsTrends = lower.includes('trend') || lower.includes('over time') || lower.includes('monthly') || lower.includes('daily') || lower.includes('growth') || lower.includes('increase');
    const wantsSpecificService = lower.includes('ec2') || lower.includes('s3') || lower.includes('rds') || lower.includes('lambda');
    const wantsOnlySpecific = lower.includes('only') || lower.includes('just') || lower.includes('specifically');
    const wantsTop = lower.includes('top') || lower.includes('highest') || lower.includes('most expensive') || lower.includes('biggest');
    const wantsUsage = lower.includes('usage') || lower.includes('utilization') || lower.includes('consumption');

    // Detect specific services mentioned
    let specificServices = [];
    if (lower.includes('ec2')) specificServices.push('EC2');
    if (lower.includes('s3')) specificServices.push('S3');
    if (lower.includes('rds')) specificServices.push('RDS');
    if (lower.includes('lambda')) specificServices.push('Lambda');
    if (lower.includes('cloudfront')) specificServices.push('CloudFront');

    // Generate dynamic intent based on user's specific request
    let intentParts = [];

    if (wantsTotal) {
      intentParts.push(wantsOnlySpecific ? 'Get total cost for' : 'Get total AWS costs');
    } else if (wantsBreakdown && wantsGroupBy) {
      if (lower.includes('by service')) {
        intentParts.push('Break down AWS costs by service');
      } else if (lower.includes('by account')) {
        intentParts.push('Break down AWS costs by account');
      } else if (lower.includes('by region')) {
        intentParts.push('Break down AWS costs by region');
      } else {
        intentParts.push('Break down and group AWS costs');
      }
    } else if (wantsBreakdown) {
      intentParts.push('Break down AWS costs in detail');
    } else if (wantsComparison) {
      intentParts.push('Compare AWS costs');
    } else if (wantsOptimization) {
      intentParts.push('Analyze AWS costs for optimization opportunities');
    } else if (wantsTrends) {
      intentParts.push('Analyze AWS cost trends');
    } else if (wantsTop) {
      intentParts.push('Find top AWS cost drivers');
    } else if (wantsUsage && specificServices.length > 0) {
      intentParts.push(`Analyze ${specificServices.join(' + ')} usage and costs`);
    } else if (specificServices.length > 0) {
      if (wantsOnlySpecific) {
        intentParts.push(`Get ${specificServices.join(' + ')} costs only`);
      } else {
        intentParts.push(`Analyze ${specificServices.join(' + ')} costs`);
      }
    } else {
      intentParts.push('Analyze AWS costs');
    }

    if (timeperiod) intentParts.push(`for ${timeperiod}`);
    if (regions.length > 0) {
      if (regions.includes('all regions')) {
        intentParts.push('across all regions');
      } else {
        intentParts.push(`in ${regions.join(', ')}`);
      }
    }

    const dynamicIntent = intentParts.join(' ');
    console.log('💰 Generated intent:', dynamicIntent);

    // Generate DYNAMIC tasks based on user's specific request
    const tasks = [];
    let taskId = 1;

    // Step 1: Always connect to Cost Explorer
    tasks.push({ id: taskId++, name: 'Connect to AWS Cost Explorer', status: 'pending' });

    // Step 2: Query setup based on timeframe and filters
    let queryDescription = 'Set up cost query';
    if (timeperiod) queryDescription += ` for ${timeperiod}`;
    if (specificServices.length > 0) queryDescription += ` filtering for ${specificServices.join(', ')} services`;
    if (regions.length > 0) queryDescription += ` in ${regions.join(', ')} region${regions.length > 1 ? 's' : ''}`;
    tasks.push({ id: taskId++, name: queryDescription, status: 'pending' });

    // Step 3: Specific analysis based on user intent
    if (wantsTotal) {
      if (specificServices.length > 0) {
        tasks.push({ id: taskId++, name: `Calculate total ${specificServices.join(' + ')} costs`, status: 'pending' });
      } else {
        tasks.push({ id: taskId++, name: 'Calculate total AWS cost amount', status: 'pending' });
      }
    } else if (wantsBreakdown || wantsGroupBy) {
      if (lower.includes('by service') || (lower.includes('service') && wantsGroupBy)) {
        tasks.push({ id: taskId++, name: 'Group and analyze costs by AWS service type', status: 'pending' });
      } else if (lower.includes('by account')) {
        tasks.push({ id: taskId++, name: 'Group and analyze costs by AWS account', status: 'pending' });
      } else if (lower.includes('by region')) {
        tasks.push({ id: taskId++, name: 'Group and analyze costs by AWS region', status: 'pending' });
      } else if (specificServices.length > 0) {
        tasks.push({ id: taskId++, name: `Break down ${specificServices.join(' and ')} costs in detail`, status: 'pending' });
      } else {
        tasks.push({ id: taskId++, name: 'Break down costs by service category', status: 'pending' });
      }
    } else if (wantsComparison) {
      if (timeperiod) {
        tasks.push({ id: taskId++, name: `Compare ${timeperiod} costs with previous periods`, status: 'pending' });
      } else {
        tasks.push({ id: taskId++, name: 'Compare costs across time periods', status: 'pending' });
      }
    } else if (wantsTrends) {
      tasks.push({ id: taskId++, name: 'Analyze cost trends and patterns over time', status: 'pending' });
    } else if (wantsTop) {
      if (specificServices.length > 0) {
        tasks.push({ id: taskId++, name: `Identify top cost drivers within ${specificServices.join(' and ')} services`, status: 'pending' });
      } else {
        tasks.push({ id: taskId++, name: 'Identify top cost drivers and expensive services', status: 'pending' });
      }
    } else if (wantsUsage && specificServices.length > 0) {
      tasks.push({ id: taskId++, name: `Analyze ${specificServices.join(' and ')} usage patterns and cost correlation`, status: 'pending' });
    } else if (specificServices.length > 0) {
      if (wantsOnlySpecific) {
        tasks.push({ id: taskId++, name: `Extract only ${specificServices.join(' and ')} cost data`, status: 'pending' });
      } else {
        tasks.push({ id: taskId++, name: `Retrieve and analyze ${specificServices.join(' and ')} cost data`, status: 'pending' });
      }
    } else {
      tasks.push({ id: taskId++, name: 'Retrieve comprehensive cost data and metrics', status: 'pending' });
    }

    // Step 4: Processing and formatting
    if (wantsBreakdown || wantsGroupBy) {
      if (lower.includes('by service')) {
        tasks.push({ id: taskId++, name: 'Format service-wise cost breakdown table', status: 'pending' });
      } else if (lower.includes('by account')) {
        tasks.push({ id: taskId++, name: 'Format account-wise cost breakdown table', status: 'pending' });
      } else if (lower.includes('by region')) {
        tasks.push({ id: taskId++, name: 'Format region-wise cost breakdown table', status: 'pending' });
      } else {
        tasks.push({ id: taskId++, name: 'Format detailed cost breakdown with grouping', status: 'pending' });
      }
    } else if (wantsTotal) {
      tasks.push({ id: taskId++, name: 'Format total cost summary', status: 'pending' });
    } else {
      tasks.push({ id: taskId++, name: 'Process and format cost analysis report', status: 'pending' });
    }

    // Step 5: Additional analysis if requested
    if (wantsOptimization) {
      tasks.push({ id: taskId++, name: 'Generate personalized cost optimization recommendations', status: 'pending' });
    } else if (wantsTrends) {
      tasks.push({ id: taskId++, name: 'Create cost trend analysis with insights', status: 'pending' });
    } else if (wantsComparison) {
      tasks.push({ id: taskId++, name: 'Highlight cost differences and percentage changes', status: 'pending' });
    } else if (wantsTop) {
      tasks.push({ id: taskId++, name: 'Rank and present top cost contributors', status: 'pending' });
    } else if (wantsUsage) {
      tasks.push({ id: taskId++, name: 'Correlate usage metrics with cost data', status: 'pending' });
    } else {
      tasks.push({ id: taskId++, name: 'Provide comprehensive cost insights and summary', status: 'pending' });
    }

    const result = {
      type: 'cost_analysis',
      intent: dynamicIntent,
      timeperiod,
      regions,
      accounts,
      tasks
    };
    console.log('💰 Final cost analysis result:');
    console.log('  Intent:', dynamicIntent);
    console.log('  Tasks:', tasks.map(t => t.name));
    console.log('  Analysis flags:', { wantsTotal, wantsBreakdown, wantsGroupBy, wantsComparison, wantsOptimization, wantsTrends, wantsTop, wantsUsage, wantsOnlySpecific });
    console.log('  Services:', specificServices);
    return result;
  }

  // Server listing intent - Only if NOT a cost query
  const isServerQuery = (lower.includes('list') || lower.includes('show') || lower.includes('get')) &&
    (lower.includes('server') || lower.includes('instance')) &&
    !lower.includes('cost') && !lower.includes('billing') && !lower.includes('spend');
  console.log('🖥️ Is server query?', isServerQuery);

  if (isServerQuery) {
    console.log('🖥️ Processing as server listing intent');

    let region = 'ap-south-1';
    if (lower.includes('mumbai') || lower.includes('ap-south')) region = 'ap-south-1';
    if (lower.includes('virginia') || lower.includes('us-east')) region = 'us-east-1';

    return {
      type: 'server_listing',
      intent: `List all servers in ${region} region`,
      region: region,
      tasks: [
        { id: 1, name: 'Connect to AWS EC2 service', status: 'pending' },
        { id: 2, name: `Query instances in ${region} region`, status: 'pending' },
        { id: 3, name: 'Filter running vs stopped instances', status: 'pending' },
        { id: 4, name: 'Retrieve instance details (type, name, status)', status: 'pending' },
        { id: 5, name: 'Format response with server information', status: 'pending' }
      ]
    };
  }

  // Deployment intent
  if (lower.includes('deploy') || lower.includes('provision') || lower.includes('create')) {
    return {
      type: 'deployment',
      intent: 'Deploy infrastructure across AWS accounts',
      tasks: [
        { id: 1, name: 'Validate deployment requirements', status: 'pending' },
        { id: 2, name: 'Create deployment plan', status: 'pending' },
        { id: 3, name: 'Deploy to production account (us-east-1)', status: 'pending' },
        { id: 4, name: 'Deploy to staging account (us-west-2)', status: 'pending' },
        { id: 5, name: 'Configure monitoring and alerts', status: 'pending' }
      ]
    };
  }

  // DEFAULT: For any other query, create a generic task breakdown
  console.log('🎯 Creating default task breakdown for query:', query);
  return {
    type: 'general_query',
    intent: `Process query: ${query}`,
    tasks: [
      { id: 1, name: 'Connect to MCP server', status: 'pending' },
      { id: 2, name: 'Process query request', status: 'pending' },
      { id: 3, name: 'Retrieve data from sources', status: 'pending' },
      { id: 4, name: 'Format and structure response', status: 'pending' },
      { id: 5, name: 'Return results to user', status: 'pending' }
    ]
  };
}

// Helper function to validate service names
function isValidServiceName(serviceName: string): boolean {
  const lower = serviceName.toLowerCase();
  return (
    serviceName.length > 1 &&
    !lower.includes('total') &&
    !lower.includes('cost') &&
    !lower.includes('here are') &&
    !lower.includes('breakdown') &&
    !lower.includes('period') &&
    !lower.includes('summary') &&
    !lower.includes('july') &&
    !lower.includes('month') &&
    !serviceName.match(/^[\d\s\-\.]+$/) && // Skip pure numbers/dates
    !serviceName.includes('\n') &&
    serviceName.trim().length > 0 &&
    serviceName.length < 100 // Reasonable service name length
  );
}

// Helper function to validate cost values
function isValidCost(cost: string): boolean {
  const numericCost = parseFloat(cost.replace(/[\$,]/g, ''));
  return !isNaN(numericCost) && numericCost >= 0;
}

// Enhanced function to extract structured information with better streaming support
function extractStructuredInfo(content: string, userQuery?: string, isStreaming: boolean = false): any {
  const cleaned = cleanAnsiCodes(content);
  const lower = cleaned.toLowerCase();

  // For streaming responses, be more lenient about incomplete data
  const minContentLength = isStreaming ? 50 : 200;
  if (cleaned.length < minContentLength && isStreaming) {
    console.log('🔄 Streaming response too short, waiting for more data...');
    return null;
  }

  // Extract cost information - make detection more flexible
  console.log('🔍 Checking cost detection conditions... (streaming:', isStreaming, ')');
  console.log('🔍 Content length:', cleaned.length);
  console.log('🔍 Contains "cost":', lower.includes('cost'));
  console.log('🔍 Contains "$":', lower.includes('$'));
  console.log('🔍 Contains "total":', lower.includes('total'));
  console.log('🔍 Contains "usd":', lower.includes('usd'));
  console.log('🔍 Contains numbers:', /\d/.test(lower));

  if (lower.includes('cost') || lower.includes('$') || lower.includes('usd') || (lower.includes('total') && /\d/.test(lower))) {
    console.log('💰 Cost content detected, parsing...', cleaned.substring(0, 200));
    console.log('🔍 Full response for debugging (first 2000 chars):', cleaned.substring(0, 2000));
    console.log('🔍 Raw content length:', cleaned.length);
    console.log('🔍 Contains dollar sign:', cleaned.includes('$'));
    console.log('🔍 Contains numbers:', /\d/.test(cleaned));

    // ==================== MULTI-PERIOD DETECTION ====================
    // Detect if content has multiple "## Cost Data for <month>" sections (comparison queries)
    const periodHeaderRegex = /## Cost Data for (.+)/g;
    const periodHeaders: Array<{ name: string; index: number }> = [];
    let phMatch;
    while ((phMatch = periodHeaderRegex.exec(cleaned)) !== null) {
      periodHeaders.push({ name: phMatch[1].trim(), index: phMatch.index });
    }

    if (periodHeaders.length >= 2 && !isStreaming) {
      console.log(`💰 MULTI-PERIOD DETECTED: ${periodHeaders.length} periods found`);

      const periods: Array<{ name: string; totalCost: string; services: Array<{ service: string; cost: string }> }> = [];

      for (let pi = 0; pi < periodHeaders.length; pi++) {
        const startIdx = periodHeaders[pi].index;
        const endIdx = pi + 1 < periodHeaders.length ? periodHeaders[pi + 1].index : cleaned.length;
        const sectionContent = cleaned.substring(startIdx, endIdx);

        // Extract total for this period
        const sectionTotalMatch = sectionContent.match(/\*\*Total Cost:\s*\$?([\d,\.]+)(?:\s*USD)?\*\*/i)
          || sectionContent.match(/Total(?:\s+Net\s+Amortized)?\s+Cost[:\s]*\$?([\d,\.]+)/i);
        const sectionTotal = sectionTotalMatch ? sectionTotalMatch[1].replace(/,/g, '') : '0.00';

        // Extract services for this period
        const sectionServices: Array<{ service: string; cost: string }> = [];
        const svcRegex = /^\s*-\s+([^:$\n]+?):\s*\$\s*([\d,\.]+)/gm;
        let svcMatch;
        while ((svcMatch = svcRegex.exec(sectionContent)) !== null) {
          const svcName = svcMatch[1].trim();
          const svcCost = svcMatch[2].trim();
          if (svcName.length > 2 && !svcName.toLowerCase().startsWith('total')) {
            sectionServices.push({ service: svcName, cost: svcCost });
          }
        }

        // Sort by cost descending
        sectionServices.sort((a, b) => parseFloat(b.cost.replace(/,/g, '')) - parseFloat(a.cost.replace(/,/g, '')));

        periods.push({
          name: periodHeaders[pi].name,
          totalCost: sectionTotal,
          services: sectionServices
        });

        console.log(`💰 Period "${periodHeaders[pi].name}": $${sectionTotal}, ${sectionServices.length} services`);
      }

      // Generate insights for comparison
      const totalCosts = periods.map(p => parseFloat(p.totalCost.replace(/,/g, '')));
      const overallTotal = totalCosts.reduce((a, b) => a + b, 0);
      const allServiceCount = periods.reduce((sum, p) => sum + p.services.length, 0);

      const insights = [
        `Compared ${periods.length} periods with combined cost of $${overallTotal.toFixed(2)}`,
        `Found ${allServiceCount} total service entries across all periods`,
        periods.length >= 2 && periods[0].services[0] ? `Highest cost in ${periods[0].name}: ${periods[0].services[0].service}` : '',
        'Review the comparison table on the right for detailed differences'
      ].filter(Boolean);

      return {
        type: 'cost_analysis_multi',
        periods,
        summary: `Cost comparison across ${periods.length} periods`,
        insights,
        warnings: [],
        recommendations: []
      };
    }
    // ==================== END MULTI-PERIOD DETECTION ====================

    // Determine user intent for grouping/breakdown
    const queryLower = userQuery?.toLowerCase() || '';
    const wantsBreakdown = queryLower.includes('breakdown') ||
      queryLower.includes('by service') ||
      queryLower.includes('service breakdown') ||
      queryLower.includes('group by') ||
      queryLower.includes('break down') ||
      queryLower.includes('by account') ||
      queryLower.includes('by dimension') ||
      queryLower.includes('detailed') ||
      queryLower.includes('services') ||
      queryLower.includes('itemized') ||
      queryLower.includes('split by');

    const wantsTotalOnly = queryLower.includes('just total') ||
      queryLower.includes('only total') ||
      queryLower.includes('total only');

    console.log('🎯 Intent analysis:', { wantsBreakdown, wantsTotalOnly, query: queryLower });

    const lines = cleaned.split('\n').filter(line => line.trim());
    let totalCost = '0.00';
    let services: Array<{ service: string, cost: string }> = [];
    let summary = '';

    // Extract services with deduplication to avoid parsing both numbered and regular formats
    // BUT only if user wants breakdown/grouping, not just total cost
    let serviceRegex;
    let match;
    const serviceMap = new Map(); // Use Map to automatically deduplicate by service name

    // Skip service parsing if user only wants total cost
    if (!wantsTotalOnly) {
      console.log('📊 User wants breakdown, parsing services... (streaming:', isStreaming, ')');

      // Use prioritized pattern matching to avoid duplicates
      const patterns = [
        { name: 'numbered_list', regex: /\d+\.\s*([A-Za-z0-9\s\-\(\)\.\&]+?):\s*\$\s*([\d,.]+)/g, priority: 10 },
        { name: 'bullet_points', regex: /^•\s*([^:]+?):\s*\$\s*([\d,.]+)/gm, priority: 9 },
        { name: 'simple_format', regex: /^([^:$\n•\d][^:$\n]*?):\s*\$\s*([\d,.]+)$/gm, priority: 8 }
      ];

      // Apply patterns in priority order and stop when we find a good match
      let bestPattern: any = null;
      let bestMatches = 0;

      patterns.forEach(pattern => {
        let match;
        let patternMatches = 0;
        let tempServices = new Map();
        const regex = new RegExp(pattern.regex.source, pattern.regex.flags);

        while ((match = regex.exec(cleaned)) !== null) {
          const serviceName = match[1].trim();
          const cost = match[2].trim();

          console.log(`🔍 ${pattern.name} found:`, serviceName, cost);

          // Better filtering logic
          if (isValidServiceName(serviceName) && isValidCost(cost)) {
            const key = serviceName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
            tempServices.set(key, { service: serviceName, cost: cost });
            patternMatches++;
          }
        }

        console.log(`📊 Pattern ${pattern.name} found ${patternMatches} valid matches`);

        // Use the pattern with the most matches and highest priority
        if (patternMatches > bestMatches || (patternMatches === bestMatches && pattern.priority > (bestPattern?.priority || 0))) {
          bestPattern = pattern;
          bestMatches = patternMatches;
          serviceMap.clear();
          tempServices.forEach((value, key) => serviceMap.set(key, value));
        }
      });

      console.log(`🏆 Using pattern: ${bestPattern?.name || 'none'} with ${bestMatches} services`);

      // Convert Map back to array, sorted by cost (highest first)
      services = Array.from(serviceMap.values()).sort((a, b) => {
        const costA = parseFloat(a.cost.replace(/[\$,]/g, ''));
        const costB = parseFloat(b.cost.replace(/[\$,]/g, ''));
        return costB - costA;
      });

      console.log('🎯 Final deduplicated services:', services.length, services);

      // Display all services - no filtering/truncation
      console.log(`✅ Displaying all ${services.length} services without filtering`);
      if (isStreaming) {
        console.log('🔄 Streaming mode: showing all services without limitations');
      }
    } else {
      console.log('🚫 User wants total only, skipping service breakdown parsing');
    }

    // Check for potential backend issues
    const suspiciousServices = services.filter(s =>
      s.service.length <= 2 ||
      s.service.startsWith('-') ||
      s.service.match(/^[0-9-]+$/)
    );

    if (suspiciousServices.length > 0) {
      console.log('⚠️ DETECTED SUSPICIOUS SERVICE NAMES:', suspiciousServices);
      console.log('⚠️ This suggests backend response formatting issues');
    }

    // Check for potential data truncation issues
    const hasEllipsis = cleaned.includes('...') || cleaned.includes('and more') || cleaned.includes('other services');
    const hasTopServicesIndicator = cleaned.toLowerCase().includes('top ') && cleaned.toLowerCase().includes('service');
    const hasTruncationHints = cleaned.includes('truncated') || cleaned.includes('summary view') ||
      cleaned.includes('first ') || cleaned.includes('showing ');

    if (services.length < 10 && (hasEllipsis || hasTopServicesIndicator || hasTruncationHints)) {
      console.log('🚨 POTENTIAL DATA TRUNCATION DETECTED!');
      console.log('🚨 Backend may be returning incomplete service list');
      console.log('🚨 Found only', services.length, 'services but response contains truncation indicators');
      console.log('🚨 Truncation indicators found:', { hasEllipsis, hasTopServicesIndicator, hasTruncationHints });
    }

    // Detect if total cost equals the largest service cost (suggests wrong total)
    if (services.length > 0) {
      const largestServiceCost = parseFloat(services[0].cost.replace(/[\$,]/g, ''));
      const parsedTotalCost = parseFloat((totalCost || '0').replace(/[\$,]/g, ''));
      if (Math.abs(largestServiceCost - parsedTotalCost) < 0.01 && services.length < 20) {
        console.log('🚨 SUSPICIOUS TOTAL COST DETECTED!');
        console.log('🚨 Total cost matches largest service cost exactly');
        console.log('🚨 This suggests the total is incorrect or data is truncated');
        console.log('🚨 Total:', parsedTotalCost, 'vs Largest Service:', largestServiceCost);
      }
    }

    // Add a timestamp for backend consistency tracking
    const responseTimestamp = new Date().toISOString();
    console.log('⏰ Response received at:', responseTimestamp);

    // Extract total cost - use all patterns and pick the most reliable one
    console.log('🔍 Trying cost extraction patterns...');

    const costPatterns = [
      { name: 'MCP_format', regex: /(?:total\s+net\s+amortized\s+cost|total\s+cost).*?(?:was|is):\s*\$\s*([\d,.]+)(?:\s+USD)?/i, priority: 10 },
      { name: 'total_cost_label', regex: /Total Cost:\s*\$\s*([\d,.]+)(?:\s+USD)?/i, priority: 9 },
      { name: 'total_across_regions', regex: /Total cost across all regions:\s*\$\s*([\d,.]+)/i, priority: 8 },
      { name: 'simple_total', regex: /Total cost:\s*\$\s*([\d,.]+)/i, priority: 7 },
      { name: 'net_amortized', regex: /Net\s+amortized\s+cost:\s*\$\s*([\d,.]+)/i, priority: 6 },
      { name: 'final_total', regex: /(?:final|overall)\s+total.*?\$\s*([\d,.]+)/i, priority: 5 }
    ];

    let bestMatch = null;
    let bestPriority = 0;

    costPatterns.forEach(pattern => {
      const match = cleaned.match(pattern.regex);
      if (match && pattern.priority > bestPriority) {
        bestMatch = match;
        bestPriority = pattern.priority;
        console.log(`✅ ${pattern.name} matched (priority ${pattern.priority}):`, match[0]);
      }
    });

    // A volume listing is not a cost response. The generic fallback below takes the first
    // dollar amount anywhere in the text, and an EBS answer now carries a per-volume cost
    // estimate for each unused volume — so one volume's "~$0.91/mo" was being reported as
    // "Total AWS cost: $0.91 for the specified period".
    const isVolumeListing = /\bvolumeid\s*:/i.test(cleaned);

    if (!bestMatch && !isStreaming && !isVolumeListing) {
      // Fallback to generic pattern for final responses only
      const genericMatch = cleaned.match(/\$\s*([\d,.]+)(?:\s+USD)?/i);
      if (genericMatch) {
        bestMatch = genericMatch;
        console.log('✅ Generic pattern fallback matched:', genericMatch[0]);
      }
    }

    if (!bestMatch) {
      console.log('❌ No cost patterns matched!');
      console.log('🔍 Sample text to debug:', cleaned.substring(0, 500));
    }

    const costMatch = bestMatch;

    if (costMatch) {
      totalCost = costMatch[1];
      console.log('💰 Found total cost from backend:', totalCost);
      console.log('💰 Raw match result:', costMatch);
      console.log('💰 Full content search area:', cleaned.substring(0, 1000));

      // Validation: if we also have services, let's verify the math
      if (services.length > 0) {
        let sum = 0;
        services.forEach(service => {
          const cleanCost = service.cost.replace(/[\$,\s]/g, '');
          const num = parseFloat(cleanCost);
          if (!isNaN(num)) sum += num;
        });
        const calculatedTotal = sum.toFixed(2);
        const backendTotal = parseFloat(totalCost.replace(/[\$,]/g, ''));
        const difference = Math.abs(sum - backendTotal);

        console.log('🔍 VALIDATION CHECK:');
        console.log('🔍 Backend total:', totalCost, '(', backendTotal, ')');
        console.log('🔍 Calculated from services:', calculatedTotal, '(', sum, ')');
        console.log('🔍 Difference:', difference.toFixed(2));

        if (difference > 0.01) {
          console.log('⚠️ MISMATCH! Backend total does not match service sum');
        } else {
          console.log('✅ Math checks out - backend and calculated totals match');
        }
      }

      console.log('💰 Using backend total - NOT calculating from services');
    } else if (services.length > 0) {
      console.log('🧮 No explicit total found, calculating from services:', services.length);
      console.log('🧮 Services found:', services);

      let sum = 0;
      let validServices = 0;
      services.forEach((service, index) => {
        const cleanCost = service.cost.replace(/[\$,\s]/g, '');
        const num = parseFloat(cleanCost);
        console.log(`🧮 Service ${index + 1}: ${service.service} = ${service.cost} → parsed as ${num}`);
        if (!isNaN(num) && num >= 0) {
          sum += num;
          validServices++;
          console.log(`🧮 Running sum: ${sum} (${validServices} valid services)`);
        } else {
          console.log(`⚠️ Failed to parse cost: "${service.cost}" → "${cleanCost}"`);
        }
      });

      if (sum > 0) {
        totalCost = sum.toFixed(2);
        console.log('🧮 Final calculated total:', totalCost, `from ${validServices} services`);
      } else {
        console.log('⚠️ Sum is 0, keeping totalCost as 0.00');
        // If no valid calculation but we have services, show that data is incomplete
        if (services.length > 0) {
          console.log('⚠️ Warning: Found services but unable to calculate total - possible parsing issue');
        }
      }
    } else {
      console.log('⚠️ No total pattern found and no services to calculate from');
      totalCost = '0.00';
    }

    // Create summary
    if (totalCost === '0.00') {
      summary = `No costs incurred in the specified period. All usage appears to be within AWS Free Tier.`;
    } else {
      summary = `Total AWS cost: $${totalCost} for the specified period.`;
    }

    // Generate insights based on actual data - fix billable services detection
    let insights = [];
    const totalCostNum = parseFloat(totalCost.replace(/,/g, ''));

    if (totalCostNum === 0) {
      insights = [
        'All services running within Free Tier limits',
        'No unexpected charges detected',
        'Consider enabling cost alerts for monitoring'
      ];
    } else {
      // If we have a cost > 0, we definitely have billable services
      insights = [
        `Billable services detected with total cost of $${totalCost}`,
        services.length > 0 ? `Found ${services.length} itemized services` : 'Service breakdown available on request',
        services.length > 0 && services[0] ? `Highest cost: ${services[0].service}` : 'Detailed breakdown available',
        'Consider cost optimization opportunities'
      ];
    }

    // Add warnings and recommendations for truncated data
    let warnings = [];
    let recommendations = [];

    if (services.length < 10 && (hasEllipsis || hasTopServicesIndicator || hasTruncationHints)) {
      warnings.push(`⚠️ Data appears truncated - showing only ${services.length} services`);
      recommendations.push('Ask for "detailed AWS cost breakdown" to see all services');
      recommendations.push('Try: "Show me complete AWS service breakdown for June 2025"');
    }

    if (services.length > 0) {
      const largestServiceCost = parseFloat(services[0].cost.replace(/[\$,]/g, ''));
      const parsedTotalCost = parseFloat((totalCost || '0').replace(/[\$,]/g, ''));
      if (Math.abs(largestServiceCost - parsedTotalCost) < 0.01 && services.length < 20) {
        warnings.push('⚠️ Total cost may be incorrect (matches single service cost)');
        recommendations.push('Request complete cost breakdown to verify totals');
      }
    }

    const result = {
      type: 'cost_analysis',
      summary,
      totalCost,
      services,
      insights,
      warnings,
      recommendations
    };
    console.log('💰 Cost analysis result:', result);
    console.log('🎯 Total cost being returned:', totalCost);
    console.log('🎯 Number of services:', services.length);
    console.log('🎯 Intent flags:', { wantsBreakdown, wantsTotalOnly });
    return result;
  }

  // All structured layouts now handled by streaming system only

  // Let streaming handle all structured layout - no default needed

  return null;
}

// Enhanced function to parse response content with streaming support
function parseResponseContent(content: string, userQuery?: string, isStreaming: boolean = false): { parsed: string; structured?: any } {
  const cleaned = cleanAnsiCodes(content);

  // Check if user wants raw detailed breakdown instead of structured summary
  const wantsRawBreakdown = userQuery && (
    userQuery.toLowerCase().includes('group by services') ||
    userQuery.toLowerCase().includes('grouped by services') ||
    userQuery.toLowerCase().includes('breakdown') ||
    userQuery.toLowerCase().includes('detailed') ||
    userQuery.toLowerCase().includes('itemized')
  );

  // If user wants raw breakdown, return the cleaned content without structured formatting
  if (wantsRawBreakdown && cleaned.includes('EC2') && cleaned.includes('$')) {
    console.log('📋 User requested detailed breakdown, showing raw response');
    return { parsed: cleaned };
  }

  const structuredInfo = extractStructuredInfo(content, userQuery, isStreaming);

  if (structuredInfo) {
    // Multi-period cost comparison (e.g., "compare feb and jan")
    if (structuredInfo.type === 'cost_analysis_multi') {
      return {
        parsed: cleaned,
        structured: {
          summary: structuredInfo.summary,
          sections: [
            // One Cost Breakdown section per period
            ...structuredInfo.periods.map((period: any) => {
              const totalNum = parseFloat(period.totalCost.replace(/,/g, ''));
              return {
                title: `💰 Cost Breakdown for ${period.name} - Total: $${period.totalCost}`,
                status: 'complete',
                items: totalNum === 0
                  ? [{ text: 'No billable services detected', status: 'complete' }]
                  : period.services.length > 0
                    ? period.services.map((svc: any) => ({
                        text: `- ${svc.service}: $${svc.cost}`,
                        status: 'complete'
                      }))
                    : [{ text: 'Billable services detected - breakdown available on request', status: 'complete' }]
              };
            }),
            {
              title: '💡 Cost Insights',
              status: 'complete',
              items: structuredInfo.insights.map((insight: string) => ({
                text: insight,
                status: 'complete'
              }))
            }
          ],
          actions: [
            { label: 'Set Cost Alerts', type: 'action', icon: 'eye' },
            { label: 'Get Optimization Tips', type: 'info', icon: 'download' },
            { label: 'Compare Previous Month', type: 'action', icon: 'eye' }
          ]
        }
      };
    }

    if (structuredInfo.type === 'cost_analysis') {
      return {
        parsed: cleaned,
        structured: {
          summary: structuredInfo.summary,
          sections: [
            {
              title: `💰 Cost Breakdown - Total: $${structuredInfo.totalCost}`,
              status: 'complete',
              items: (() => {
                const totalCostNum = parseFloat(structuredInfo.totalCost.replace(/,/g, ''));
                if (totalCostNum === 0) {
                  return [{ text: 'No billable services detected', status: 'complete' }];
                } else if (structuredInfo.services.length > 0) {
                  return structuredInfo.services.map((svc: any) => ({
                    text: `${svc.service}: $${svc.cost}`,
                    status: 'complete'
                  }));
                } else {
                  return [{ text: 'Billable services detected - breakdown available on request', status: 'complete' }];
                }
              })()
            },
            {
              title: '💡 Cost Insights',
              status: 'complete',
              items: structuredInfo.insights.map((insight: string) => ({
                text: insight,
                status: 'complete'
              }))
            },
            // Add warnings section if there are any
            ...(structuredInfo.warnings && structuredInfo.warnings.length > 0 ? [{
              title: '⚠️ Data Quality Warnings',
              status: 'warning',
              items: structuredInfo.warnings.map((warning: string) => ({
                text: warning,
                status: 'warning'
              }))
            }] : []),
            // Add recommendations section if there are any
            ...(structuredInfo.recommendations && structuredInfo.recommendations.length > 0 ? [{
              title: '💭 FinOps',
              status: 'info',
              items: structuredInfo.recommendations.map((rec: string) => ({
                text: rec,
                status: 'info'
              }))
            }] : [])
          ],
          actions: [
            { label: 'Set Cost Alerts', type: 'action', icon: 'eye' },
            { label: 'View Detailed Report', type: 'info', icon: 'download' }
          ]
        }
      };
    }

    if (structuredInfo.type === 'deployment') {
      const actionTypes: ('emergency' | 'warning' | 'action' | 'info')[] = ['emergency', 'warning', 'action', 'info'];
      const actionIcons = ['stop', 'undo', 'pause', 'eye'];

      return {
        parsed: cleaned,
        structured: {
          summary: structuredInfo.summary,
          sections: [
            // Add execution plan section first
            {
              title: '📋 Execution Plan',
              status: isStreaming ? 'in_progress' : 'complete',
              items: structuredInfo.executionPlan || []
            },
            // Then add account sections
            ...structuredInfo.accounts.map((account: any) => ({
              title: account.name,
              status: account.status,
              items: account.tasks
            }))
          ],
          actions: structuredInfo.actions.map((action: string, idx: number) => ({
            label: action,
            type: actionTypes[idx] || 'info',
            icon: actionIcons[idx] || 'eye'
          }))
        }
      };
    }

    // Removed mcp_query handler - streaming handles everything now
  }

  return { parsed: cleaned };
}

// Component to render structured response
function StructuredResponse({
  structured,
  tokens,
  messageId,
  onCancelExecution,
  onExecuteQuickAction,
  onHandleAccountSelection
}: {
  structured: any;
  tokens?: number;
  messageId: string;
  onCancelExecution: () => void;
  onExecuteQuickAction: (action: string, messageId: string, context?: any) => void;
  onHandleAccountSelection?: (accountId: string, originalQuery: string, messageId: string) => void;
}) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete': return '•';
      case 'in_progress': return '•';
      case 'pending': return '•';
      case 'error': return '•';
      case 'warning': return '•';
      case 'info': return '•';
      default: return '•';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'text-green-400';
      case 'in_progress': return 'text-blue-400';
      case 'pending': return 'text-gray-500';
      case 'error': return 'text-red-400';
      case 'warning': return 'text-yellow-400';
      case 'info': return 'text-blue-300';
      default: return 'text-gray-300';
    }
  };

  const getSectionBg = (status: string) => {
    switch (status) {
      case 'error': return 'bg-red-900/30 border border-red-600';
      case 'warning': return 'bg-yellow-900/30 border border-yellow-600';
      case 'info': return 'bg-blue-900/20 border border-blue-600';
      default: return 'bg-dark-bg';
    }
  };

  const getActionStyle = (type: string) => {
    switch (type) {
      case 'emergency': return 'bg-red-500/5 text-red-400 border border-red-500/10 hover:bg-red-500/10 hover:border-red-500/20 text-xs';
      case 'warning': return 'bg-yellow-500/5 text-yellow-400 border border-yellow-500/10 hover:bg-yellow-500/10 hover:border-yellow-500/20 text-xs';
      case 'action': return 'bg-blue-500/5 text-blue-400 border border-blue-500/10 hover:bg-blue-500/10 hover:border-blue-500/20 text-xs';
      case 'info': return 'bg-gray-500/5 text-gray-400 border border-gray-500/10 hover:bg-gray-500/10 hover:border-gray-500/20 text-xs';
      default: return 'bg-slate-500/5 text-slate-400 border border-slate-500/10 hover:bg-slate-500/10 hover:border-slate-500/20 text-xs';
    }
  };

  return (
    <div className="bg-dark-panel rounded-2xl rounded-tl-md p-2 sm:p-3 border border-dark-border">
      {structured.summary && (
        <div className="text-sm mb-3">{structured.summary}</div>
      )}

      {structured.sections && (
        <div className="space-y-3 mb-3">
          {structured.sections.map((section: any, idx: number) => (
            <div key={idx} className={`rounded-lg p-3 ${getSectionBg(section.status)}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-orange-400">{section.title}</span>
                <span className={`text-xs ${getStatusColor(section.status)} flex items-center`}>
                  {section.status === 'in_progress' && (
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse mr-2"></div>
                  )}
                  {section.status === 'complete' ? '✓ Complete' :
                    section.status === 'in_progress' ? 'In Progress' :
                      section.status === 'pending' ? 'Pending' : 'Issues Found'}
                </span>
              </div>
              <div className="text-xs text-gray-300 space-y-1">
                {section.items.map((item: any, itemIdx: number) => (
                  <div key={itemIdx} className={getStatusColor(item.status)}>
                    {getStatusIcon(item.status)} {item.text}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {structured.rawResponse && (
        <div className="mt-4 mb-3">
          <div className="rounded-lg p-3 bg-gray-900 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-green-400">🖥️ Live MCP Server Response</span>
              <span className="text-xs text-green-400 flex items-center">
                {structured.finalResult ? (
                  <>✓ Complete</>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                    Streaming...
                  </>
                )}
              </span>
            </div>
            <div className="text-xs text-gray-300 font-mono whitespace-pre-wrap bg-black rounded p-3 max-h-96 overflow-y-auto ansi-content">
              <div
                dangerouslySetInnerHTML={{
                  __html: structured.enhancedResponse || structured.rawResponse
                }}
              />
            </div>
          </div>
        </div>
      )}

      {structured.actions && (
        <div className="flex flex-wrap gap-2">
          {structured.actions.map((action: any, idx: number) => {
            const getIcon = (iconName: string) => {
              switch (iconName) {
                case 'stop': return faStop;
                case 'undo': return faUndo;
                case 'pause': return faPause;
                case 'eye': return faEye;
                case 'shield': return faShield;
                default: return faStop;
              }
            };

            const handleActionClick = async () => {
              if (action.label === 'Cancel Execution') {
                onCancelExecution();
              } else if (action.accountId && action.originalQuery && onHandleAccountSelection) {
                // Handle account selection
                await onHandleAccountSelection(action.accountId, action.originalQuery, messageId);
              } else if (action.action) {
                onExecuteQuickAction(action.action, messageId, structured);
              }
            };

            return (
              <button
                key={idx}
                onClick={handleActionClick}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all duration-200 ${getActionStyle(action.type)}`}
              >
                <FontAwesomeIcon icon={getIcon(action.icon)} className="mr-1.5 text-xs" />
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const getPostCompletionActions = (userQuery?: string, structured?: any) => {
  const query = userQuery?.toLowerCase() || '';

  // Cost query actions
  if (query.includes('cost') || query.includes('spending') || query.includes('bill')) {
    return [
      { label: 'Set Cost Alerts', type: 'action', icon: 'shield', action: 'Set up cost alerts for these services' },
      { label: 'Get Optimization Tips', type: 'info', icon: 'eye', action: 'Analyze cost optimization opportunities' },
      { label: 'Compare Previous Month', type: 'info', icon: 'eye', action: 'Compare costs with previous month' },
      { label: 'Detailed Service Breakdown', type: 'action', icon: 'eye', action: 'Show detailed breakdown by service type' }
    ];
  }

  // Infrastructure/server query actions
  if (query.includes('server') || query.includes('instance') || query.includes('ec2') || query.includes('infrastructure')) {
    return [
      { label: 'Monitor Resources', type: 'action', icon: 'eye', action: 'Show resource utilization monitoring' },
      { label: 'Set Up Scaling Rules', type: 'action', icon: 'shield', action: 'Configure auto-scaling policies' },
      { label: 'Check Security Groups', type: 'warning', icon: 'shield', action: 'Review security group configurations' },
      { label: 'Performance Metrics', type: 'info', icon: 'eye', action: 'Show server performance metrics' }
    ];
  }

  // Region-specific actions
  if (query.includes('region') || query.includes('mumbai') || query.includes('us-east') || query.includes('eu-west')) {
    return [
      { label: 'Cross-Region Analysis', type: 'action', icon: 'eye', action: 'Compare resources across regions' },
      { label: 'Network Latency Check', type: 'info', icon: 'eye', action: 'Check network latency between regions' },
      { label: 'Disaster Recovery Plan', type: 'warning', icon: 'shield', action: 'Review disaster recovery setup' }
    ];
  }

  // Default actions for general queries
  return [
    { label: 'Create Dashboard', type: 'action', icon: 'eye', action: 'Create monitoring dashboard for this data' },
    { label: 'Set Up Alerts', type: 'warning', icon: 'shield', action: 'Configure alerts based on these results' },
    { label: 'Export Report', type: 'info', icon: 'eye', action: 'Export this data as PDF report' }
  ];
};

// Dynamic Visualization Detection Logic
const detectVisualizationOpportunity = (query: string, content: string): VisualizationData | null => {
  console.log('🎨 VISUALIZATION DEBUG: Checking query:', query);
  console.log('🎨 VISUALIZATION DEBUG: Content length:', content.length);
  console.log('🎨 VISUALIZATION DEBUG: Content preview:', content.substring(0, 500));

  const lowerQuery = query.toLowerCase();
  const lowerContent = content.toLowerCase();

  const isCostQuery = lowerQuery.includes('cost') || lowerQuery.includes('spending') || lowerQuery.includes('bill') ||
    lowerQuery.includes('price') || lowerQuery.includes('budget');
  const hasMoneySymbols = lowerContent.includes('$') || lowerContent.includes('cost') || lowerContent.includes('total') ||
    lowerContent.includes('usd') || lowerContent.includes('billing');

  console.log('🎨 VISUALIZATION DEBUG: isCostQuery:', isCostQuery, 'hasMoneySymbols:', hasMoneySymbols);

  // Cost data detection - improved patterns with AWS service name validation
  if (isCostQuery && hasMoneySymbols) {
    console.log('🎨 VISUALIZATION DEBUG: Starting cost data extraction...');

    // Extract cost data - look for "ServiceName: $amount → $amount" pattern for comparisons
    const comparisonPattern = /([A-Z][A-Za-z\s]+):\s*\$([0-9,]+\.?[0-9]*)\s*→\s*\$([0-9,]+\.?[0-9]*)/g;
    let comparisonMatch;
    const comparisonMatches = [];
    while ((comparisonMatch = comparisonPattern.exec(content)) !== null) {
      comparisonMatches.push(comparisonMatch);
    }

    if (comparisonMatches.length > 0) {
      console.log('🎨 Found comparison data:', comparisonMatches.length, 'services');

      const costData = comparisonMatches
        .map(match => {
          const service = match[1].trim();
          const cost1 = parseFloat(match[2].replace(/,/g, ''));
          const cost2 = parseFloat(match[3].replace(/,/g, ''));

          // Filter out invalid service names
          if (service.length < 3 ||
            /^(from|to|at|were|other|system|cloud|slightly|decreased|increased|total)$/i.test(service)) {
            return null;
          }

          return {
            service,
            cost: cost1 + cost2,
            cost1,
            cost2,
            costFormatted: `$${(cost1 + cost2).toFixed(2)}`
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (costData.length > 0) {
        console.log('🔍 VISUALIZATION DEBUG: Parsed cost data:', costData);
        return {
          id: Date.now().toString(),
          type: 'cost-breakdown',
          title: `Cost Comparison - ${costData.length} Services`,
          data: {
            services: costData,
            total: costData.reduce((sum, item) => sum + item.cost, 0),
            queryType: 'cost'
          },
          timestamp: new Date(),
          query
        };
      }
    }

    // Single period cost extraction
    const singleCostPattern = /([A-Z][A-Za-z\s]+):\s*\$([0-9,]+\.?[0-9]*)/g;
    let singleMatch;
    const singleMatches = [];
    while ((singleMatch = singleCostPattern.exec(content)) !== null) {
      singleMatches.push(singleMatch);
    }

    if (singleMatches.length > 0) {
      const costData = singleMatches
        .map(match => {
          const service = match[1].trim();
          const cost = parseFloat(match[2].replace(/,/g, ''));

          // Filter out invalid service names and non-AWS patterns
          if (service.length < 3 ||
            /^(from|to|at|were|other|system|cloud|slightly|decreased|increased|total|price|cost|spending)$/i.test(service) ||
            cost < 0.01) {
            return null;
          }

          return {
            service,
            cost,
            costFormatted: `$${cost.toFixed(2)}`
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (costData.length > 2) {
        console.log('🔍 VISUALIZATION DEBUG: Found single period cost data:', costData);
        return {
          id: Date.now().toString(),
          type: 'cost-breakdown',
          title: `Cost Analysis - ${costData.length} Services`,
          data: {
            services: costData,
            total: costData.reduce((sum, item) => sum + item.cost, 0),
            queryType: 'cost'
          },
          timestamp: new Date(),
          query
        };
      }
    }

    // Last resort: Extract total cost only (for queries like "cost of august 2025")
    const totalCostPattern = /(?:total|costs?).*?\$([0-9,]+\.?[0-9]*)/gi;
    let totalMatch;
    const totalMatches = [];
    while ((totalMatch = totalCostPattern.exec(content)) !== null) {
      totalMatches.push(totalMatch);
    }

    if (totalMatches.length > 0) {
      const totalCost = parseFloat(totalMatches[0][1].replace(/,/g, ''));

      // Extract period from query (month/year)
      const periodMatch = query.match(/(january|february|march|april|may|june|july|august|september|october|november|december)\s*\d{4}|\d{4}/gi);
      const period = periodMatch ? periodMatch[0] : 'Period';

      console.log('🔍 VISUALIZATION DEBUG: Found total-only cost:', totalCost, 'for period:', period);
      return {
        id: Date.now().toString(),
        type: 'cost-breakdown',
        title: `Total Cost - ${period}`,
        data: {
          services: [],
          total: totalCost,
          period: period,
          queryType: 'cost',
          totalOnly: true
        },
        timestamp: new Date(),
        query
      };
    }
  }

  console.log('🎨 VISUALIZATION DEBUG: No visualization detected');
  return null;
};

// EBS Volumes Dashboard Component (separate to use hooks properly)
// EBS Volume Cost Dashboard — the real billed figure, carved out of a USAGE_TYPE breakdown.
// Exists because AWS has no "EBS" service line: volume charges sit inside "EC2 - Other"
// alongside NAT gateway and data transfer, so a service-grouped chart can never answer
// "what do my volumes cost". Single series, so no legend — the heading names it.
const EBSCostDashboard: React.FC<{ dashboard: any; onClose: () => void }> = ({ dashboard, onClose }) => {
  const items = dashboard.data.items || [];
  const total = dashboard.data.total || 0;
  const accountTotal = dashboard.data.accountTotal || 0;
  const share = accountTotal > 0 ? (total / accountTotal) * 100 : 0;
  const max = items.length > 0 ? items[0].cost : 0;

  // AWS prefixes a usage type with a short region code outside us-east-1, which carries no
  // prefix at all. Dropping that prefix made every region's gp3 line read "gp3 storage", so
  // one account showed the same label fifteen times with no way to tell the rows apart.
  const REGION_PREFIX: Record<string, string> = {
    USE1: 'us-east-1', USE2: 'us-east-2', USW1: 'us-west-1', USW2: 'us-west-2',
    CAN1: 'ca-central-1', SAE1: 'sa-east-1',
    EU: 'eu-west-1', EUW2: 'eu-west-2', EUW3: 'eu-west-3', EUC1: 'eu-central-1', EUN1: 'eu-north-1',
    APS1: 'ap-southeast-1', APS2: 'ap-southeast-2', APS3: 'ap-south-1',
    APN1: 'ap-northeast-1', APN2: 'ap-northeast-2', APN3: 'ap-northeast-3', APE1: 'ap-east-1',
    MES1: 'me-south-1', AFS1: 'af-south-1'
  };

  // "USE1-EBS:VolumeUsage.gp3" → "gp3 storage · us-east-1". The raw usage type stays in the
  // hover, so nothing is hidden by the friendlier name.
  const pretty = (usageType: string) => {
    const prefixMatch = usageType.match(/^([A-Z]{2,4}\d?)-EBS:/);
    // No prefix means us-east-1 — that is how Cost Explorer writes it, not a missing value.
    const region = prefixMatch
      ? (REGION_PREFIX[prefixMatch[1]] || prefixMatch[1])
      : 'us-east-1';

    // Matches VolumeUsage.gp3, VolumeP-IOPS, VolumeIOUsage, VolumeP3Throughput. The earlier
    // pattern stopped at the hyphen and rendered "EBS:VolumeP-IOPS" as just "p".
    const m = usageType.match(/EBS:Volume([A-Za-z0-9-]*?)(?:\.(\w+))?$/i);
    if (!m) return `${usageType} · ${region}`;

    const rawKind = (m[1] || '').replace(/-/g, ' ').trim();
    let kind: string;
    if (/^usage$/i.test(rawKind)) kind = 'storage';
    else if (/iops/i.test(rawKind)) kind = 'provisioned IOPS';
    else if (/throughput/i.test(rawKind)) kind = 'provisioned throughput';
    else if (/^io\s*usage$/i.test(rawKind)) kind = 'I/O requests';
    else kind = rawKind.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase() || 'storage';

    const type = m[2] ? `${m[2]} ` : '';
    return `${type}${kind} · ${region}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">EBS Volume Cost</h1>
            <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              {dashboard.data.periodStart && (
                <span className="text-xs text-blue-600 inline-block font-medium">
                  {dashboard.data.periodStart} → {dashboard.data.periodEnd}
                </span>
              )}
              {/* Without this the chart for one region and the chart for the whole account
                  are visually identical — and the whole-account figure is 3x larger. */}
              {dashboard.data.regionScope && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                  {dashboard.data.regionScope === 'all'
                    ? 'All regions'
                    : `Region: ${dashboard.data.regionScope}`}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300">Close</button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-gray-900">${total.toFixed(2)}</div>
            <div className="text-xs text-gray-600">EBS Volume Cost</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-blue-700">{share.toFixed(1)}%</div>
            <div className="text-xs text-gray-600">of total spend in scope</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-gray-500">${accountTotal.toFixed(2)}</div>
            <div className="text-xs text-gray-600">Total spend in scope</div>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="bg-white rounded p-4 border border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">Cost by volume type</h2>
            <p className="text-xs text-gray-500 mb-3">
              Billed EBS usage types. Snapshots are excluded — they are backup storage, not volumes.
            </p>
            <div className="space-y-2">
              {items.map((item: any, index: number) => {
                const pct = max > 0 ? (item.cost / max) * 100 : 0;
                const sharePct = total > 0 ? (item.cost / total) * 100 : 0;
                return (
                  <div key={index} className="flex items-center text-xs" title={item.usageType}>
                    <div className="w-56 font-medium text-gray-700 truncate">{pretty(item.usageType)}</div>
                    <div className="flex-1 mx-3">
                      <div className="bg-gray-200 rounded h-3 relative">
                        <div className="bg-blue-600 h-3 rounded" style={{ width: `${Math.max(pct, 2)}%` }}></div>
                      </div>
                    </div>
                    <div className="w-20 text-right font-semibold text-gray-900">${item.cost.toFixed(2)}</div>
                    <div className="w-12 text-right text-gray-500">{sharePct.toFixed(1)}%</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center text-xs">
              <div className="w-56 font-semibold text-gray-900">Total</div>
              <div className="flex-1 mx-3"></div>
              <div className="w-20 text-right font-bold text-gray-900">${total.toFixed(2)}</div>
              <div className="w-12"></div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded p-6 border border-gray-200 text-center">
            <p className="text-sm text-gray-600">No EBS volume charges found in this period.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// EBS Volume Cost across accounts.
// Answers "total cost of EBS volumes across all AWS accounts" with the EBS share only.
// The generic multi-account chart sums every billed line, which reported $26,481.38 for a
// question whose real answer was a few hundred dollars.
const EBSCostMultiAccountDashboard: React.FC<{ dashboard: any; onClose: () => void }> = ({ dashboard, onClose }) => {
  const accounts = dashboard.data.accounts || [];
  const items = dashboard.data.items || [];
  const grandTotal = dashboard.data.grandTotal || 0;
  const billGrandTotal = dashboard.data.billGrandTotal || 0;
  const share = billGrandTotal > 0 ? (grandTotal / billGrandTotal) * 100 : 0;
  const max = items.length > 0 ? items[0].total : 0;

  const pretty = (usageType: string) => {
    const p = usageType.match(/^([A-Z]{2,4}\d?)-EBS:/);
    const REGION: Record<string, string> = {
      USE1: 'us-east-1', USE2: 'us-east-2', USW1: 'us-west-1', USW2: 'us-west-2',
      CAN1: 'ca-central-1', SAE1: 'sa-east-1', EU: 'eu-west-1', EUW2: 'eu-west-2',
      EUW3: 'eu-west-3', EUC1: 'eu-central-1', EUN1: 'eu-north-1',
      APS1: 'ap-southeast-1', APS2: 'ap-southeast-2', APS3: 'ap-south-1',
      APN1: 'ap-northeast-1', APN2: 'ap-northeast-2', APN3: 'ap-northeast-3',
      APE1: 'ap-east-1', MES1: 'me-south-1', AFS1: 'af-south-1'
    };
    const region = p ? (REGION[p[1]] || p[1]) : 'us-east-1';
    const m = usageType.match(/EBS:Volume([A-Za-z0-9-]*?)(?:\.(\w+))?$/i);
    if (!m) return `${usageType} · ${region}`;
    const rawKind = (m[1] || '').replace(/-/g, ' ').trim();
    let kind = /^usage$/i.test(rawKind) ? 'storage'
      : /iops/i.test(rawKind) ? 'provisioned IOPS'
      : /throughput/i.test(rawKind) ? 'provisioned throughput'
      : rawKind.toLowerCase() || 'storage';
    return `${m[2] ? m[2] + ' ' : ''}${kind} · ${region}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">EBS Volume Cost — All Accounts</h1>
            <p className="text-xs text-gray-600">"{dashboard.query}"</p>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300">Close</button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-gray-900">${grandTotal.toFixed(2)}</div>
            <div className="text-xs text-gray-600">EBS Volume Cost — {accounts.length} account(s)</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-blue-700">{share.toFixed(1)}%</div>
            <div className="text-xs text-gray-600">of total spend</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-gray-500">${billGrandTotal.toFixed(2)}</div>
            <div className="text-xs text-gray-600">Total spend across accounts</div>
          </div>
        </div>

        <div className="bg-white rounded p-4 border border-gray-200 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">EBS cost per account</h2>
          <div className="space-y-2">
            {accounts.map((a: any, i: number) => {
              const pct = grandTotal > 0 ? (a.total / grandTotal) * 100 : 0;
              return (
                <div key={a.accountId || i} className="flex items-center text-xs">
                  <div className="w-56 font-medium text-gray-700 truncate">{a.name}</div>
                  <div className="flex-1 mx-3">
                    <div className="bg-gray-200 rounded h-3"><div className="bg-blue-600 h-3 rounded" style={{ width: `${Math.max(pct, 2)}%` }}></div></div>
                  </div>
                  <div className="w-24 text-right font-semibold text-gray-900">${a.total.toFixed(2)}</div>
                  <div className="w-12 text-right text-gray-500">{pct.toFixed(1)}%</div>
                </div>
              );
            })}
          </div>
        </div>

        {items.length > 0 && (
          <div className="bg-white rounded border border-gray-200">
            <div className="p-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Cost by volume type and account</h2>
              <p className="text-xs text-gray-500 mt-1">Billed EBS usage types only. Snapshots excluded — they are backup storage, not volumes.</p>
            </div>
            <div className="overflow-auto" style={{ maxHeight: '55vh' }}>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 bg-gray-50">Volume type</th>
                    {accounts.map((a: any, i: number) => (
                      <th key={i} className="text-right py-2 px-3 font-semibold text-gray-700 bg-gray-50">{a.name}</th>
                    ))}
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 bg-gray-50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: any, i: number) => {
                    const pct = max > 0 ? (item.total / max) * 100 : 0;
                    return (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" title={item.usageType}>
                        <td className="py-2 px-3 text-gray-700">
                          <div className="font-medium">{pretty(item.usageType)}</div>
                          <div className="bg-gray-200 rounded h-1.5 mt-1 w-40">
                            <div className="bg-blue-600 h-1.5 rounded" style={{ width: `${Math.max(pct, 2)}%` }}></div>
                          </div>
                        </td>
                        {accounts.map((a: any, ai: number) => (
                          <td key={ai} className="py-2 px-3 text-right text-gray-700">
                            {item.byAccount?.[a.name] !== undefined ? `$${item.byAccount[a.name].toFixed(2)}` : '—'}
                          </td>
                        ))}
                        <td className="py-2 px-3 text-right font-semibold text-gray-900">${item.total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-gray-300">
                    <td className="py-2 px-3 font-semibold text-gray-900">Total</td>
                    {accounts.map((a: any, ai: number) => (
                      <td key={ai} className="py-2 px-3 text-right font-semibold text-gray-900">${a.total.toFixed(2)}</td>
                    ))}
                    <td className="py-2 px-3 text-right font-bold text-gray-900">${grandTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// S3 Buckets Dashboard.
// No region shown in the header on purpose: S3 is global, list_buckets() returns the whole
// account, and the "in us-east-1" that MCP stamps on its response is meaningless here. The
// per-bucket Region column is the real answer to "where does this live".
const S3Dashboard: React.FC<{ dashboard: any; onClose: () => void }> = ({ dashboard, onClose }) => {
  const buckets = dashboard.data.buckets || [];

  const [search, setSearch] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('all');

  const regions = useMemo(() => {
    const set = new Set<string>(buckets.map((b: any) => b.region).filter(Boolean));
    return Array.from(set).sort();
  }, [buckets]);

  const filteredBuckets = useMemo(() => {
    let out = buckets;
    if (regionFilter !== 'all') out = out.filter((b: any) => b.region === regionFilter);
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((b: any) => (b.name || '').toLowerCase().includes(q));
    return out;
  }, [buckets, search, regionFilter]);

  const hasFilter = regionFilter !== 'all' || search.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">S3 Buckets</h1>
            <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            <span className="text-xs text-blue-600 mt-1 inline-block font-medium">Global — all regions</span>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300">Close</button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-gray-900">{filteredBuckets.length}</div>
            <div className="text-xs text-gray-600">{hasFilter ? `Matching Buckets (of ${buckets.length})` : 'Total Buckets'}</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-blue-700">{regions.length || '—'}</div>
            <div className="text-xs text-gray-600">Regions</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-green-700">{buckets.length}</div>
            <div className="text-xs text-gray-600">Buckets in Account</div>
          </div>
        </div>

        {buckets.length > 0 ? (
          <div className="bg-white rounded border border-gray-200">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-sm font-semibold text-gray-900">
                Bucket Details ({filteredBuckets.length}
                {filteredBuckets.length !== buckets.length && <span className="font-normal text-gray-500"> of {buckets.length}</span>})
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search bucket name"
                  className="text-xs px-2 py-1 border border-gray-300 rounded w-48 focus:outline-none focus:border-blue-400"
                />
                {regions.length > 0 && (
                  <select
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="all">All regions</option>
                    {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                )}
                {hasFilter && (
                  <button
                    onClick={() => { setSearch(''); setRegionFilter('all'); }}
                    className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {/* Accounts here run to 190+ buckets, so the body scrolls inside a fixed height
                and the header row stays put instead of the page growing without end. */}
            <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 bg-gray-50">#</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 bg-gray-50">Bucket Name</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 bg-gray-50">Region</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 bg-gray-50">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBuckets.length === 0 && (
                    <tr><td colSpan={4} className="py-6 px-3 text-center text-gray-500">No buckets match the current filters.</td></tr>
                  )}
                  {filteredBuckets.map((b: any, i: number) => (
                    <tr key={b.name || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                      <td className="py-2 px-3 font-mono text-gray-700 break-all">{b.name || '-'}</td>
                      <td className="py-2 px-3 text-gray-600">{b.region || '-'}</td>
                      <td className="py-2 px-3 text-gray-600">{b.created || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded p-6 border border-gray-200 text-center">
            <p className="text-sm text-gray-600">Found {dashboard.data.totalCount || 0} bucket(s), but detailed information is not available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const EBSDashboard: React.FC<{ dashboard: any; onClose: () => void }> = ({ dashboard, onClose }) => {
  // Data sourced from MCP server response — no redundant API call needed
  const volumes = dashboard.data.volumes || [];

  // Filters the backend already derived from the user's own words ("larger than 50GB").
  // These rows arrive ALREADY narrowed — the chips are informational, not re-applied here.
  const appliedFilters = dashboard.data.filters || null;
  const totalScanned = dashboard.data.totalScanned;

  // Extra filters the user can drive from the table itself, on top of whatever the query said.
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [minSize, setMinSize] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const filteredVolumes = useMemo(() => {
    let out = volumes;
    if (stateFilter !== 'all') {
      out = out.filter((v: any) => (v.status || v.state) === stateFilter);
    }
    const min = parseInt(minSize, 10);
    if (Number.isFinite(min)) {
      out = out.filter((v: any) => (v.metadata?.size ?? v.size ?? 0) >= min);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((v: any) => `${v.id || v.volumeId || ''} ${v.name || ''}`.toLowerCase().includes(q));
    }
    return out;
  }, [volumes, stateFilter, minSize, search]);

  const hasLocalFilter = stateFilter !== 'all' || minSize !== '' || search.trim() !== '';

  // Summary cards follow whatever is actually on screen. With no filter active
  // filteredVolumes === volumes, so these are the same numbers as before.
  const totalSize = filteredVolumes.reduce((sum: number, v: any) => sum + (v.metadata?.size ?? v.size ?? 0), 0);
  const inUseCount = filteredVolumes.filter((v: any) => (v.status || v.state) === 'in-use').length;
  const availableCount = filteredVolumes.filter((v: any) => (v.status || v.state) === 'available').length;

  // Estimated monthly storage cost of what is currently on screen. Only meaningful for
  // volumes nobody is attached to — an in-use volume's cost is not a saving — so the cost
  // chart below is gated on the unused filter.
  const showCostChart = appliedFilters?.state === 'available' || stateFilter === 'available';
  const costRows = useMemo(() => {
    return filteredVolumes
      .filter((v: any) => typeof v.estimatedMonthlyCost === 'number' && v.estimatedMonthlyCost > 0)
      .sort((a: any, b: any) => b.estimatedMonthlyCost - a.estimatedMonthlyCost);
  }, [filteredVolumes]);
  const costTotal = costRows.reduce((s: number, v: any) => s + v.estimatedMonthlyCost, 0);
  const costMax = costRows.length > 0 ? costRows[0].estimatedMonthlyCost : 0;
  const unpricedCount = filteredVolumes.length - costRows.length;

  const filterChipLabel = () => {
    if (!appliedFilters) return null;
    const bits: string[] = [];
    if (appliedFilters.size) bits.push(`Size ${appliedFilters.size.op} ${appliedFilters.size.value} GB`);
    if (appliedFilters.state) bits.push(`State: ${appliedFilters.state}`);
    if (appliedFilters.volumeType) bits.push(`Type: ${appliedFilters.volumeType}`);
    if (appliedFilters.encrypted !== undefined) bits.push(appliedFilters.encrypted ? 'Encrypted' : 'Unencrypted');
    return bits;
  };
  const queryChips = filterChipLabel();

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 mb-1">EBS Volumes</h1>
            <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              {dashboard.data.region && <span className="text-xs text-blue-600 inline-block font-medium">Region: {dashboard.data.region}</span>}
              {queryChips && queryChips.map((chip: string, i: number) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                  {chip}
                </span>
              ))}
              {/* The MCP response can arrive without the field a filter needs. Saying so beats
                  showing rows that look narrowed when they were not. */}
              {dashboard.data.unappliedFilters?.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-medium">
                  {dashboard.data.unappliedFilters.join(', ')} not reported — filter not applied
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300">Close</button>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-gray-900">{volumes.length > 0 ? filteredVolumes.length : (dashboard.data.totalCount || 0)}</div>
            <div className="text-xs text-gray-600">
              {(queryChips?.length || hasLocalFilter) && totalScanned ? `Matching Volumes (of ${totalScanned})` : 'Total Volumes'}
            </div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-green-700">{inUseCount}</div>
            <div className="text-xs text-gray-600">In Use</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-yellow-600">{availableCount}</div>
            <div className="text-xs text-gray-600">Available</div>
          </div>
          <div className="bg-white rounded p-3 border border-gray-200">
            <div className="text-lg font-bold text-blue-700">{totalSize} GB</div>
            <div className="text-xs text-gray-600">Total Size</div>
          </div>
        </div>

        {/* Estimated cost of idle storage. Single series, so no legend — the heading names
            it. Values sit in ink tokens; the bar alone carries magnitude. Matches the
            Service Breakdown bar list used elsewhere in the app. */}
        {showCostChart && costRows.length > 0 && (
          <div className="bg-white rounded p-4 border border-gray-200 mb-4">
            <div className="flex items-baseline justify-between mb-1 gap-3 flex-wrap">
              <h2 className="text-sm font-semibold text-gray-900">Estimated monthly cost of unused volumes</h2>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  ~${costTotal.toFixed(2)}<span className="text-xs font-normal text-gray-500">/mo</span>
                </div>
                <div className="text-xs text-gray-500">~${(costTotal * 12).toFixed(2)}/year</div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Storage list price only — excludes IOPS, throughput, snapshots and any account discount.
              {unpricedCount > 0 && ` ${unpricedCount} volume(s) could not be priced, so the total is a floor.`}
            </p>
            <div className="space-y-2">
              {costRows.slice(0, 15).map((v: any, index: number) => {
                const pct = costMax > 0 ? (v.estimatedMonthlyCost / costMax) * 100 : 0;
                return (
                  <div
                    key={v.volumeId || index}
                    className="flex items-center text-xs"
                    title={`${v.volumeId} — ${v.size} GB ${v.volumeType || ''} in ${v.availabilityZone || v.region || 'unknown AZ'} — ~$${v.estimatedMonthlyCost.toFixed(2)}/mo`}
                  >
                    <div className="w-48 font-mono text-gray-700 truncate">{v.volumeId}</div>
                    <div className="flex-1 mx-3">
                      <div className="bg-gray-200 rounded h-3 relative">
                        <div className="bg-blue-600 h-3 rounded" style={{ width: `${Math.max(pct, 2)}%` }}></div>
                      </div>
                    </div>
                    <div className="w-16 text-right text-gray-500">{v.size} GB</div>
                    <div className="w-20 text-right font-semibold text-gray-900">${v.estimatedMonthlyCost.toFixed(2)}</div>
                  </div>
                );
              })}
              {costRows.length > 15 && (
                <p className="text-xs text-gray-500 pt-1">…and {costRows.length - 15} more in the table below.</p>
              )}
            </div>
          </div>
        )}

        {volumes.length > 0 ? (
          <div className="bg-white rounded border border-gray-200">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-sm font-semibold text-gray-900">
                Volume Details ({filteredVolumes.length}
                {filteredVolumes.length !== volumes.length && <span className="font-normal text-gray-500"> of {volumes.length}</span>})
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search ID or name"
                  className="text-xs px-2 py-1 border border-gray-300 rounded w-40 focus:outline-none focus:border-blue-400"
                />
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400"
                >
                  <option value="all">All states</option>
                  <option value="in-use">In use</option>
                  <option value="available">Available</option>
                </select>
                <input
                  type="number"
                  value={minSize}
                  onChange={(e) => setMinSize(e.target.value)}
                  placeholder="Min GB"
                  className="text-xs px-2 py-1 border border-gray-300 rounded w-20 focus:outline-none focus:border-blue-400"
                />
                {hasLocalFilter && (
                  <button
                    onClick={() => { setStateFilter('all'); setMinSize(''); setSearch(''); }}
                    className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Volume ID</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Size (GB)</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Type</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">State</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Attached To</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">AZ</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Encrypted</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Est. $/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVolumes.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-6 px-3 text-center text-gray-500">
                        No volumes match the current filters.
                      </td>
                    </tr>
                  )}
                  {filteredVolumes.map((vol: any, index: number) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 font-mono text-gray-600">{vol.id || vol.volumeId || '-'}</td>
                      <td className="py-2 px-3 text-gray-700">{vol.name || '-'}</td>
                      <td className="py-2 px-3 text-gray-700 font-medium">{vol.metadata?.size || vol.size || '-'}</td>
                      <td className="py-2 px-3 text-gray-700">{vol.metadata?.volumeType || vol.volumeType || '-'}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          (vol.status || vol.state) === 'in-use' ? 'bg-green-100 text-green-700' :
                          (vol.status || vol.state) === 'available' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{vol.status || vol.state || '-'}</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600">{vol.metadata?.attachedTo || vol.attachedTo || '-'}</td>
                      <td className="py-2 px-3 text-gray-600">{vol.metadata?.availabilityZone || vol.availabilityZone || '-'}</td>
                      <td className="py-2 px-3 text-gray-600">{(vol.metadata?.encrypted ?? vol.encrypted) ? 'Yes' : 'No'}</td>
                      <td className="py-2 px-3 text-right text-gray-700">
                        {typeof vol.estimatedMonthlyCost === 'number' ? `$${vol.estimatedMonthlyCost.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded p-6 border border-gray-200 text-center">
            {dashboard.data.sweepIncomplete ? (
              // Not "zero volumes" — the all-regions sweep ran but its rows never made it
              // back through MCP's text formatter. Saying 0 here would be a false negative.
              <>
                <p className="text-sm text-gray-600">
                  The all-regions scan ran, but no volume rows came back from the MCP server.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  This is <span className="font-medium">not</span> a result of zero volumes. Ask for one region (e.g. "in ap-south-1") for a reliable answer.
                </p>
              </>
            ) : totalScanned > 0 ? (
              // A filter from the query excluded every row. Saying "details not available"
              // here reads as a failure when the scan actually worked and the honest answer
              // is simply "none matched".
              <>
                <p className="text-sm text-gray-600">
                  None of the {totalScanned} volume(s) in {dashboard.data.region || 'this region'} match {queryChips?.length ? queryChips.join(' + ') : 'the filter'}.
                </p>
                {dashboard.data.regionExplicit === false && (
                  <p className="text-xs text-gray-500 mt-2">
                    No region was named, so only <span className="font-medium">{dashboard.data.region}</span> was checked. Add e.g. "in ap-south-1" to look elsewhere.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">Found {dashboard.data.totalCount || 0} volume(s), but detailed information is not available.</p>
                <p className="text-xs text-gray-500 mt-2">Volume details may not be accessible.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Modern Dashboard Components
const ModernDashboard: React.FC<{
  dashboard: VisualizationData;
  onClose: () => void;
}> = ({ dashboard, onClose }) => {

  // Error Dashboard
  if (dashboard.type === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-red-200">
            <div>
              <h1 className="text-lg font-semibold text-red-600 mb-1">❌ Query Failed</h1>
              <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
            >
              Close
            </button>
          </div>
          <div className="bg-white rounded p-6 border border-red-200 text-center">
            <div className="text-4xl mb-3">⚠️</div>
            {dashboard.title.startsWith('Error Code:') ? (
              <>
                <p className="text-xs font-mono font-semibold text-red-500 mb-2">{dashboard.title}</p>
                <p className="text-sm text-gray-700">{dashboard.data.error || 'An unknown error occurred'}</p>
              </>
            ) : (
              <>
                <h2 className="text-sm font-semibold text-red-600 mb-2">Service Error</h2>
                <p className="text-sm text-gray-700 bg-red-50 rounded p-3">{dashboard.data.error || 'An unknown error occurred'}</p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-3">Please try again or contact support if the issue persists.</p>
          </div>
        </div>
      </div>
    );
  }

  // Cost Dashboard
  if (dashboard.data.queryType === 'cost') {
    // Handle total-only visualization (no service breakdown)
    if (dashboard.data.totalOnly) {
      return (
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
              <div>
                <h1 className="text-lg font-semibold text-gray-900 mb-1">Cost Summary</h1>
                <p className="text-xs text-gray-600">"{dashboard.query}"</p>
              </div>
              <button
                onClick={onClose}
                className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
              >
                Close
              </button>
            </div>

            {/* Total Cost Card */}
            <div className="bg-white rounded p-6 border border-gray-200 text-center">
              <h2 className="text-sm font-semibold text-gray-600 mb-2">Total AWS Cost</h2>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                ${dashboard.data.total.toFixed(2)}
              </div>
              <p className="text-xs text-gray-500">{dashboard.data.period}</p>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-600">
                  Service breakdown not available. Ask for "service-wise breakdown" to see detailed costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Detect if this is a period comparison (services with month/period names like "2025", "March 2025")
    const isPeriodComparison = dashboard.data.services.some((s: any) =>
      s.service.match(/^(january|february|march|april|may|june|july|august|september|october|november|december)\s*\d{4}$|^\d{4}$/i)
    );

    // Check if it's a service-wise comparison query
    const isServiceWiseComparison = dashboard.query.toLowerCase().includes('service') &&
      dashboard.query.toLowerCase().includes('compare');

    // If service-wise comparison, we need to group data by service across periods
    let serviceComparisonData: any = null;
    if (isServiceWiseComparison && dashboard.data.services) {
      // Try to detect if we have structured comparison data
      // The data might be nested with periods as keys
      const periods = Object.keys(dashboard.data).filter(k => k !== 'services' && k !== 'total' && k !== 'queryType');

      if (periods.length > 0) {
        // Structure: { "March 2025": { services: [...] }, "April 2025": { services: [...] } }
        serviceComparisonData = {};
        periods.forEach(period => {
          if (dashboard.data[period] && dashboard.data[period].services) {
            dashboard.data[period].services.forEach((svc: any) => {
              if (!serviceComparisonData[svc.service]) {
                serviceComparisonData[svc.service] = {};
              }
              serviceComparisonData[svc.service][period] = svc.cost;
            });
          }
        });
      }
    }

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 mb-1">Cost Analysis</h1>
              <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
            >
              Close
            </button>
          </div>

          {serviceComparisonData && Object.keys(serviceComparisonData).length > 0 ? (
            // Service-wise Comparison across periods - AWS Cost Explorer Style
            <div className="bg-white rounded p-4 border border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Cost Comparison by Service</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700 sticky left-0 bg-gray-50">Service</th>
                      {Object.keys(Object.values(serviceComparisonData)[0] || {}).map((period, idx) => (
                        <th key={idx} className="text-right py-2 px-3 font-semibold text-gray-700">{period}</th>
                      ))}
                      <th className="text-right py-2 px-3 font-semibold text-gray-700">Difference</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-700">Change %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(serviceComparisonData).map(([serviceName, periods]: [string, any], index) => {
                      const periodKeys = Object.keys(periods);
                      const costs = periodKeys.map(p => periods[p]);
                      const difference = costs.length === 2 ? costs[1] - costs[0] : 0;
                      const changePercent = costs.length === 2 && costs[0] !== 0 ? ((difference / costs[0]) * 100) : 0;

                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium text-gray-900 sticky left-0 bg-white">{serviceName}</td>
                          {periodKeys.map((period, pIdx) => (
                            <td key={pIdx} className="py-2 px-3 text-right text-gray-700">${periods[period].toFixed(2)}</td>
                          ))}
                          <td className={`py-2 px-3 text-right font-semibold ${difference >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {difference >= 0 ? '+$' : '-$'}{Math.abs(difference).toFixed(2)}
                          </td>
                          <td className={`py-2 px-3 text-right font-semibold ${changePercent >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr>
                      <td className="py-2 px-3 font-bold text-gray-900 sticky left-0 bg-gray-50">Total</td>
                      {Object.keys(Object.values(serviceComparisonData)[0] || {}).map((period, idx) => {
                        const totalCost = Object.values(serviceComparisonData).reduce((sum: number, periods: any) => sum + (periods[period] || 0), 0);
                        return (
                          <td key={idx} className="py-2 px-3 text-right font-bold text-gray-900">${totalCost.toFixed(2)}</td>
                        );
                      })}
                      <td className="py-2 px-3"></td>
                      <td className="py-2 px-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : isPeriodComparison ? (
            // Period Comparison View
            <div className="bg-white rounded p-4 border border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Period Comparison</h2>
              <div className="space-y-3">
                {dashboard.data.services.map((item: any, index: number) => {
                  const maxCost = Math.max(...dashboard.data.services.map((s: any) => s.cost));
                  const widthPercentage = (item.cost / maxCost) * 100;

                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-700">{item.service}</span>
                        <span className="text-xs font-bold text-gray-900">{item.costFormatted}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded h-6 relative">
                        <div
                          className="bg-blue-600 h-6 rounded flex items-center justify-end pr-2"
                          style={{ width: `${Math.max(widthPercentage, 5)}%` }}
                        >
                          <span className="text-xs font-semibold text-white">
                            {(dashboard.data.total > 0 ? (item.cost / dashboard.data.total) * 100 : 0).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">Total</span>
                  <span className="text-sm font-bold text-gray-900">${dashboard.data.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          ) : (
            // Regular Service Breakdown
            (() => {
              const activeServices = dashboard.data.services.filter((s: any) => s.cost > 0);
              return (
              <>
              {/* Cost Summary */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-white rounded p-3 border border-gray-200">
                  <div className="text-lg font-bold text-gray-900">${dashboard.data.total.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">Total Cost</div>
                </div>
                <div className="bg-white rounded p-3 border border-gray-200">
                  <div className="text-lg font-bold text-gray-900">{activeServices.length}</div>
                  <div className="text-xs text-gray-600">Services</div>
                </div>
                <div className="bg-white rounded p-3 border border-gray-200">
                  <div className="text-lg font-bold text-gray-900">
                    ${activeServices.length > 0 ? (dashboard.data.total / activeServices.length).toFixed(2) : '0.00'}
                  </div>
                  <div className="text-xs text-gray-600">Avg per Service</div>
                </div>
              </div>

              {/* Service Breakdown - Horizontal Bars */}
              {activeServices.length > 0 ? (
              <div className="bg-white rounded p-4 border border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Service Breakdown</h2>
                <div className="space-y-2">
                  {activeServices.map((service: any, index: number) => {
                    const percentage = dashboard.data.total > 0 ? (service.cost / dashboard.data.total) * 100 : 0;
                    return (
                      <div key={index} className="flex items-center text-xs">
                        <div className="w-48 font-medium text-gray-700">{service.service}</div>
                        <div className="flex-1 mx-3">
                          <div className="bg-gray-200 rounded h-3 relative">
                            <div
                              className="bg-blue-600 h-3 rounded"
                              style={{ width: `${Math.max(percentage, 2)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="w-20 text-right font-semibold text-gray-900">{service.costFormatted}</div>
                        <div className="w-12 text-right text-gray-500">{percentage.toFixed(1)}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              ) : (
              <div className="bg-white rounded p-4 border border-gray-200 text-center text-sm text-gray-500">
                No services with costs found for this period.
              </div>
              )}
              </>
              );
            })()
          )}
        </div>
      </div>
    );
  }

  // Cost Comparison Dashboard
  // Multi-Account Cost Dashboard (all-accounts fan-out).
  // Deliberately its own block: the cost_comparison renderer below is built around
  // period-over-period language (MoM Change, "vs previous month", trend arrows), which
  // would be wrong for accounts. That block stays exactly as it is.
  if (dashboard.data.queryType === 'cost_multi_account') {
    const { accounts = [], serviceRows = [], grandTotal = 0, accountNames = [], skippedAccounts = [] } = dashboard.data;
    // Multi-month runs split each account into month sub-columns; single-month keeps one column.
    const periods: string[] = dashboard.data.periods || [];
    const periodTotals = dashboard.data.periodTotals || {};
    const isMultiPeriod = !!dashboard.data.isMultiPeriod && periods.length > 1;
    const ACCOUNT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 mb-1">Cost Across Accounts</h1>
              <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            </div>
            <button onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300">Close</button>
          </div>

          {/* Per-account totals + grand total */}
          <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: `repeat(${Math.min(accounts.length + 1, 5)}, minmax(0, 1fr))` }}>
            {accounts.map((acc: any, idx: number) => (
              <div key={idx} className="bg-white rounded p-3 border border-gray-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length] }} />
                  <div className="text-xs text-gray-500 truncate" title={acc.name}>{acc.name}</div>
                </div>
                <div className="text-lg font-bold text-gray-900">${acc.total?.toFixed(2) ?? '—'}</div>
                <div className="text-xs text-gray-400 mt-1">{acc.sharePercent}% of total</div>
                {/* Month split, so a multi-month run shows where the total came from */}
                {isMultiPeriod && acc.perPeriod && (
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
                    {periods.map((p: string) => (
                      <div key={p} className="flex justify-between text-xs">
                        <span className="text-gray-500">{p}</span>
                        <span className="text-gray-700">${(acc.perPeriod[p] ?? 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="bg-white rounded p-3 border-2 border-blue-200">
              <div className="text-xs text-gray-500 mb-1">GRAND TOTAL</div>
              <div className="text-lg font-bold text-blue-700">${grandTotal?.toFixed(2) ?? '—'}</div>
              <div className="text-xs text-gray-400 mt-1">{accounts.length} account{accounts.length === 1 ? '' : 's'}</div>
            </div>
          </div>

          {/* Share bar — one row, each account proportional */}
          {grandTotal > 0 && accounts.length > 1 && (
            <div className="bg-white rounded p-4 border border-gray-200 mb-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Spend Split</h2>
              <div className="flex h-6 rounded overflow-hidden mb-2">
                {accounts.map((acc: any, idx: number) => (
                  <div
                    key={idx}
                    style={{ width: `${Math.max(acc.sharePercent, 0.5)}%`, background: ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length] }}
                    title={`${acc.name}: $${acc.total?.toFixed(2)} (${acc.sharePercent}%)`}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                {accounts.map((acc: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length] }} />
                    {acc.name} — ${acc.total?.toFixed(2)} ({acc.sharePercent}%)
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service rows x account columns */}
          {serviceRows.length > 0 && (
            <div className="bg-white rounded border border-gray-200 mb-4">
              <div className="p-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">
                  {isMultiPeriod ? 'Service Breakdown by Account & Month' : 'Service Breakdown by Account'}
                </h2>
              </div>

              {/* Multi-month: each account gets one sub-column per month */}
              {isMultiPeriod ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    {/* Row 1: account groups */}
                    <tr>
                      <th rowSpan={2} className="text-left py-2 px-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 align-bottom">Service</th>
                      {accountNames.map((name: string, idx: number) => (
                        <th
                          key={idx}
                          colSpan={periods.length}
                          className="text-center py-2 px-3 font-semibold text-gray-700 border-l border-gray-200 whitespace-nowrap"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length] }} />
                            {name}
                          </span>
                        </th>
                      ))}
                      <th rowSpan={2} className="text-right py-2 px-3 font-semibold text-gray-700 border-l border-gray-200 align-bottom">Total</th>
                    </tr>
                    {/* Row 2: month sub-headers */}
                    <tr>
                      {accountNames.map((name: string, aIdx: number) =>
                        periods.map((p: string, pIdx: number) => (
                          <th
                            key={`${aIdx}-${pIdx}`}
                            className={`text-right py-1.5 px-3 font-medium text-gray-500 whitespace-nowrap ${pIdx === 0 ? 'border-l border-gray-200' : ''}`}
                          >
                            {p}
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {serviceRows.map((row: any, i: number) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-900 sticky left-0 bg-white">{row.service}</td>
                        {accountNames.map((name: string, aIdx: number) =>
                          periods.map((p: string, pIdx: number) => {
                            const val = row.cells?.[name]?.[p] ?? 0;
                            return (
                              <td
                                key={`${aIdx}-${pIdx}`}
                                className={`py-2 px-3 text-right ${val > 0 ? 'text-gray-700' : 'text-gray-300'} ${pIdx === 0 ? 'border-l border-gray-100' : ''}`}
                              >
                                {val > 0 ? `$${val.toFixed(2)}` : '—'}
                              </td>
                            );
                          })
                        )}
                        <td className="py-2 px-3 text-right font-semibold text-gray-900 border-l border-gray-100">${row.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr>
                      <td className="py-2 px-3 font-bold text-gray-900 sticky left-0 bg-gray-50">Total</td>
                      {accounts.map((acc: any, aIdx: number) =>
                        periods.map((p: string, pIdx: number) => (
                          <td
                            key={`${aIdx}-${pIdx}`}
                            className={`py-2 px-3 text-right font-bold text-gray-900 ${pIdx === 0 ? 'border-l border-gray-200' : ''}`}
                          >
                            ${(acc.perPeriod?.[p] ?? 0).toFixed(2)}
                          </td>
                        ))
                      )}
                      <td className="py-2 px-3 text-right font-bold text-blue-700 border-l border-gray-200">${grandTotal?.toFixed(2)}</td>
                    </tr>
                    {/* Month totals across every account */}
                    <tr className="border-t border-gray-200">
                      <td className="py-2 px-3 text-xs font-semibold text-gray-500 sticky left-0 bg-gray-50">All accounts</td>
                      {accountNames.map((name: string, aIdx: number) =>
                        periods.map((p: string, pIdx: number) => (
                          <td
                            key={`${aIdx}-${pIdx}`}
                            className={`py-2 px-3 text-right text-xs text-gray-500 ${pIdx === 0 ? 'border-l border-gray-200' : ''}`}
                          >
                            {aIdx === 0 ? `$${(periodTotals[p] ?? 0).toFixed(2)}` : ''}
                          </td>
                        ))
                      )}
                      <td className="py-2 px-3 border-l border-gray-200"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700 sticky left-0 bg-gray-50">Service</th>
                      {accountNames.map((name: string, idx: number) => (
                        <th key={idx} className="text-right py-2 px-3 font-semibold text-gray-700 whitespace-nowrap">{name}</th>
                      ))}
                      <th className="text-right py-2 px-3 font-semibold text-gray-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceRows.map((row: any, i: number) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-900 sticky left-0 bg-white">{row.service}</td>
                        {accountNames.map((name: string, idx: number) => {
                          const val = row.perAccountCost?.[name] ?? 0;
                          return (
                            <td key={idx} className={`py-2 px-3 text-right ${val > 0 ? 'text-gray-700' : 'text-gray-300'}`}>
                              {val > 0 ? `$${val.toFixed(2)}` : '—'}
                            </td>
                          );
                        })}
                        <td className="py-2 px-3 text-right font-semibold text-gray-900">${row.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr>
                      <td className="py-2 px-3 font-bold text-gray-900 sticky left-0 bg-gray-50">Total</td>
                      {accounts.map((acc: any, idx: number) => (
                        <td key={idx} className="py-2 px-3 text-right font-bold text-gray-900">${acc.total?.toFixed(2)}</td>
                      ))}
                      <td className="py-2 px-3 text-right font-bold text-blue-700">${grandTotal?.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              )}
            </div>
          )}

          {/* Accounts that could not be included */}
          {skippedAccounts.length > 0 && (
            <div className="bg-yellow-50 rounded p-3 border border-yellow-200">
              <p className="text-xs text-yellow-800">
                <span className="font-semibold">Not included:</span> {skippedAccounts.join(', ')}
                <span className="text-yellow-700"> — no cost data returned (role assumption may have failed).</span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (dashboard.data.queryType === 'cost_comparison') {
    const { periods, comparisonData, totalComparison, chartData, momLabel } = dashboard.data;
    const periodCount = dashboard.data.periodCount || (periods?.length ?? 2);
    const period1 = totalComparison?.period1;
    const period2 = totalComparison?.period2;
    const allPeriods = totalComparison?.allPeriods;
    const isMultiPeriod = periodCount > 2 && allPeriods;

    // Color palette for stacked bar chart
    const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6366F1'];

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 mb-1">Cost Comparison</h1>
              <p className="text-xs text-gray-600">"{dashboard.query}"</p>
            </div>
            <button onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300">Close</button>
          </div>

          {/* ==================== 3+ PERIOD LAYOUT ==================== */}
          {isMultiPeriod ? (
            <>
              {/* Period Total Cards + MoM Change */}
              <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: `repeat(${allPeriods.length + 1}, minmax(0, 1fr))` }}>
                {allPeriods.map((p: any, idx: number) => (
                  <div key={idx} className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">{p.name}</div>
                    <div className="text-lg font-bold text-gray-900">${p.total?.toFixed(2) ?? '—'}</div>
                    {idx > 0 && (() => {
                      const prev = allPeriods[idx - 1].total;
                      const diff = p.total - prev;
                      const pct = prev > 0 ? ((diff / prev) * 100) : 0;
                      return (
                        <div className={`text-xs mt-1 ${diff > 0 ? 'text-red-500' : diff < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                          {diff > 0 ? '+' : ''}{diff.toFixed(0)} ({pct.toFixed(1)}%) vs prev
                        </div>
                      );
                    })()}
                  </div>
                ))}
                <div className={`bg-white rounded p-3 border ${totalComparison.trend === 'increased' ? 'border-red-200' : 'border-green-200'}`}>
                  <div className="text-xs text-gray-500 mb-1">MoM Change</div>
                  <div className={`text-lg font-bold ${totalComparison.trend === 'increased' ? 'text-red-600' : 'text-green-600'}`}>
                    {totalComparison.trend === 'increased' ? '+' : ''}{totalComparison.difference >= 0 ? '+' : '-'}${Math.abs(totalComparison.difference ?? 0).toFixed(2)}
                  </div>
                  <div className={`text-xs ${totalComparison.trend === 'increased' ? 'text-red-500' : 'text-green-500'}`}>
                    {momLabel || 'vs previous month'} ({totalComparison.percentageChange?.toFixed(1)}%)
                  </div>
                </div>
              </div>

              {/* Charts Row: Line Chart + Stacked Bar */}
              {chartData && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Line Chart: Monthly Cost Trend */}
                  <div className="bg-white rounded p-4 border border-gray-200">
                    <h2 className="text-sm font-semibold text-gray-900 mb-3">Monthly Cost Trend</h2>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartData.trendLine}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(1)}k`} />
                        <Tooltip
                          formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Total Cost']}
                          labelFormatter={(label: any) => {
                            const item = chartData.trendLine.find((d: any) => d.name === String(label));
                            if (item?.momPercent !== null && item?.momPercent !== undefined) {
                              return `${item.fullName} (${item.momPercent > 0 ? '+' : ''}${item.momPercent}% vs prev)`;
                            }
                            return item?.fullName || String(label);
                          }}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke="#3B82F6"
                          strokeWidth={2.5}
                          dot={{ r: 5, fill: '#3B82F6', strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Stacked Bar Chart: Service Breakdown by Month */}
                  <div className="bg-white rounded p-4 border border-gray-200">
                    <h2 className="text-sm font-semibold text-gray-900 mb-3">Service Breakdown by Month</h2>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={chartData.stackedBar}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(1)}k`} />
                        <Tooltip formatter={(value: any) => `$${Number(value).toFixed(2)}`} contentStyle={{ fontSize: 11 }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        {chartData.serviceKeys.map((key: string, idx: number) => (
                          <Bar key={key} dataKey={key} stackId="a" fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Service Detail Table with MoM + Trend Arrows */}
              {comparisonData && comparisonData.length > 0 && (
                <div className="bg-white rounded border border-gray-200">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">Service Details</h2>
                    <span className="text-xs text-gray-500">MoM = {momLabel || 'Month-over-Month'}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Service</th>
                          {periods.map((pName: string, idx: number) => (
                            <th key={idx} className="text-right py-2 px-3 font-semibold text-gray-700">{pName}</th>
                          ))}
                          <th className="text-right py-2 px-3 font-semibold text-gray-700">MoM Change</th>
                          <th className="text-center py-2 px-3 font-semibold text-gray-700">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.map((row: any, i: number) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 text-gray-900 font-medium">{row.service}</td>
                            {row.periodCosts && periods.map((pName: string, idx: number) => (
                              <td key={idx} className="py-2 px-3 text-right text-gray-700">
                                ${(row.periodCosts[pName] ?? 0).toFixed(2)}
                              </td>
                            ))}
                            <td className={`py-2 px-3 text-right font-semibold ${row.difference > 0.01 ? 'text-red-600' : row.difference < -0.01 ? 'text-green-600' : 'text-gray-500'}`}>
                              {row.difference > 0 ? '+' : ''}{row.difference.toFixed(2)}
                              <span className="text-gray-400 ml-1">({row.percentageChange.toFixed(1)}%)</span>
                            </td>
                            <td className="py-2 px-3 text-center text-lg">
                              {row.trend === 'increased' ? <span className="text-red-500">&#8593;</span>
                                : row.trend === 'decreased' ? <span className="text-green-500">&#8595;</span>
                                : <span className="text-gray-400">&#8594;</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                        <tr>
                          <td className="py-2 px-3 font-bold text-gray-900">Total</td>
                          {allPeriods.map((p: any, idx: number) => (
                            <td key={idx} className="py-2 px-3 text-right font-bold text-gray-900">${p.total?.toFixed(2)}</td>
                          ))}
                          <td className={`py-2 px-3 text-right font-bold ${totalComparison.trend === 'increased' ? 'text-red-600' : 'text-green-600'}`}>
                            {totalComparison.difference >= 0 ? '+' : ''}{totalComparison.difference?.toFixed(2)}
                            <span className="text-gray-400 ml-1">({totalComparison.percentageChange?.toFixed(1)}%)</span>
                          </td>
                          <td className="py-2 px-3 text-center text-lg">
                            {totalComparison.trend === 'increased' ? <span className="text-red-500">&#8593;</span>
                              : totalComparison.trend === 'decreased' ? <span className="text-green-500">&#8595;</span>
                              : <span className="text-gray-400">&#8594;</span>}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ==================== 2-PERIOD LAYOUT (unchanged) ==================== */
            <>
              {/* Period Totals */}
              {period1 && period2 && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">{period2.name}</div>
                    <div className="text-lg font-bold text-gray-900">${period2.total?.toFixed(2) ?? '—'}</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">{period1.name}</div>
                    <div className="text-lg font-bold text-gray-900">${period1.total?.toFixed(2) ?? '—'}</div>
                  </div>
                  <div className={`bg-white rounded p-3 border ${totalComparison.trend === 'increased' ? 'border-red-200' : 'border-green-200'}`}>
                    <div className="text-xs text-gray-500 mb-1">Change</div>
                    <div className={`text-lg font-bold ${totalComparison.trend === 'increased' ? 'text-red-600' : 'text-green-600'}`}>
                      {totalComparison.trend === 'increased' ? '+' : '-'}${Math.abs(totalComparison.difference ?? 0).toFixed(2)}
                      <span className="text-xs ml-1">({totalComparison.percentageChange?.toFixed(1)}%)</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Service-level comparison table */}
              {comparisonData && comparisonData.length > 0 && (
                <div className="bg-white rounded border border-gray-200">
                  <div className="p-3 border-b border-gray-200">
                    <h2 className="text-sm font-semibold text-gray-900">Service Breakdown</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Service</th>
                          <th className="text-right py-2 px-3 font-semibold text-gray-700">{period2?.name ?? 'Period 1'}</th>
                          <th className="text-right py-2 px-3 font-semibold text-gray-700">{period1?.name ?? 'Period 2'}</th>
                          <th className="text-right py-2 px-3 font-semibold text-gray-700">Difference</th>
                          <th className="text-right py-2 px-3 font-semibold text-gray-700">Change %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.map((row: any, i: number) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 text-gray-900">{row.service}</td>
                            <td className="py-2 px-3 text-right text-gray-700">${(row.period2Cost ?? 0).toFixed(2)}</td>
                            <td className="py-2 px-3 text-right text-gray-700">${(row.period1Cost ?? 0).toFixed(2)}</td>
                            <td className={`py-2 px-3 text-right font-semibold ${row.trend === 'increased' ? 'text-red-600' : row.trend === 'decreased' ? 'text-green-600' : 'text-gray-500'}`}>
                              {row.trend === 'increased' ? '+' : row.trend === 'decreased' ? '-' : ''}${Math.abs(row.difference ?? 0).toFixed(2)}
                            </td>
                            <td className={`py-2 px-3 text-right font-semibold ${row.trend === 'increased' ? 'text-red-600' : row.trend === 'decreased' ? 'text-green-600' : 'text-gray-500'}`}>
                              {row.trend === 'increased' ? '+' : row.trend === 'decreased' ? '-' : ''}{(row.percentageChange ?? 0).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // EC2 Dashboard
  if (dashboard.data.queryType === 'ec2') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 mb-1">EC2 Instances</h1>
              <p className="text-xs text-gray-600">"{dashboard.query}"</p>
              {dashboard.data.region && (
                <span className="text-xs text-blue-600 mt-1 inline-block font-medium">
                  Region: {dashboard.data.region}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
            >
              Close
            </button>
          </div>

          {/* EC2 Summary Cards */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-gray-900">{dashboard.data.totalCount || 0}</div>
              <div className="text-xs text-gray-600">Total Instances</div>
            </div>
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-green-700">
                {dashboard.data.instances?.filter((i: any) => i.state === 'running').length || 0}
              </div>
              <div className="text-xs text-gray-600">Running</div>
            </div>
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-red-700">
                {dashboard.data.instances?.filter((i: any) => i.state === 'stopped').length || 0}
              </div>
              <div className="text-xs text-gray-600">Stopped</div>
            </div>
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-blue-700">
                {dashboard.data.region || 'N/A'}
              </div>
              <div className="text-xs text-gray-600">Region</div>
            </div>
          </div>

          {/* Instance Details */}
          {dashboard.data.instances && dashboard.data.instances.length > 0 ? (
            <div className="bg-white rounded border border-gray-200">
              <div className="p-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Instance Details</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Instance ID</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Type</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">State</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">AZ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.data.instances.map((instance: any, index: number) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono text-gray-600">{instance.instanceId}</td>
                        <td className="py-2 px-3 text-gray-700">{instance.name || '-'}</td>
                        <td className="py-2 px-3 text-gray-700">{instance.type || '-'}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${instance.state === 'running' ? 'bg-green-100 text-green-700' :
                            instance.state === 'stopped' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                            {instance.state}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{instance.availabilityZone || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded p-6 border border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Found {dashboard.data.totalCount || 0} instance(s), but detailed information is not available.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                The backend returned only a count. Instance details may not be accessible.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // EBS Volumes Dashboard
  if (dashboard.data.queryType === 'ebs') {
    return <EBSDashboard dashboard={dashboard} onClose={onClose} />;
  }

  // S3 Buckets Dashboard
  if (dashboard.data.queryType === 's3') {
    return <S3Dashboard dashboard={dashboard} onClose={onClose} />;
  }

  // EBS Volume Cost Dashboard
  if (dashboard.data.queryType === 'ebs_cost') {
    return <EBSCostDashboard dashboard={dashboard} onClose={onClose} />;
  }

  // EBS Volume Cost across accounts
  if (dashboard.data.queryType === 'ebs_cost_multi_account') {
    return <EBSCostMultiAccountDashboard dashboard={dashboard} onClose={onClose} />;
  }

  // Infrastructure Dashboard
  if (dashboard.data.queryType === 'infrastructure') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 mb-1">Infrastructure</h1>
              <p className="text-xs text-gray-600">"{dashboard.query}"</p>
              {dashboard.data.region && (
                <span className="text-xs text-gray-500 mt-1 inline-block">
                  Region: {dashboard.data.region}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
            >
              Close
            </button>
          </div>

          {/* Infrastructure Summary */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-gray-900">{dashboard.data.total}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-green-700">{dashboard.data.running}</div>
              <div className="text-xs text-gray-600">Running</div>
            </div>
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-red-700">{dashboard.data.stopped}</div>
              <div className="text-xs text-gray-600">Stopped</div>
            </div>
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-lg font-bold text-gray-900">
                {dashboard.data.total > 0 ? Math.round((dashboard.data.running / dashboard.data.total) * 100) : 0}%
              </div>
              <div className="text-xs text-gray-600">Uptime</div>
            </div>
          </div>

          {/* Instance Table */}
          <div className="bg-white rounded border border-gray-200">
            <div className="p-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Instances</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Instance ID</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.data.instances.map((instance: any, index: number) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 px-3 font-mono text-gray-600">{instance.instanceId}</td>
                      <td className="py-2 px-3 font-medium text-gray-900">
                        {instance.name === 'Unnamed' ? (
                          <span className="text-gray-400 italic">Unnamed</span>
                        ) : (
                          instance.name
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${instance.state.toLowerCase() === 'running'
                          ? 'bg-green-100 text-green-800'
                          : instance.state.toLowerCase() === 'stopped'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {instance.state}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{dashboard.title}</h1>
          <button
            onClick={onClose}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
          >
            ✕ Close
          </button>
        </div>
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <p className="text-gray-600">Dashboard for: {dashboard.query}</p>
        </div>
      </div>
    </div>
  );
};

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true); // Add loading state
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [showSignup, setShowSignup] = useState<boolean>(false);
  const [loginForm, setLoginForm] = useState({ email: 'testuser@devopsx.com', password: 'testpassword123' });
  const [signupForm, setSignupForm] = useState({ full_name: '', organisation: '', email: '', password: '', number: '' });
  const [authError, setAuthError] = useState<string>('');

  // Logging system state
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logsConnected, setLogsConnected] = useState<boolean>(false);
  const [logFilter, setLogFilter] = useState<'all' | 'frontend' | 'backend' | 'mcp'>('all');

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [executionPlan, setExecutionPlan] = useState(streamingEnhancer.getExecutionPlan());
  const [securityScanId, setSecurityScanId] = useState<string | null>(null);
  // One-shot: set only when a chat query asked about a specific account, cleared as soon as
  // SecurityTab applies it. Opening the tab manually therefore never carries a filter.
  const [securityAccountFilter, setSecurityAccountFilter] = useState<string | null>(null);
  const [securityOpenHistory, setSecurityOpenHistory] = useState(false);
  // ==================== CONVERSATION MANAGEMENT (Backend-Persisted) ====================
  const userHasChattedRef = useRef(false);

  // Restore from localStorage — this has the REAL sessionId (conv_uuid) that matches MongoDB
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const saved = localStorage.getItem('active_conversation');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.id && parsed.sessionId && parsed.sessionId.startsWith('conv_')) {
          return [{
            id: parsed.id,
            title: parsed.title || 'New Chat',
            messages: [],
            sessionId: parsed.sessionId,
            createdAt: parsed.createdAt || new Date().toISOString(),
            updatedAt: parsed.updatedAt || new Date().toISOString(),
            messagesLoaded: false
          }];
        }
      } catch {}
    }
    // No saved conversation — start empty, useEffect will create one
    return [];
  });
  const [activeConversationId, setActiveConversationId] = useState<string>(() => {
    const saved = localStorage.getItem('active_conversation');
    if (saved) { try { return JSON.parse(saved).id || ''; } catch {} }
    return '';
  });
  const [conversationsLoaded, setConversationsLoaded] = useState(false);

  // Get active conversation and its sessionId
  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const messages = activeConversation?.messages || [];
  const activeSessionId = activeConversation?.sessionId || '';

  // Persist active conversation info to localStorage whenever it changes
  useEffect(() => {
    if (activeConversation && activeConversation.id !== 'default') {
      localStorage.setItem('active_conversation', JSON.stringify({
        id: activeConversation.id,
        sessionId: activeConversation.sessionId,
        title: activeConversation.title,
        createdAt: activeConversation.createdAt,
        updatedAt: activeConversation.updatedAt
      }));
    }
  }, [activeConversation?.id, activeConversation?.sessionId, activeConversation?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update messages function that works with active conversation
  const setMessages = (updater: React.SetStateAction<Message[]>) => {
    setConversations(prevConvs => prevConvs.map(conv => {
      if (conv.id === activeConversationId) {
        const newMessages = typeof updater === 'function' ? updater(conv.messages) : updater;
        const firstUserMsg = newMessages.find(m => m.type === 'user');
        const newTitle = conv.title === 'New Chat' && firstUserMsg
          ? firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '')
          : conv.title;

        // Auto-update title on backend if changed
        if (newTitle !== conv.title) {
          const token = localStorage.getItem('auth_token');
          if (token) {
            fetch(`${API_BASE_URL}/api/chat/conversations/${conv.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ title: newTitle })
            }).catch(() => {}); // Fire and forget
          }
        }

        return { ...conv, messages: newMessages, updatedAt: new Date().toISOString(), title: newTitle };
      }
      return conv;
    }));
  };

  // Create a new conversation via backend
  const createNewConversation = async (customTitle?: string) => {
    const token = localStorage.getItem('auth_token');
    try {
      if (token) {
        const resp = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ title: customTitle || 'New Chat' })
        });
        if (resp.ok) {
          const { conversation } = await resp.json();
          const newConv: Conversation = {
            id: conversation.conversationId,
            title: conversation.title,
            messages: [],
            createdAt: conversation.createdAt,
            updatedAt: conversation.updatedAt,
            sessionId: conversation.sessionId,
            messagesLoaded: true
          };
          setConversations(prev => [newConv, ...prev]);
          setActiveConversationId(newConv.id);
          setInputValue('');
          // Clear previous visualization data for new conversation
          setQueryDashboards([]);
          setActiveQueryDashboard(null);
          return;
        }
      }
    } catch (e) {
      console.error('[CONV] Failed to create conversation via API:', e);
    }
    // Fallback: create locally if API fails
    const fallbackId = Date.now().toString();
    const newConv: Conversation = {
      id: fallbackId,
      title: customTitle || 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sessionId: `conv_${fallbackId}`,
      messagesLoaded: true
    };
    setConversations(prev => [newConv, ...prev]);
    setActiveConversationId(newConv.id);
    setInputValue('');
  };

  const handleCreateNewConversation = () => {
    if (newConversationName.trim()) {
      createNewConversation(newConversationName.trim());
      setNewConversationName('');
      setShowNewConversationPrompt(false);
    } else {
      createNewConversation();
      setShowNewConversationPrompt(false);
    }
  };

  // Keep a ref to conversations so switchConversation always reads the latest state
  const conversationsRef = useRef(conversations);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  // Switch conversation — load messages from backend if not already loaded
  const switchConversation = async (conversationId: string) => {
    setActiveConversationId(conversationId);

    // Read from ref to get latest state (avoids stale closure)
    const conv = conversationsRef.current.find(c => c.id === conversationId);
    const convSessionId = conv?.sessionId;
    const alreadyLoaded = conv?.messagesLoaded;

    if (alreadyLoaded || !convSessionId) return;

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const resp = await fetch(`${API_BASE_URL}/api/chat/session/${convSessionId}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const { messages: dbMessages } = await resp.json();
        if (dbMessages && dbMessages.length > 0) {
          try {
            const { messages: restored, visualizations } = reconstructChatHistory(dbMessages);
            setConversations(prev => prev.map(c =>
              c.id === conversationId ? { ...c, messages: restored, messagesLoaded: true } : c
            ));
            if (visualizations.length > 0) {
              setQueryDashboards(visualizations);
              setActiveQueryDashboard(visualizations[visualizations.length - 1].id);
            }
          } catch (reconstructErr) {
            console.error('[CONV] switchConversation: reconstruct failed, showing raw user messages:', reconstructErr);
            const fallbackMessages = dbMessages
              .filter((m: any) => m.role === 'user')
              .map((m: any) => ({
                id: `hist_user_${m.id}`,
                type: 'user' as const,
                content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
                timestamp: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }));
            setConversations(prev => prev.map(c =>
              c.id === conversationId ? { ...c, messages: fallbackMessages, messagesLoaded: true } : c
            ));
          }
        } else {
          setConversations(prev => prev.map(c =>
            c.id === conversationId ? { ...c, messagesLoaded: true } : c
          ));
        }
      } else {
        // Mark as loaded even on HTTP error
        setConversations(prev => prev.map(c =>
          c.id === conversationId ? { ...c, messagesLoaded: true } : c
        ));
      }
    } catch (e) {
      console.error('[CONV] Failed to load messages:', e);
      // Mark as loaded even on network error to prevent stuck state
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, messagesLoaded: true } : c
      ));
    }
  };

  // Delete conversation via backend
  const deleteConversation = async (conversationId: string) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }

    if (conversations.length <= 1) {
      createNewConversation();
    } else {
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (conversationId === activeConversationId) {
        const remaining = conversations.filter(c => c.id !== conversationId);
        if (remaining.length > 0) {
          setActiveConversationId(remaining[0].id);
        }
      }
    }
  };

  const renameConversation = (conversationId: string, newTitle: string) => {
    setConversations(prev => prev.map(conv =>
      conv.id === conversationId ? { ...conv, title: newTitle, updatedAt: new Date().toISOString() } : conv
    ));
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title: newTitle })
      }).catch(() => {});
    }
  };

  // Load conversations from backend on mount — creates one if none exist
  useEffect(() => {
    const initSession = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        // Not authenticated — create a local fallback
        if (conversations.length === 0) {
          const fallbackConv: Conversation = {
            id: 'local', title: 'New Chat', messages: [], sessionId: `user_anonymous`,
            createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), messagesLoaded: true
          };
          setConversations([fallbackConv]);
          setActiveConversationId('local');
        }
        setConversationsLoaded(true);
        return;
      }

      try {
        // 1. Load all conversations
        const resp = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) throw new Error('Failed to load conversations');

        let { conversations: backendConvs } = await resp.json();

        // 2. If no conversations exist, create one
        if (!backendConvs || backendConvs.length === 0) {
          const createResp = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ title: 'New Chat' })
          });
          if (createResp.ok) {
            const { conversation } = await createResp.json();
            backendConvs = [conversation];
          }
        }

        if (!backendConvs || backendConvs.length === 0) throw new Error('No conversations');

        // 3. Build conversation list
        const convList: Conversation[] = backendConvs.map((c: any) => ({
          id: c.conversationId,
          title: c.title,
          messages: [],
          sessionId: c.sessionId,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          messagesLoaded: false,
          messageCount: c.messageCount || 0
        }));

        // 4. Determine active conversation (prefer saved, fall back to most recent)
        let activeConv = convList[0];
        const saved = localStorage.getItem('active_conversation');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const found = convList.find(c => c.id === parsed.id);
            if (found) activeConv = found;
          } catch {}
        }

        // 5. Don't replace if user is already chatting
        if (userHasChattedRef.current) {
          // Just add backend convs alongside current conversation
          setConversations(prev => {
            const currentIds = new Set(prev.map(c => c.id));
            const newConvs = convList.filter(c => !currentIds.has(c.id));
            return [...prev, ...newConvs];
          });
          setConversationsLoaded(true);
          return;
        }

        // 6. Set conversations and active
        setConversations(convList);
        setActiveConversationId(activeConv.id);

        // 7. Load messages for active conversation
        try {
          const histResp = await fetch(`${API_BASE_URL}/api/chat/session/${activeConv.sessionId}/history`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (histResp.ok) {
            const { messages: dbMessages } = await histResp.json();
            if (dbMessages && dbMessages.length > 0) {
              try {
                const { messages: restored, visualizations } = reconstructChatHistory(dbMessages);
                setConversations(prev => prev.map(c =>
                  c.id === activeConv.id ? { ...c, messages: restored, messagesLoaded: true } : c
                ));
                if (visualizations.length > 0) {
                  setQueryDashboards(visualizations);
                  setActiveQueryDashboard(visualizations[visualizations.length - 1].id);
                }
              } catch (reconstructErr) {
                console.error('[CONV] reconstructChatHistory failed, showing raw user messages:', reconstructErr);
                // Fallback: at minimum show user messages so chat isn't empty
                const fallbackMessages = dbMessages
                  .filter((m: any) => m.role === 'user')
                  .map((m: any) => ({
                    id: `hist_user_${m.id}`,
                    type: 'user' as const,
                    content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
                    timestamp: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  }));
                setConversations(prev => prev.map(c =>
                  c.id === activeConv.id ? { ...c, messages: fallbackMessages, messagesLoaded: true } : c
                ));
              }
            } else {
              setConversations(prev => prev.map(c =>
                c.id === activeConv.id ? { ...c, messagesLoaded: true } : c
              ));
            }
          } else {
            // Mark as loaded even on HTTP error to prevent stuck state
            console.error('[CONV] History fetch failed with status:', histResp.status);
            setConversations(prev => prev.map(c =>
              c.id === activeConv.id ? { ...c, messagesLoaded: true } : c
            ));
          }
        } catch (histErr) {
          console.error('[CONV] Failed to load messages:', histErr);
          // Mark as loaded even on network error to prevent stuck state
          setConversations(prev => prev.map(c =>
            c.id === activeConv.id ? { ...c, messagesLoaded: true } : c
          ));
        }
      } catch (e) {
        console.error('[CONV] Init failed:', e);
        // Fallback: create local conversation if none exist
        if (conversations.length === 0) {
          setConversations([{
            id: 'local', title: 'New Chat', messages: [], sessionId: `user_anonymous`,
            createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), messagesLoaded: true
          }]);
          setActiveConversationId('local');
        }
      }
      setConversationsLoaded(true);
    };

    initSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chat panel collapse state
  const [isChatCollapsed, setIsChatCollapsed] = useState<boolean>(false);
  const [showConversationList, setShowConversationList] = useState<boolean>(false);
  const [showNewConversationPrompt, setShowNewConversationPrompt] = useState<boolean>(false);
  const [newConversationName, setNewConversationName] = useState<string>('');

  // Initialize activeTab from URL hash or default to 'overview'
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '');
    const validTabs = ['overview', 'infrastructure', 'deployments', 'monitoring', 'security', 'resources', 'costs', 'gpus', 'support', 'byoip', 'recommendations'];
    return validTabs.includes(hash) ? hash : 'overview';
  };

  const [activeTab, setActiveTabState] = useState(getInitialTab());

  // Custom setActiveTab function that updates both state and URL hash
  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    window.location.hash = tab;

    // Fetch cost data when switching to costs or recommendations tab
    if (tab === 'costs' || tab === 'recommendations') {
      fetchCostData();
    }
  };

  // Listen for hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const validTabs = ['overview', 'infrastructure', 'deployments', 'monitoring', 'security', 'resources', 'costs', 'gpus', 'support', 'byoip', 'recommendations'];
      if (validTabs.includes(hash)) {
        setActiveTabState(hash);

        // Fetch cost data if navigating to costs or recommendations tab
        if (hash === 'costs' || hash === 'recommendations') {
          fetchCostData();
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // On page refresh, setActiveTab is never called so fetchCostData never fires.
  // Detect the initial tab from the hash and fetch cost data if needed.
  useEffect(() => {
    const initialTab = window.location.hash.replace('#', '');
    if (initialTab === 'recommendations' || initialTab === 'costs') {
      fetchCostData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Authentication initialization - Check on app startup
  useEffect(() => {
    const validateTokenAndInit = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          // Validate token with server by making a simple API call
          const response = await fetch(`${API_BASE_URL}/api/accounts`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            // Token is valid
            setAuthToken(token);
            setIsAuthenticated(true);
            setShowLogin(false);

            // Extract user profile from token (supplement with localStorage for fields not in JWT)
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const storedFullName = localStorage.getItem('user_full_name');
              const storedOrganisation = localStorage.getItem('user_organisation');
              await Promise.all([fetchUserProfile(token), fetchBillingData(token)]);
            } catch (e) {
              console.warn('Could not decode token for user profile');
            }

            // Load user's accounts and data after authentication
            loadAccounts();
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('auth_token');
            setIsAuthenticated(false);
            setShowLogin(false);
          }
        } catch (error) {
          // Network error or invalid token, clear it
          localStorage.removeItem('auth_token');
          setIsAuthenticated(false);
          setShowLogin(false);
        }
      } else {
        // No token found - user needs to login manually
        console.log('No token found, user must login manually');
        setIsAuthenticated(false);
        setShowLogin(false);
      }

      // Done with auth initialization
      setIsAuthLoading(false);
    };

    validateTokenAndInit();

    // Add initial log entry to test logging system
    addLog('info', 'DevOpsX Application initialized', 'frontend', 'App');

    // Connect to backend log stream with retry limits
    let retryCount = 0;
    const MAX_RETRIES = 5;
    let retryTimeout: NodeJS.Timeout | null = null;

    const connectToLogStream = () => {
      if (retryCount >= MAX_RETRIES) {
        addLog('error', `Failed to connect to log stream after ${MAX_RETRIES} attempts. Log streaming disabled.`, 'frontend', 'logStream');
        return;
      }

      const eventSource = new EventSource(`${API_BASE_URL}/api/logs/stream`);
      logStreamEventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        addLog('info', 'Connected to backend log stream', 'frontend', 'logStream');
        setLogsConnected(true);
        retryCount = 0; // Reset retry count on successful connection
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'log') {
            // Add backend/MCP logs to the frontend log display
            setLogs(prevLogs => {
              const newLogs = [data, ...prevLogs];
              return newLogs.slice(0, 1000); // Keep max 1000 logs
            });
          }
        } catch (error) {
          // Silently handle parse errors to avoid log spam
          console.error('Log parse error:', error);
        }
      };

      eventSource.onerror = () => {
        eventSource.close(); // Close the failed connection
        setLogsConnected(false);

        retryCount++;
        if (retryCount < MAX_RETRIES) {
          const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 30000); // Exponential backoff, max 30s
          console.log(`Log stream disconnected, retry ${retryCount}/${MAX_RETRIES} in ${backoffDelay}ms`);

          retryTimeout = setTimeout(() => {
            connectToLogStream();
          }, backoffDelay);
          logStreamRetryTimeoutRef.current = retryTimeout;
        } else {
          addLog('warn', 'Log stream connection failed. Continuing without live logs.', 'frontend', 'logStream');
        }
      };
    };

    // DISABLED: Log stream temporarily disabled to prevent browser hang
    // Only connect to log stream if explicitly enabled
    if (localStorage.getItem('enable_log_stream') === 'true') {
      connectToLogStream();
    } else {
      addLog('info', 'Log stream disabled. Set localStorage.enable_log_stream=true to enable.', 'frontend', 'logStream');
    }

    return () => {
      if (logStreamEventSourceRef.current) {
        logStreamEventSourceRef.current.close();
        logStreamEventSourceRef.current = null;
      }
      if (logStreamRetryTimeoutRef.current) {
        clearTimeout(logStreamRetryTimeoutRef.current);
        logStreamRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // Listen for navigation state changes
  useEffect(() => {
    const checkNavState = () => {
      setClusterView(navigationState.getCurrentView());
    };

    const interval = setInterval(checkNavState, 100);
    return () => clearInterval(interval);
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [latestCostData, setLatestCostData] = useState<any>(null);
  const [infrastructureData, setInfrastructureData] = useState<any>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [lastPolled, setLastPolled] = useState<Date | null>(null);

  const [deployments, setDeployments] = useState<any>(null);
  const [alerts, setAlerts] = useState<any>(null);
  const [autoExecute, setAutoExecute] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [multiAccount, setMultiAccount] = useState(false);
  // Smart routing is always enabled internally
  const smartAccountRouting = true;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileTabsOpen, setIsMobileTabsOpen] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Recommendations Scan State
  const [awsScanStatus, setAwsScanStatus] = useState<'idle' | 'scanning' | 'complete' | 'error'>('idle');
  const [gcpScanStatus, setGcpScanStatus] = useState<'idle' | 'scanning' | 'complete' | 'error'>('idle');
  const [awsScanResults, setAwsScanResults] = useState<any>(null);
  const [gcpScanResults, setGcpScanResults] = useState<any>(null);
  const [scanProgress, setScanProgress] = useState<string>('');
  const [lastAwsScanId, setLastAwsScanId] = useState<string | null>(null);
  const [showScanDetailsModal, setShowScanDetailsModal] = useState(false);
  const [scanDetailsData, setScanDetailsData] = useState<any>(null);
  const [loadingScanDetails, setLoadingScanDetails] = useState(false);
  const [recommendationType, setRecommendationType] = useState<'recommendation1' | 'recommendation2'>('recommendation2');
  const [v1ScanTrigger, setV1ScanTrigger] = useState<number>(0);
  const [v2ScanTrigger, setV2ScanTrigger] = useState<number>(0);
  const [recommendationsSubTab, setRecommendationsSubTab] = useState<'savingsTracker' | 'recommendation1' | 'recommendation2' | 'dataTransfer' | 'storage' | 'gpu' | 'preApprovedDiscounts'>('recommendation2');
  const [approvedRecommendations, setApprovedRecommendations] = useState<Set<string>>(new Set());
  const [rejectedRecommendations, setRejectedRecommendations] = useState<Set<string>>(new Set());
  const [ignoredRecommendations, setIgnoredRecommendations] = useState<Set<string>>(new Set());
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<string | null>(null);

  // Kubernetes Clusters State
  const [kubernetesClusters, setKubernetesClusters] = useState<any[]>([]);
  const [loadingClusters, setLoadingClusters] = useState<boolean>(false);
  const [selectedCluster, setSelectedCluster] = useState<any | null>(null);
  const [showClusterDetailsModal, setShowClusterDetailsModal] = useState<boolean>(false);
  const [syncingCluster, setSyncingCluster] = useState<string | null>(null);
  const [clusterDetailsTab, setClusterDetailsTab] = useState<'cluster' | 'deployments' | 'monitoring'>('cluster');
  const [clusterResources, setClusterResources] = useState<any>(null);
  const [clusterDeployments, setClusterDeployments] = useState<any>(null);
  const [clusterMonitoring, setClusterMonitoring] = useState<any>(null);
  const [clusterResourcesTimestamp, setClusterResourcesTimestamp] = useState<Date | null>(null);
  const [clusterDeploymentsTimestamp, setClusterDeploymentsTimestamp] = useState<Date | null>(null);
  const [clusterMonitoringTimestamp, setClusterMonitoringTimestamp] = useState<Date | null>(null);
  const [loadingClusterDetails, setLoadingClusterDetails] = useState<boolean>(false);
  const [selectedNamespace, setSelectedNamespace] = useState<string>('all');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('nodes');

  // EKS Upgrade Wizard State
  const [showUpgradeWizard, setShowUpgradeWizard] = useState<boolean>(false);
  const [upgradeAnalysis, setUpgradeAnalysis] = useState<any>(null);
  const [loadingUpgradeAnalysis, setLoadingUpgradeAnalysis] = useState<boolean>(false);

  // Page-based Navigation State
  const [clusterView, setClusterView] = useState<ClusterView>(
    navigationState.getCurrentView()
  );
  const [upgradingCluster, setUpgradingCluster] = useState<boolean>(false);
  const [showUpgradeProgress, setShowUpgradeProgress] = useState<boolean>(false);
  const [upgradeTargetVersion, setUpgradeTargetVersion] = useState<string>('');

  // Save with DevOpsX - Discount toggles and expanded state
  const [expandedDiscountId, setExpandedDiscountId] = useState<string | null>(null);
  const [enabledDiscounts, setEnabledDiscounts] = useState<Set<string>>(new Set());

  // Real data for savings calculations (fetched from API)
  const [totalCurrentSpend, setTotalCurrentSpend] = useState<number>(0);
  const [threeMonthAverage, setThreeMonthAverage] = useState<number>(0);
  const [totalSavingsPotential, setTotalSavingsPotential] = useState<number>(0);
  const [resourcesAnalyzed, setResourcesAnalyzed] = useState<number>(0);
  const [savingsAchieved, setSavingsAchieved] = useState<number>(0);
  const [executingMessageId, setExecutingMessageId] = useState<string | null>(null);

  // Dynamic Visualization State - Each query gets its own dashboard
  const [queryDashboards, setQueryDashboards] = useState<VisualizationData[]>([]);
  const [activeQueryDashboard, setActiveQueryDashboard] = useState<string | null>(null);

  // Delete a visualization message from the database so it doesn't reappear on refresh
  const deleteVisualizationFromDB = async (vizId: string) => {
    // Viz IDs from reconstructChatHistory are formatted as "hist_viz_{mongoId}"
    const mongoId = vizId.startsWith('hist_viz_') ? vizId.replace('hist_viz_', '') : null;
    if (!mongoId) return; // Real-time viz (not yet reloaded) — no DB doc to delete
    const token = localStorage.getItem('auth_token');
    const sessionId = conversations.find(c => c.id === activeConversationId)?.sessionId;
    if (!token || !sessionId) return;
    try {
      await fetch(`${API_BASE_URL}/api/chat/session/${sessionId}/message/${mongoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to delete visualization from DB:', err);
    }
  };

  // User-based persistent session management for complete conversation context
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => {
    // Try to use user-based session if authenticated
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.id;
        const userSession = `user_${userId}`;
        localStorage.setItem('conversation_session_id', userSession);
        console.log('Using user-based persistent session:', userSession);
        return userSession;
      } catch (e) {
        console.log('Could not extract user ID from token, using fallback');
      }
    }

    // Fallback to stored session or create temporary one
    const stored = localStorage.getItem('conversation_session_id');
    if (stored && stored.startsWith('user_')) {
      console.log('Loaded existing user session from localStorage:', stored);
      return stored;
    }

    const tempSession = `temp_${Date.now()}`;
    localStorage.setItem('conversation_session_id', tempSession);
    console.log('Created temporary session (will upgrade when authenticated):', tempSession);
    return tempSession;
  });

  // Chat history loading is now handled by the conversation loading useEffect above

  const [isAccountsModalOpen, setIsAccountsModalOpen] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);

  // ✅ useMemo: connectedAccounts pe depend karta hai
  // Jab bhi loadAccounts() → setConnectedAccounts() update hoga → accounts auto re-compute hoga
  // Bina page refresh ke header badges dynamically update ho jayenge
  const accounts = useMemo<AccountStatus>(() => {
    const aws     = connectedAccounts.filter(a => a.type === 'aws').length;
    const gcp     = connectedAccounts.filter(a => a.type === 'gcp').length;
    const github  = connectedAccounts.filter(a => a.type === 'github').length;
    const jenkins = connectedAccounts.filter(a => a.type === 'jenkins').length;
    const total   = connectedAccounts.filter(a => a.status === 'active').length;
    return { aws, gcp, github, jenkins, total };
  }, [connectedAccounts]);

  const [selectedAccountType, setSelectedAccountType] = useState<'aws' | 'gcp' | 'github' | 'jenkins' | 'eks' | null>(null);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scanPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scanPollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logStreamEventSourceRef = useRef<EventSource | null>(null);
  const logStreamRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // API functions for account management
  const saveAccount = async (accountData: any) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(`${API_BASE_URL}/api/accounts`, accountData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.account) {
        // Update the connectedAccounts state
        setConnectedAccounts(prev => [...prev, {
          id: response.data.account.id,
          name: response.data.account.name,
          type: response.data.account.type,
          status: response.data.account.status,
          connectionMethod: response.data.account.connectionMethod,
          accountId: response.data.account.accountId,
          region: response.data.account.region,
          lastSync: 'Just now',
          config: response.data.account.config || {}
        }]);

        // Close modals and reset forms
        setShowAddAccountModal(false);
        setSelectedAccountType(null);

        return { success: true, message: 'Account connected successfully' };
      }
    } catch (error: any) {
      console.error('Error saving account:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to connect account'
      };
    }
  };

  const loadAccounts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('No auth token available for loading accounts');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.accounts) {
        const formattedAccounts = response.data.accounts.map((account: any) => ({
          id: account.id,
          name: account.name,
          type: account.type,
          status: account.status,
          connectionMethod: account.connectionMethod,
          accountId: account.accountId,
          region: account.region,
          lastSync: new Date(account.lastSync).toLocaleString(),
          config: account.config || {}
        }));
        setConnectedAccounts(formattedAccounts);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const deleteAccount = async (accountId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return { success: false, message: 'No authentication token' };

      // Find the account to get details for confirmation
      const accountToDelete = connectedAccounts.find(acc => acc.id === accountId);
      const accountName = accountToDelete?.name || 'Unknown Account';

      // Confirm deletion
      const confirmed = window.confirm(
        `⚠️ Delete AWS Account?\n\n` +
        `Account: ${accountName}\n` +
        `Type: ${accountToDelete?.type?.toUpperCase() || 'AWS'}\n` +
        `Region: ${accountToDelete?.region || 'Unknown'}\n\n` +
        `This will remove the account from DevOpsX. You can re-add it later if needed.\n\n` +
        `Continue with deletion?`
      );

      if (!confirmed) {
        return { success: false, message: 'Deletion cancelled by user' };
      }

      await axios.delete(`${API_BASE_URL}/api/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setConnectedAccounts(prev => prev.filter(acc => acc.id !== accountId));

      // Show success confirmation
      alert(`✅ Account Deleted Successfully!\n\n${accountName} has been removed from DevOpsX.`);

      return { success: true };
    } catch (error: any) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete account';
      alert(`❌ Deletion Failed\n\n${errorMessage}`);
      return { success: false, message: errorMessage };
    }
  };

  // Fetch real cost data from API
  const fetchCostData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const baseUrl = API_BASE_URL;

      // Fetch cost summary and recommendations in parallel
      const [summaryRes, recommendationsRes] = await Promise.allSettled([
        fetch(`${baseUrl}/api/cost/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/api/cost/recommendations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Process cost summary
      if (summaryRes.status === 'fulfilled' && summaryRes.value.ok) {
        const summaryData = await summaryRes.value.json();
        if (summaryData.success && summaryData.data) {
          const totalSpend = summaryData.data.totalSpend || 0;
          setTotalCurrentSpend(totalSpend);
        }
      }

      // Fetch 3-month cost analysis for average
      try {
        const analysisRes = await fetch(`${baseUrl}/api/cost/analysis/three-months`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (analysisRes.ok) {
          const analysisData = await analysisRes.json();
          if (analysisData.success && analysisData.data?.summary?.threeMonthAverage) {
            setThreeMonthAverage(analysisData.data.summary.threeMonthAverage);
          }
        }
      } catch (err) {
        console.error('Error fetching 3-month analysis:', err);
      }

      // Process recommendations for savings potential
      if (recommendationsRes.status === 'fulfilled' && recommendationsRes.value.ok) {
        const recData = await recommendationsRes.value.json();
        if (recData.success) {
          const recommendations = recData.data || [];
          const summary = recData.summary;

          // Calculate total savings potential
          // COMMENTED OUT: Now using savings tracking API as single source of truth (line 2167)
          // if (summary?.totalPotentialSavings?.monthly) {
          //   setTotalSavingsPotential(summary.totalPotentialSavings.monthly);
          // }

          // Resources analyzed now set by onBreakdownLoad from breakdown data
          // setResourcesAnalyzed(summary?.total || 0); ← removed to prevent overwrite
        }
      }

      // Fetch real savings achieved AND potential from savings tracking API
      try {
        const savingsResponse = await fetch(`${baseUrl}/api/savings/dashboard`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });

        if (savingsResponse.ok) {
          const savingsResult = await savingsResponse.json();
          if (savingsResult.success && savingsResult.initialized && savingsResult.data) {
            // Use actual achieved savings from the tracking system
            setSavingsAchieved(savingsResult.data.achievedSavings || 0);
            // Total savings now calculated from breakdown (onBreakdownLoad) not Firebase
            // setTotalSavingsPotential(savingsResult.data.potentialSavings || 0); ← removed
          } else {
            // Not initialized yet, set to 0
            setSavingsAchieved(0);
          }
        }
      } catch (savingsError) {
        console.error('Error fetching savings data:', savingsError);
        // Fallback: don't update if API fails
      }
    } catch (error) {
      console.error('Error fetching cost data:', error);
    }
  };

  // User Profile Management
  const [userProfile, setUserProfile] = useState<any>(null);

  const [billingData, setBillingData] = useState<any>({
    subscription: null, plan: null, wallet: null, usage: null,
    paymentMethods: [], invoices: [],
  });

  const fetchBillingData = async (token: string) => {
    try {
      const [summaryRes, pmRes, invRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/billing/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/billing/payment-methods`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/billing/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [summary, pm, inv] = await Promise.all([summaryRes.json(), pmRes.json(), invRes.json()]);
      setBillingData({
        subscription:        summary?.subscription          || null,
        plan:                summary?.plan                  || null,
        wallet:              summary?.wallet                || null,
        usage:               summary?.usage                 || null,
        creditBalanceCents:  summary?.credit_balance_cents  ?? 0,
        creditCurrency:      summary?.credit_currency       || 'USD',
        paymentMethods:      pm?.paymentMethods             || [],
        invoices:            inv?.invoices                  || [],
      });
    } catch (e) {
      console.warn('Could not fetch billing data:', e);
    }
  };

  // Convenience for profile dialogs to call after a purchase
  const refreshBilling = () => {
    const token = localStorage.getItem('auth_token');
    if (token) fetchBillingData(token);
  };

  // ── Post-payment confirmation banner state ─────────────────────────────
  // Driven by the URL params Stripe writes when redirecting back after
  // checkout. Shows a top-of-page toast that polls billing/summary until
  // the webhook lands, then flips from "activating" → "success".
  const [paymentToast, setPaymentToast] = useState<null | {
    kind: 'subscription' | 'addon' | 'ip_marketplace' | 'gpu_purchase' | 'cancelled' | 'failed';
    state: 'activating' | 'success' | 'cancelled' | 'failed';
    title: string;
    detail?: string;
  }>(null);

  // ── Stripe Checkout post-redirect handling ────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    const sessionId = params.get('session_id');
    if (!billing) return;

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('billing');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams}` : ''));
    };

    if (billing === 'cancelled') {
      setPaymentToast({
        kind: 'cancelled', state: 'cancelled',
        title: 'Checkout cancelled',
        detail: 'No charges were made.',
      });
      cleanUrl();
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) { cleanUrl(); return; }

    (async () => {
      try {
        // ── Add-on flow: confirm immediately via signature-verified endpoint
        if (billing === 'addon_success' && sessionId) {
          setPaymentToast({
            kind: 'addon', state: 'activating',
            title: 'Crediting your account…',
            detail: 'This usually takes a couple of seconds.',
          });
          const res = await fetch(`${API_BASE_URL}/api/billing/addons/confirm`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          await fetchBillingData(token);
          if (data?.bucket?.tokens_total) {
            setPaymentToast({
              kind: 'addon', state: 'success',
              title: 'Credits added to your wallet',
              detail: `+${data.bucket.tokens_total.toLocaleString()} credits are now available.`,
            });
          } else {
            setPaymentToast({
              kind: 'addon', state: 'success',
              title: 'Payment successful',
              detail: 'Your credits will appear in a moment.',
            });
          }
          cleanUrl();
          return;
        }

        // ── IP Marketplace flow: confirm immediately via signature-verified endpoint
        if (billing === 'ip_purchase_success' && sessionId) {
          setPaymentToast({
            kind: 'ip_marketplace', state: 'activating',
            title: 'Confirming your IP lease…',
            detail: 'This usually takes a couple of seconds.',
          });
          const res = await fetch(`${API_BASE_URL}/api/byoip/order/confirm`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          await fetchBillingData(token);
          if (data?.cidrs?.length) {
            setPaymentToast({
              kind: 'ip_marketplace', state: 'success',
              title: 'IP block leased',
              detail: `${data.cidrs.map((c: any) => c.cidr).join(', ')} is now listed under BYOIP.`,
            });
          } else {
            setPaymentToast({
              kind: 'ip_marketplace', state: 'success',
              title: 'Payment successful',
              detail: 'Your leased IP block will appear under BYOIP in a moment.',
            });
          }
          cleanUrl();
          return;
        }

        // ── IP Marketplace renewal: manual one-time re-charge, no subscription
        if (billing === 'ip_renewal_success' && sessionId) {
          setPaymentToast({
            kind: 'ip_marketplace', state: 'activating',
            title: 'Confirming your lease renewal…',
            detail: 'This usually takes a couple of seconds.',
          });
          const res = await fetch(`${API_BASE_URL}/api/byoip/cidrs/renew/confirm`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          await fetchBillingData(token);
          if (data?.cidr?.leaseExpiresAt) {
            setPaymentToast({
              kind: 'ip_marketplace', state: 'success',
              title: 'Lease renewed',
              detail: `${data.cidr.cidr} is now renewed until ${new Date(data.cidr.leaseExpiresAt).toLocaleDateString()}.`,
            });
          } else {
            setPaymentToast({
              kind: 'ip_marketplace', state: 'success',
              title: 'Payment successful',
              detail: 'Your lease renewal will appear under BYOIP in a moment.',
            });
          }
          cleanUrl();
          return;
        }

        // ── GPU marketplace: confirm payment, then the server is provisioned
        if (billing === 'gpu_purchase_success' && sessionId) {
          setPaymentToast({
            kind: 'gpu_purchase', state: 'activating',
            title: 'Confirming payment…',
            detail: 'This usually takes a couple of seconds.',
          });
          const res = await fetch(`${API_BASE_URL}/api/gpu/checkout/confirm`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          await fetchBillingData(token);
          if (data?.instance?.name) {
            setPaymentToast({
              kind: 'gpu_purchase', state: 'success',
              title: 'GPU instance provisioning',
              detail: `"${data.instance.name}" is now being provisioned — check the GPUs tab.`,
            });
          } else {
            setPaymentToast({
              kind: 'gpu_purchase', state: 'success',
              title: 'Payment successful',
              detail: 'Your GPU instance will appear in a moment.',
            });
          }
          cleanUrl();
          return;
        }

        // ── Subscription flow: pull Stripe directly, then poll as a safety net
        if (billing === 'success') {
          setPaymentToast({
            kind: 'subscription', state: 'activating',
            title: 'Activating your plan…',
            detail: 'Confirming with Stripe — this usually takes 2-8 seconds.',
          });

          // Synchronous activation path: hit /subscribe/confirm with the
          // session id. The backend pulls the latest invoice from Stripe and
          // idempotently writes the subscription doc, token bucket, invoice
          // and history rows — same code path the webhook uses. So even if
          // the webhook never lands (Stripe CLI off, network glitch), the
          // user's account is fully provisioned on this call.
          if (sessionId) {
            try {
              await fetch(`${API_BASE_URL}/api/billing/subscribe/confirm`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId }),
              });
            } catch (err) {
              console.warn('[billing] subscribe/confirm failed (will fall back to polling):', err);
            }
          }

          // Poll for up to 20s. Each iteration refreshes the parent state
          // (subscription + wallet + invoices + payment_methods) and only
          // declares success once ALL these are present:
          //   - subscription is active/trialing
          //   - wallet shows the subscription bucket
          //   - at least one payment method is saved
          //   - at least one invoice exists
          // If only some land within the timeout, we still flip to success
          // (the missing pieces will appear when the user reopens the tab).
          let allLanded = false;
          let lastSummary: any = null;
          for (let i = 0; i < 14; i++) {
            // Refetch the parent-managed billing state
            await fetchBillingData(token);

            const [summary, pmRes, invRes] = await Promise.all([
              fetch(`${API_BASE_URL}/api/billing/summary`,         { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
              fetch(`${API_BASE_URL}/api/billing/payment-methods`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
              fetch(`${API_BASE_URL}/api/billing/invoices`,        { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
            ]);
            lastSummary = summary;
            const sub = summary?.subscription;
            const wallet = summary?.wallet;
            const subActive = !!sub && (sub.status === 'active' || sub.status === 'trialing');
            const tokensCredited = (wallet?.by_source?.subscription || 0) > 0;
            const hasPM = (pmRes?.paymentMethods?.length || 0) > 0;
            const hasInvoice = (invRes?.invoices?.length || 0) > 0;

            if (subActive && tokensCredited && hasPM && hasInvoice) {
              allLanded = true;
              break;
            }
            await new Promise(r => setTimeout(r, 1500));
          }

          if (allLanded && lastSummary) {
            setPaymentToast({
              kind: 'subscription', state: 'success',
              title: `${lastSummary.plan?.name || 'Plan'} activated`,
              detail: `${(lastSummary.wallet?.by_source?.subscription || 0).toLocaleString()} credits added.`,
            });
          } else if (lastSummary?.subscription) {
            setPaymentToast({
              kind: 'subscription', state: 'success',
              title: `${lastSummary.plan?.name || 'Plan'} activated`,
              detail: 'Some details may take a moment to appear in Billing.',
            });
          } else {
            setPaymentToast({
              kind: 'subscription', state: 'success',
              title: 'Payment received',
              detail: 'Your plan will activate shortly. Open the Billing tab to refresh.',
            });
          }
          cleanUrl();
        }
      } catch (err) {
        console.warn('post-checkout refresh failed:', err);
        setPaymentToast({
          kind: 'failed', state: 'failed',
          title: 'Could not confirm payment',
          detail: 'Please refresh the page or contact support if the issue persists.',
        });
        cleanUrl();
      }
    })();
  }, []);

  // Auto-dismiss success/cancel toasts after 6s
  useEffect(() => {
    if (!paymentToast) return;
    if (paymentToast.state === 'activating') return;   // keep visible while polling
    const t = setTimeout(() => setPaymentToast(null), 6000);
    return () => clearTimeout(t);
  }, [paymentToast]);

  const fetchUserProfile = async (token: string, base: Partial<any> = {}) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { user } = await res.json();
        setUserProfile({
          id: user.id,
          email: user.email,
          name: user.full_name || '',
          organisation: user.organisation || '',
          number: user.number || '',
          status: user.status || 'active',
          tool_access: user.tool_access ?? false,
          ticket_access: user.ticket_access ?? false,
          quota_per_period: user.quota_per_period ?? 10,
          quota_renewed_at: user.quota_renewed_at || null,
          subscription_id: user.subscription_id || null,
          last_login: user.last_login || null,
          createdAt: user.createdAt || null,
          ...base,
        });
      }
    } catch (e) {
      console.warn('Could not fetch user profile:', e);
    }
  };

  // Enhanced logging function
  const addLog = (level: 'info' | 'warn' | 'error' | 'debug', message: string, source: 'frontend' | 'backend' | 'mcp' = 'frontend', component?: string, data?: any) => {
    const logEntry: LogEntry = {
      id: `log_${Date.now()}_${Math.random()}`,
      timestamp: new Date(),
      level,
      source,
      component,
      message,
      data
    };

    setLogs(prevLogs => {
      const newLogs = [logEntry, ...prevLogs];
      // Keep only latest 1000 logs to prevent memory issues
      return newLogs.slice(0, 1000);
    });

    // Also log to console for debugging
    console.log(`[${source}${component ? `::${component}` : ''}] ${message}`, data);
  };

  // Authentication functions - Global user session management
  const handleLogin = async () => {
    try {
      setAuthError('');
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginForm)
      });

      const data = await response.json();

      if (response.ok && data.token) {
        // Store authentication token
        localStorage.setItem('auth_token', data.token);
        // Persist full_name and organisation so they survive page refresh
        if (data.full_name) localStorage.setItem('user_full_name', data.full_name);
        if (data.organisation) localStorage.setItem('user_organisation', data.organisation);
        setAuthToken(data.token);
        setIsAuthenticated(true);
        setShowLogin(false);
        setLoginForm({ email: '', password: '' });

        // Build user profile – prefer API response body fields over JWT payload
        try {
          const payload = JSON.parse(atob(data.token.split('.')[1]));
          await Promise.all([fetchUserProfile(data.token), fetchBillingData(data.token)]);

          // Upgrade session to user-based persistent session
          const userId = payload.id;
          const userSession = `user_${userId}`;
          const currentSession = localStorage.getItem('conversation_session_id');

          if (currentSession !== userSession) {
            console.log(`Upgrading session from ${currentSession} to ${userSession}`);
            localStorage.setItem('conversation_session_id', userSession);
            setCurrentSessionId(userSession);
          }
        } catch (e) {
          console.warn('Could not decode token for user profile');
        }

        // Load all user data after successful authentication
        await loadUserData();
      } else {
        setAuthError(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Network error. Please check backend connection.');
    }
  };

  const handleSignup = async () => {
    try {
      setAuthError('');

      // ── Client-side validation (before hitting the API) ──
      if (!signupForm.full_name.trim()) {
        setAuthError('Full name is required.');
        return;
      }
      if (!signupForm.organisation.trim()) {
        setAuthError('Organisation name is required.');
        return;
      }
      if (!signupForm.email.trim()) {
        setAuthError('Email is required.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupForm.email.trim())) {
        setAuthError('Please enter a valid email address.');
        return;
      }
      if (signupForm.password.length < 6) {
        setAuthError('Password must be at least 6 characters.');
        return;
      }

      console.log('📤 [SIGNUP] Sending signup request...', {
        email: signupForm.email,
        full_name: signupForm.full_name,
        organisation: signupForm.organisation,
      });

      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(signupForm)
      });

      const data = await response.json();

      console.log('📨 [SIGNUP] Response received:', { status: response.status, data });

      if (response.ok) {
        console.log(`✅ [SIGNUP] Signup successful! userId: ${data.userId}`);

        // Signup successful — reset form state, then auto-login
        setSignupForm({ full_name: '', organisation: '', email: '', password: '', number: '' });
        setShowSignup(false);

        // Set login form with signup email/password and trigger login
        setLoginForm({ email: signupForm.email, password: signupForm.password });
        await handleLogin();

        alert('Account created successfully! You are now logged in.');
      } else {
        console.warn('⚠️ [SIGNUP] Signup failed:', data.message || data);
        setAuthError(data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('❌ [SIGNUP] Network/runtime error:', error);
      setAuthError('Network error. Please check backend connection.');
    }
  };

  // Load all user-related data (accounts, profiles, etc.)
  const loadUserData = async () => {
    try {
      await loadAccounts(); // Load user's connected accounts
      // Add more user data loading here as needed
      // await loadUserPreferences();
      // await loadUserProjects(); etc.
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleLogout = () => {
    // Clear all user data and session
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_full_name');
    localStorage.removeItem('user_organisation');
    localStorage.removeItem('active_conversation');
    localStorage.removeItem('conversation_session_id');
    setAuthToken(null);
    setIsAuthenticated(false);
    setShowLogin(true);
    setUserProfile(null);
    setConnectedAccounts([]);
    // Clear any other user-specific state
    setMessages([]);
    setLatestCostData(null);
    setInfrastructureData(null);
    // Clear visualization data so next user doesn't see previous user's data
    setQueryDashboards([]);
    setActiveQueryDashboard(null);

    // Clear conversation state
    setConversations([]);
    setActiveConversationId('');
  };

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
  }, []);

  const handleAccountSelection = async (accountId: string, originalQuery: string, messageId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      setIsLoading(true);

      let selectedAccountIds = null;
      if (accountId === 'all') {
        selectedAccountIds = connectedAccounts.map(acc => acc.id);
      } else {
        selectedAccountIds = [accountId];
      }

      // Update UI to show processing
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
            ...msg,
            content: '🔄 Processing with selected account(s)...',
            structured: undefined
          }
          : msg
      ));

      const response = await fetch(`${API_BASE_URL}/api/chat/confirm-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originalQuery,
          selectedAccountIds
        })
      });

      if (!response.ok) {
        throw new Error('Failed to confirm accounts');
      }

      const result = await response.json();

      if (result.type === 'success' && result.results) {
        // Format and display results
        const formattedContent = formatMultiAccountResults(result.results, result.accountSelection);

        setMessages(prev => prev.map(msg =>
          msg.id === messageId
            ? { ...msg, content: formattedContent }
            : msg
        ));
      } else {
        setMessages(prev => prev.map(msg =>
          msg.id === messageId
            ? { ...msg, content: `❌ ${result.message || 'Failed to process request'}` }
            : msg
        ));
      }

    } catch (error) {
      console.error('Account selection error:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, content: `❌ Failed to process account selection` }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const formatMultiAccountResults = (results: any[], accountSelection: any) => {
    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);

    let content = `📊 **${accountSelection.message}**\n\n`;

    if (successful.length > 0) {
      content += `✅ **Successful Results (${successful.length}):**\n`;
      successful.forEach(result => {
        content += `• **${result.accountName}**: ${JSON.stringify(result.result.result?.mockData || result.result, null, 2)}\n`;
      });
    }

    if (failed.length > 0) {
      content += `\n❌ **Failed Results (${failed.length}):**\n`;
      failed.forEach(result => {
        content += `• **${result.accountName}**: ${result.error}\n`;
      });
    }

    return content;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const cancelExecution = () => {
    if (abortController && executingMessageId) {
      abortController.abort();
      setIsLoading(false);
      setExecutingMessageId(null);
      setAbortController(null);

      // Update the message to show cancellation
      setMessages(prev => prev.map(msg => {
        if (msg.id === executingMessageId && msg.structured) {
          const updatedSections = msg.structured.sections?.map(section => {
            if (section.title === '📋 Execution Plan') {
              return {
                ...section,
                status: 'cancelled',
                items: section.items.map((item: any) => ({
                  ...item,
                  status: item.status === 'complete' ? 'complete' : 'cancelled'
                }))
              };
            }
            return section;
          });

          return {
            ...msg,
            content: '❌ Execution cancelled by user',
            structured: {
              ...msg.structured,
              sections: updatedSections,
              actions: [] // Remove action buttons
            }
          };
        }
        return msg;
      }));
    }
  };

  const executeQuickAction = async (action: string, messageId: string, context?: any) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: action,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      type: 'assistant',
      content: '',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      tokens: 0,
      metadata: {}
    };

    setMessages(prev => [...prev, assistantMessage]);
    await executeNormalQuery(assistantMessageId, action);
  };

  // Helper function to check if JSON string is complete and valid
  const isCompleteJSON = (str: string): boolean => {
    if (!str.trim()) return false;
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Helper function to process individual SSE messages
  const processSSEMessage = (jsonStr: string, messageId: string, streamContentRef: { current: string }, setMessages: any, setIsLoading: any, setExecutingMessageId: any, setAbortController: any) => {
    try {
      if (!jsonStr.trim()) return;

      // Debug: log problematic JSON strings
      if (jsonStr.includes('"') && !jsonStr.startsWith('{')) {
        console.log('🐛 DEBUG: Potentially malformed JSON:', jsonStr.substring(0, 200));
      }

      const data = JSON.parse(jsonStr);

      // Debug: log which server is responding
      if (data.server_name || data.server_id) {
        console.log(`📡 Response from: ${data.server_name || data.server_id}`);
      }

      if (data.type === 'account_info') {
        // Show which accounts are being used
        setMessages((prev: any) => prev.map((msg: any) =>
          msg.id === messageId
            ? { ...msg, content: `🔗 ${data.content}` }
            : msg
        ));
      } else if ((data.type === 'delta' || data.type === 'stream') && data.content) {
        streamContentRef.current += data.content;
        setMessages((prev: any) => prev.map((msg: any) =>
          msg.id === messageId
            ? { ...msg, content: streamContentRef.current, structured: msg.structured } // Preserve structured data
            : msg
        ));
      } else if (data.type === 'done') {
        console.log('✅ Received completion signal from:', data.server_name || 'unknown server');
        setIsLoading(false);
        setExecutingMessageId(null);
        setAbortController(null);
        return;
      } else if (data.type === 'mcp_complete') {
        console.log('✅ Received MCP completion from:', data.server_name || 'unknown server');
        console.log('🚨 MCP completion - setting loading states to false');
        setIsLoading(false);
        setExecutingMessageId(null);
        setAbortController(null);
        return;
      }

    } catch (e) {
      console.error('Error parsing SSE message:', e);
      console.error('🔥 Failed JSON string:', jsonStr.substring(0, 500));
      // Continue processing instead of breaking the stream
    }
  };

  // Ref to hold the current sessionId — always up-to-date, no React state delay
  const activeSessionIdRef = useRef(activeSessionId);
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    userHasChattedRef.current = true;

    // If no proper conversation yet (no conv_ sessionId), create one BEFORE sending
    let sessionIdForRequest = activeSessionIdRef.current;
    if (!sessionIdForRequest || !sessionIdForRequest.startsWith('conv_')) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const resp = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ title: inputValue.trim().substring(0, 50) })
          });
          if (resp.ok) {
            const { conversation } = await resp.json();
            // Update state for UI
            setConversations(prev => prev.map(c =>
              c.id === activeConversationId
                ? { ...c, id: conversation.conversationId, sessionId: conversation.sessionId }
                : c
            ));
            setActiveConversationId(conversation.conversationId);
            // Use the sessionId DIRECTLY — don't wait for React state
            sessionIdForRequest = conversation.sessionId;
            activeSessionIdRef.current = conversation.sessionId;
          }
        } catch (e) {
          console.error('[CONV] Failed to create conversation on send:', e);
        }
      }
    }

    // Store in a ref so downstream functions can use it
    const sendSessionId = sessionIdForRequest;

    // Reset execution plan for new query
    streamingEnhancer.resetExecutionPlan();
    setExecutionPlan(streamingEnhancer.getExecutionPlan());

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);

    // STEP 1: Analyze intent and create task breakdown
    const taskBreakdown = analyzeIntentAndCreateTasks(currentInput);
    console.log('🔍 DEBUG: TaskBreakdown result:', JSON.stringify(taskBreakdown, null, 2));
    console.log('🔍 DEBUG: Current input was:', currentInput);

    const assistantMessageId = (Date.now() + 1).toString();

    if (taskBreakdown) {
      // Show task breakdown immediately with basic initial steps
      const assistantMessage: Message = {
        id: assistantMessageId,
        type: 'assistant',
        content: `${taskBreakdown.intent}`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        tokens: 0,
        metadata: {},
        structured: {
          summary: `${taskBreakdown.intent}`,
          sections: [
            {
              title: '📋 Execution Plan',
              status: 'in_progress',
              items: [
                { text: 'Analyzing query and creating execution plan', status: 'pending' },
                { text: 'Connecting to AWS services', status: 'pending' }
              ] // Basic initial steps, refined pipeline will add detailed todos
            }
          ],
          actions: [
            { label: 'Cancel Execution', type: 'emergency', icon: 'stop' }
          ]
        }
      };

      setMessages(prev => [...prev, assistantMessage]);

      // STEP 2: Start executing tasks progressively
      setExecutingMessageId(assistantMessageId);
      await executeTasksProgressively(assistantMessageId, taskBreakdown, currentInput);
    } else {
      // Fallback for unrecognized intents - original behavior
      const assistantMessage: Message = {
        id: assistantMessageId,
        type: 'assistant',
        content: '',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        tokens: 0,
        metadata: {}
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Execute with smart routing if enabled, otherwise use normal query
      if (smartAccountRouting) {
        await executeSmartQuery(assistantMessageId, currentInput);
      } else {
        await executeNormalQuery(assistantMessageId, currentInput);
      }
    }
  };

  const executeTasksProgressively = async (messageId: string, taskBreakdown: any, query: string) => {
    let currentTaskIndex = 0;

    // Function to update task status in UI
    const updateTaskStatus = (taskId: number, status: string) => {
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId && msg.structured) {
          const updatedSections = msg.structured.sections?.map(section => {
            if (section.title === '📋 Execution Plan') {
              return {
                ...section,
                items: section.items.map((item, idx) => ({
                  ...item,
                  status: idx + 1 === taskId ? status :
                    idx + 1 < taskId ? 'complete' : 'pending'
                }))
              };
            }
            return section;
          });

          return {
            ...msg,
            structured: {
              ...msg.structured,
              sections: updatedSections
            }
          };
        }
        return msg;
      }));
    };

    const requestBody = {
      message: query,
      options: { autoExecute, dryRun, multiAccount },
      accountId: selectedAccountId,
      sessionId: activeSessionIdRef.current  // Use ref for latest sessionId (avoids stale state)
    };
    console.log('📤 Sending request:', requestBody);
    console.log('📍 Session ID:', currentSessionId);
    console.log('🕐 Request timestamp:', new Date().toISOString());
    console.log('⚙️ Current options state:', { autoExecute, dryRun, multiAccount });

    try {
      const controller = new AbortController();
      setAbortController(controller);

      // Add authentication headers if token exists
      const token = localStorage.getItem('auth_token');
      const headers: any = {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('🔐 Adding auth header to progressive tasks');
      } else {
        console.log('⚠️ No auth token - using unauthenticated request');
      }

      console.log('🚀 Making request to router service:', {
        url: `${API_BASE_URL}/api/chat/routerx?t=${Date.now()}`,
        method: 'POST',
        headers: headers,
        body: requestBody
      });

      const response = await fetch(`${API_BASE_URL}/api/chat/routerx?t=${Date.now()}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Router service error response:', errorText);
        addLog('error', `HTTP error! status: ${response.status} - ${errorText}`, 'frontend', 'executeNormalQuery');
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      addLog('info', 'Successfully connected to streaming endpoint', 'frontend', 'executeNormalQuery');
      console.log('✅ Stream connection established');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      const streamContentRef = { current: '' };
      const visualizationReceivedRef = { current: false };
      const mcpErrorRef = { current: '' };
      let sseBuffer = ''; // Buffer to reassemble SSE messages split across TCP chunks

      if (reader) {
        console.log('🔄 Starting to read stream...');
        const startTime = Date.now();
        const timeoutMs = 360000; // 6 minutes timeout for testing Thinker streaming (300s wait + buffer)

        while (true) {
          // Check for timeout
          if (Date.now() - startTime > timeoutMs) {
            console.warn('⏰ Stream reading timed out after 2 minutes');
            if (streamContentRef.current.length > 0) {
              await showFinalResults(messageId, taskBreakdown, streamContentRef.current, query);
            }
            setIsLoading(false);
            break;
          }
          const { done, value } = await reader.read();
          if (done) {
            console.log('📡 Stream ended naturally');
            // Show final results when stream ends
            if (streamContentRef.current.length > 0) {
              await showFinalResults(messageId, taskBreakdown, streamContentRef.current, query);
            }
            setIsLoading(false);
            break;
          }

          const chunk = decoder.decode(value);
          console.log('📦 Raw chunk received:', chunk);

          // Do NOT pre-unescape \n sequences here. The backend sends actual newline bytes as
          // SSE delimiters, so we split on those directly. Pre-unescaping \n (backslash-n)
          // sequences in the raw chunk corrupts JSON payloads where the content string itself
          // contains \n (e.g. mcp_response with multi-line text like "No Instances found.\n\n").

          // Buffer chunks to handle large SSE events (e.g. cost_comparison ~12KB)
          // that get split across TCP boundaries
          sseBuffer += chunk;

          // Split by SSE message delimiter (\n\n) — last part may be incomplete
          const parts = sseBuffer.split(/\n\n/);
          sseBuffer = parts.pop() || ''; // Keep incomplete tail for next chunk
          const messages = parts;
          console.log('📝 SSE Messages parsed:', messages.length);

          for (const message of messages) {
            if (message.trim() === '') continue; // Skip empty messages

            console.log('🔍 Processing message:', message);
            console.log('🔍 Message length:', message.length);
            console.log('🔍 Message starts with "data: "?', message.startsWith('data: '));

            if (message.startsWith('data: ')) {
              try {
                const jsonStr = message.substring(6).trim();
                console.log('🔧 Parsing JSON (length=' + jsonStr.length + '):', jsonStr);
                console.log('🔧 First 50 chars:', jsonStr.substring(0, 50));

                if (!jsonStr || jsonStr === '') {
                  console.log('⚠️ Empty JSON string, skipping');
                  continue;
                }

                const data = JSON.parse(jsonStr);
                console.log('✅ Parsed data:', data);
                console.log('📊 Data type:', data.type, '| Has content:', !!data.content);

                // DEBUG: Check for clarification_required specifically
                if (data.type === 'clarification_required') {
                  console.log('🚨🚨🚨 CLARIFICATION_REQUIRED EVENT DETECTED! 🚨🚨🚨');
                  console.log('📋 Clarifications:', data.clarifications);
                  console.log('📝 Original Query:', data.originalQuery);
                  console.log('🔑 Session ID:', data.sessionId);
                }

                if (data.type === 'todoList' && data.todos) {
                  // Handle todo list from refined pipeline - add to execution plan
                  console.log('📋 Received todo list from refined pipeline:', data.todos);

                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId && msg.structured) {
                      const existingSection = msg.structured.sections?.find(s => s.title === '📋 Execution Plan');
                      if (existingSection) {
                        // Mark initial todos as complete and add refined pipeline todos
                        const newItems = data.todos.map((todo: any) => ({
                          text: todo.todo,
                          status: 'pending'
                        }));

                        return {
                          ...msg,
                          structured: {
                            ...msg.structured,
                            sections: msg.structured.sections?.map(section => {
                              if (section.title === '📋 Execution Plan') {
                                // Mark existing initial items as complete
                                const updatedInitialItems = section.items.map(item => ({
                                  ...item,
                                  status: 'complete'
                                }));

                                return {
                                  ...section,
                                  items: [...updatedInitialItems, ...newItems] // Append refined todos after initial ones
                                };
                              }
                              return section;
                            })
                          }
                        };
                      }
                    }
                    return msg;
                  }));
                } else if (data.type === 'todoUpdate') {
                  // Handle todo status update from refined pipeline
                  console.log(`📝 Updating todo ${data.todoNumber} to ${data.status}`);

                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId && msg.structured) {
                      return {
                        ...msg,
                        structured: {
                          ...msg.structured,
                          sections: msg.structured.sections?.map(section => {
                            if (section.title === '📋 Execution Plan') {
                              // Offset by 2 because of the initial todos (analyzing query, connecting to AWS)
                              const INITIAL_TODO_COUNT = 2;
                              const targetIndex = INITIAL_TODO_COUNT + data.todoNumber;

                              console.log(`🎯 Updating todo at index ${targetIndex} (refined index: ${data.todoNumber}) to status ${data.status}`);

                              return {
                                ...section,
                                items: section.items.map((item, idx) => {
                                  if (idx === targetIndex) {
                                    return {
                                      ...item,
                                      status: data.status === 'in_progress' ? 'in_progress' :
                                        data.status === 'completed' ? 'complete' :
                                          data.status === 'failed' ? 'failed' : 'pending'
                                    };
                                  }
                                  return item;
                                })
                              };
                            }
                            return section;
                          })
                        }
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'Greeting_message' && data.content) {
                  // Handle greeting response from backend
                  console.log('👋 Greeting message received:', data.content);
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      return {
                        ...msg,
                        content: data.content,
                        isGreeting: true,
                        structured: undefined
                      };
                    }
                    return msg;
                  }));
                  setIsLoading(false);
                } else if (data.type === 'mcp_response' && data.content) {
                  // Handle MCP streaming response - display content immediately
                  console.log('📡 MCP response chunk:', data.content);
                  console.log('📡 MCP response chunk type:', typeof data.content);

                  // Ensure content is a string to prevent [object Object]
                  let contentStr = typeof data.content === 'string'
                    ? data.content
                    : (typeof data.content === 'object' ? JSON.stringify(data.content) : String(data.content));

                  // If the content is a JSON object (e.g. EC2 server returns {"response":"..."}),
                  // extract the human-readable text so the terminal box shows meaningful output.
                  if (contentStr.trimStart().startsWith('{')) {
                    try {
                      const parsed = JSON.parse(contentStr);
                      if (typeof parsed.error === 'string') {
                        // Check if it's a known external service error
                        const bizErr = getBusinessError(parsed.error);
                        if (bizErr) {
                          setMessages(prev => prev.map(msg =>
                            msg.id === messageId ? { ...msg, content: bizErr.message, errorCode: bizErr.code, structured: undefined } : msg
                          ));
                          setIsLoading(false);
                          setExecutingMessageId(null);
                          break;
                        }
                        contentStr = `❌ MCP Server Error: ${parsed.error}`;
                        mcpErrorRef.current = parsed.error;
                      } else if (typeof parsed.response === 'string') {
                        contentStr = parsed.response;
                      } else if (typeof parsed.message === 'string') {
                        contentStr = parsed.message;
                      } else if (typeof parsed.data === 'string') {
                        contentStr = parsed.data;
                      }
                      // If none of the above keys exist, keep original contentStr
                    } catch {
                      // Not valid JSON — keep contentStr as-is
                    }
                  }

                  streamContentRef.current += contentStr;

                  // Update message content in real-time AND rawResponse for structured messages
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      // If message has structured data, update rawResponse for terminal display
                      if (msg.structured) {
                        return {
                          ...msg,
                          content: msg.content + contentStr,
                          structured: {
                            ...msg.structured,
                            rawResponse: (msg.structured.rawResponse || '') + contentStr
                          }
                        };
                      }
                      return {
                        ...msg,
                        content: msg.content + contentStr
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'mcp_complete') {
                  // MCP step completed, but DO NOT close stream - Thinker may still be processing
                  console.log('✅ MCP step completed for:', data.server_name || 'unknown server');
                  console.log('⏳ Continuing to listen for Thinker analysis...');
                  // Do NOT set isLoading to false or break the loop
                } else if (data.type === 'Thinker' && data.content) {
                  // Handle Thinker streaming response - display analysis immediately
                  console.log('🤔🤔🤔 THINKER EVENT RECEIVED IN FRONTEND!!! 🤔🤔🤔');

                  // Ensure content is a string to prevent [object Object]
                  const thinkerContentStr = typeof data.content === 'string'
                    ? data.content
                    : (typeof data.content === 'object' ? JSON.stringify(data.content) : String(data.content));

                  console.log('🤔 Thinker analysis chunk length:', thinkerContentStr.length);
                  console.log('🤔 Thinker content preview:', thinkerContentStr.substring(0, 200));
                  console.log('🤔 Current message ID:', messageId);
                  console.log('🤔 streamContentRef.current length before:', streamContentRef.current.length);

                  streamContentRef.current += thinkerContentStr;

                  console.log('🤔 streamContentRef.current length after:', streamContentRef.current.length);

                  // Update message content in real-time
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      console.log('🤔 UPDATING MESSAGE WITH THINKER CONTENT!');
                      return {
                        ...msg,
                        content: msg.content + thinkerContentStr
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'mcp_streaming') {
                  // MCP started streaming
                  console.log('📡 MCP streaming started:', data.content);
                } else if (data.type === 'tool_call') {
                  // Handle tool call events from EKS agent
                  console.log('🔧 Tool call:', data.data);
                  // These are just status updates, don't need to display them
                } else if (data.type === 'tool_result') {
                  // Handle tool result events from EKS agent
                  console.log('✅ Tool result:', data.data);
                  // These are just status updates, don't need to display them
                } else if (data.type === 'thinker_complete' && data.content) {
                  // Handle complete Thinker analysis - append to message content
                  console.log('🤔 Thinker analysis complete, appending full analysis');
                  streamContentRef.current += '\n\n---\n\n## 🤔 Detailed Analysis\n\n' + data.content;

                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      return {
                        ...msg,
                        content: msg.content + '\n\n---\n\n## 🤔 Detailed Analysis\n\n' + data.content
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'clarification_required') {
                  // Handle clarification request from backend - show as inline chat message
                  console.log('❓❓❓ ENTERING CLARIFICATION HANDLER ❓❓❓');
                  console.log('❓ Clarification required:', data.clarifications);
                  console.log('❓ Data object:', JSON.stringify(data, null, 2));

                  // Create an assistant message with clarification form
                  const clarificationMessage: Message = {
                    id: (Date.now() + 2).toString(),
                    type: 'assistant',
                    content: `I need some additional information to proceed with your request: "${data.originalQuery || ''}"`,
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    clarificationRequest: {
                      clarifications: data.clarifications || [],
                      originalQuery: data.originalQuery || '',
                      sessionId: data.sessionId || ''
                    }
                  };

                  setMessages(prev => [...prev, clarificationMessage]);
                  setIsLoading(false);
                  console.log('✅ Clarification message added to chat');

                } else if (data.type === 'security_tab_open' && data.scanId) {
                  // Open security tab and load the specific scan
                  console.log('🔒 Security tab open requested, scanId:', data.scanId, '| accountFilter:', data.accountId || 'none');
                  setSecurityScanId(data.scanId);
                  // Only when the query named one account — otherwise stays null and the tab
                  // shows every account exactly as before
                  setSecurityAccountFilter(data.accountId || null);
                  setActiveTab('security');

                } else if (data.type === 'security_scan_list' && data.scans) {
                  // Push a new message for scan cards — don't modify the structured execution-plan message
                  console.log('🔒 Security scan list received:', data.scans.length, 'of', data.totalScans, 'scans');
                  const scanListMsg: Message = {
                    id: (Date.now() + 2).toString(),
                    type: 'assistant',
                    content: '',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    securityScanList: data.scans,
                    securityScanLabel: data.label,
                    securityScanTotal: data.totalScans,
                    securityScanTrend: data.totalScans > 1
                      ? { from: data.trendFrom, to: data.trendTo }
                      : undefined,
                  };
                  setMessages(prev => [...prev, scanListMsg]);

                } else if (data.type === 'security_no_scans') {
                  // Push a new message for the "no scans" card
                  const noScansMsg: Message = {
                    id: (Date.now() + 2).toString(),
                    type: 'assistant',
                    content: '',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    securityNoScans: { label: data.label },
                  };
                  setMessages(prev => [...prev, noScansMsg]);

                } else if (data.type === 'phase' && data.content) {
                  // Note: Task status updates are now handled via todoUpdate events from refined pipeline
                  // This ensures accurate, real-time updates based on actual execution progress
                  console.log('📍 Phase update:', data.content);

                  // Mark all todos as complete when pipeline completes
                  if (data.phase === 'completed' || data.content.includes('completed')) {
                    console.log('✅ Pipeline completed - marking all todos as complete');
                    setMessages(prev => prev.map(msg => {
                      if (msg.id === messageId && msg.structured) {
                        return {
                          ...msg,
                          structured: {
                            ...msg.structured,
                            sections: msg.structured.sections?.map(section => {
                              if (section.title === '📋 Execution Plan') {
                                return {
                                  ...section,
                                  status: 'complete',
                                  items: section.items.map((item: any) => ({
                                    ...item,
                                    status: 'complete'
                                  }))
                                };
                              }
                              return section;
                            })
                          }
                        };
                      }
                      return msg;
                    }));
                  }
                } else if (data.type === 'visualization' && data.data) {
                  // Handle visualization data from backend
                  console.log('📊📊📊 VISUALIZATION SSE EVENT RECEIVED! 📊📊📊');
                  console.log('📊 Full data:', JSON.stringify(data, null, 2));

                  // Validate that we have actual data before creating visualization
                  // Support both cost queries (services) and EC2 queries (instances/totalCount)
                  const hasValidCostData = data.data.services &&
                    data.data.services.length > 0 &&
                    data.data.services.some((s: any) =>
                      s.service &&
                      s.service.length > 2 &&
                      !s.service.match(/^(were|other|system|cloud|slightly|decreased)$/i)
                    );

                  const hasValidEC2Data = data.data.queryType === 'ec2' &&
                    (data.data.totalCount !== undefined ||
                      (data.data.instances && data.data.instances.length > 0));

                  const hasValidEBSData = data.data.queryType === 'ebs' &&
                    (data.data.totalCount !== undefined ||
                      (data.data.volumes && data.data.volumes.length > 0));

                  const hasValidS3Data = data.data.queryType === 's3' &&
                    data.data.buckets && data.data.buckets.length > 0;

                  const hasValidEBSCostData = (data.data.queryType === 'ebs_cost'
                      || data.data.queryType === 'ebs_cost_multi_account') &&
                    Array.isArray(data.data.items);

                  if (data.data.queryType === 'error') {
                    // MCP returned an error — show error visualization instead of opening data panel
                    const rawErr = data.data.error || 'An unknown error occurred';
                    const vizBizErr = getBusinessError(rawErr);
                    const errViz: VisualizationData = {
                      id: Date.now().toString(),
                      type: 'error',
                      title: vizBizErr ? `Error Code: ${vizBizErr.code}` : `Query Failed: ${data.query || query}`,
                      data: { error: vizBizErr ? vizBizErr.message : rawErr, query: data.query || query },
                      timestamp: new Date(),
                      query: data.query || query
                    };
                    setQueryDashboards(prev => [...prev, errViz]);
                    setActiveQueryDashboard(errViz.id);
                    setActiveTab('overview');
                    visualizationReceivedRef.current = true;
                    console.log('❌ MCP error — showing error visualization');
                  } else if (hasValidCostData || hasValidEC2Data || hasValidEBSData || hasValidS3Data || hasValidEBSCostData) {
                    const vizData: VisualizationData = {
                      id: Date.now().toString(),
                      type: data.data.queryType || 'cost-breakdown',
                      title: `Query Result: ${data.query}`,
                      data: data.data,
                      timestamp: new Date(),
                      query: data.query
                    };

                    setQueryDashboards(prev => [...prev, vizData]);
                    // Auto-open the visualization and switch to DevOpsX tab
                    setActiveQueryDashboard(vizData.id);
                    setActiveTab('overview');
                    visualizationReceivedRef.current = true;
                    console.log('📊 Added visualization to dashboards and switched to DevOpsX tab');
                  } else {
                    console.log('📊 Skipping invalid visualization data - no valid data found');
                  }
                } else if (data.type === 'cost_multi_account' && data.data) {
                  // Multi-account fan-out chart from backend aggregateMultiAccountCost().
                  // Separate from cost_comparison so the 2-month and 3+-month comparison
                  // charts keep their own renderer untouched.
                  console.log('🌐 COST MULTI-ACCOUNT SSE EVENT RECEIVED!', data.data.accountCount, 'accounts');
                  const multiViz: VisualizationData = {
                    id: Date.now().toString(),
                    type: 'cost-breakdown',
                    title: `Cost across ${data.data.accountCount || 0} accounts`,
                    data: { ...data.data, queryType: 'cost_multi_account' },
                    timestamp: new Date(),
                    query: query
                  };
                  setQueryDashboards(prev => [...prev, multiViz]);
                  setActiveQueryDashboard(multiViz.id);
                  setActiveTab('overview');
                  visualizationReceivedRef.current = true;
                  console.log('🌐 Added multi-account visualization to dashboards');
                } else if (data.type === 'cost_comparison' && data.data) {
                  // Handle comparison visualization from backend aggregateCostComparison()
                  console.log('📊 COST COMPARISON SSE EVENT RECEIVED!');
                  const vizData: VisualizationData = {
                    id: Date.now().toString(),
                    type: 'cost-breakdown',
                    title: `Cost Comparison: ${data.data.periods?.join(' vs ') || query}`,
                    data: { ...data.data, queryType: 'cost_comparison' },
                    timestamp: new Date(),
                    query: query
                  };
                  setQueryDashboards(prev => [...prev, vizData]);
                  setActiveQueryDashboard(vizData.id);
                  setActiveTab('overview');
                  visualizationReceivedRef.current = true;
                  console.log('📊 Added comparison visualization to dashboards');
                } else if ((data.type === 'delta' || data.type === 'stream') && data.content) {
                  // Standard format for cost queries and updated MCP EKS format
                  streamContentRef.current += data.content;
                  console.log('📥 Streaming content length:', streamContentRef.current.length);

                  // Mark current task as complete when we get content
                  if (currentTaskIndex <= taskBreakdown.tasks.length) {
                    updateTaskStatus(currentTaskIndex, 'complete');
                  }

                  // Enhance streaming content with ANSI conversion and execution updates
                  const enhancedContent = streamingEnhancer.enhanceStreamingContent(
                    streamContentRef.current,
                    {
                      onExecutionUpdate: (step, status) => {
                        console.log(`🔄 Execution update: ${step} -> ${status}`);
                        setExecutionPlan(streamingEnhancer.getExecutionPlan());
                      }
                    }
                  );

                  // Stream raw response in real-time
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId && msg.structured) {
                      return {
                        ...msg,
                        structured: {
                          ...msg.structured,
                          rawResponse: streamContentRef.current, // Original raw content
                          enhancedResponse: enhancedContent, // Enhanced with ANSI conversion
                          sections: msg.structured.sections
                        }
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'content' && data.data) {
                  // Original EKS server format (before MCP client transformation) - fallback
                  streamContentRef.current += data.data;
                  console.log('📥 EKS streaming content length:', streamContentRef.current.length);

                  // Mark current task as complete when we get content
                  if (currentTaskIndex <= taskBreakdown.tasks.length) {
                    updateTaskStatus(currentTaskIndex, 'complete');
                  }

                  // Enhance streaming content with ANSI conversion and execution updates
                  const enhancedContent = streamingEnhancer.enhanceStreamingContent(
                    streamContentRef.current,
                    {
                      onExecutionUpdate: (step, status) => {
                        console.log(`🔄 EKS execution update: ${step} -> ${status}`);
                        setExecutionPlan(streamingEnhancer.getExecutionPlan());
                      }
                    }
                  );

                  // Stream raw response in real-time for EKS
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId && msg.structured) {
                      return {
                        ...msg,
                        structured: {
                          ...msg.structured,
                          rawResponse: streamContentRef.current,
                          enhancedResponse: enhancedContent, // Enhanced with ANSI conversion
                          sections: msg.structured.sections
                        }
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'server_source') {
                  // Add server source footer to content
                  streamContentRef.current += '\n\n' + data.message;
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      return {
                        ...msg,
                        content: streamContentRef.current,
                        structured: msg.structured // Preserve structured data
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'quota_exceeded') {
                  console.warn('🚫 Quota exceeded — stopping pipeline');
                  const quotaMsg = data.content || 'You have run out of tokens. Please upgrade your plan or purchase add-on tokens to continue.';
                  setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                      ? { ...msg, content: quotaMsg, structured: undefined }
                      : msg
                  ));
                  setIsLoading(false);
                  setExecutingMessageId(null);
                  if (authToken) fetchBillingData(authToken);
                  break;

                } else if (data.type === 'business_error') {
                  console.warn('🚨 Business error received:', data.code, data.message);
                  setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                      ? { ...msg, content: data.message, errorCode: data.code, structured: undefined }
                      : msg
                  ));
                  setIsLoading(false);
                  setExecutingMessageId(null);
                  break;

                } else if (data.type === 'pipeline_stop') {
                  // Pipeline stopped early (greeting, no tasks) — show content as assistant response
                  console.log('🛑 Pipeline stopped (no tasks/greeting). Content:', data.content);
                  setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                      ? { ...msg, content: data.content || "Hello! I'm your DevOpsX AI assistant.", structured: undefined }
                      : msg
                  ));
                  setIsLoading(false);
                  setExecutingMessageId(null);
                  if (authToken) fetchBillingData(authToken);
                  break; // Stop processing SSE — pipeline is done

                } else if (data.type === 'suggestions' && data.suggestions) {
                  // Handle smart follow-up suggestions from backend
                  console.log('💡 Suggestions received:', data.suggestions);
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      return {
                        ...msg,
                        suggestions: data.suggestions
                      };
                    }
                    return msg;
                  }));
                } else if (data.type === 'pipeline_end' || data.type === 'done' || (data.phase === 'completed' && data.type === 'phase')) {
                  console.log('🚨 MAIN COMPLETION HANDLER TRIGGERED:', data.type, data.phase);
                  console.log('🚨 MAIN HANDLER: Message ID:', messageId);
                  console.log('🏁 Pipeline completed, streaming content length:', streamContentRef.current.length);

                  if (data.type === 'pipeline_end') {
                    console.log('✅ ========== REQUEST END MARKER RECEIVED ==========');
                  }

                  // IMPORTANT: Only proceed with completion if we received pipeline_end or phase completed
                  // Do NOT close on mcp_complete as Thinker may still be streaming
                  if (data.type !== 'pipeline_end' && data.phase !== 'completed') {
                    console.log('⚠️ Ignoring premature completion signal, waiting for pipeline_end or phase completed');
                    continue;
                  }

                  console.log('🏁 Marking all execution plan items as COMPLETE');
                  console.log('🏁 Content preview:', streamContentRef.current.substring(0, 500));
                  const currentMsg = (messages as any[]).find((m: any) => m.id === messageId);
                  console.log('🏁 DEBUG: Current message exists:', !!currentMsg);
                  console.log('🏁 DEBUG: Current message structured data:', JSON.stringify(currentMsg?.structured, null, 2));
                  console.log('🏁 PERSISTENCE FIX: About to create/update structured data for persistence');

                  // Keep ALL streaming windows persistent and create/update execution plan
                  setMessages(prev => {
                    const existingMsg = prev.find(m => m.id === messageId);
                    console.log('🔍 PERSISTENCE: Found existing message:', !!existingMsg);

                    if (!existingMsg) {
                      // Message doesn't exist - create it with structured data
                      console.log('🚨 PERSISTENCE: Message not found, creating new structured message');
                      const newMessage: Message = {
                        id: messageId,
                        type: 'assistant' as const,
                        content: streamContentRef.current,
                        timestamp: new Date().toISOString(),
                        structured: {
                          summary: 'Query Completed',
                          finalResult: true,
                          sections: [
                            {
                              title: '📋 Execution Plan',
                              status: 'complete',
                              items: [
                                { text: 'Connect to MCP server', status: 'complete' },
                                { text: 'Process query request', status: 'complete' },
                                { text: 'Retrieve data from sources', status: 'complete' },
                                { text: 'Format and structure response', status: 'complete' },
                                { text: 'Return results to user', status: 'complete' }
                              ]
                            },
                            {
                              title: '🖥️ Live MCP Server Response',
                              status: 'complete',
                              items: [{ text: streamContentRef.current, status: 'complete' }]
                            }
                          ],
                          actions: getPostCompletionActions(query, { sections: [{ title: '📋 Execution Plan', status: 'complete' }, { title: '🖥️ Live MCP Server Response', status: 'complete' }] })
                        }
                      };
                      return [...prev, newMessage];
                    }

                    return prev.map(msg => {
                      if (msg.id === messageId) {
                        // Create structured data if it doesn't exist
                        if (!msg.structured) {
                          return {
                            ...msg,
                            content: streamContentRef.current,
                            structured: {
                              summary: 'Infrastructure Query Completed',
                              sections: [
                                {
                                  title: '📋 Execution Plan',
                                  status: 'complete',
                                  items: [
                                    { text: 'Connect to AWS EC2 service', status: 'complete' },
                                    { text: 'Query instances in region', status: 'complete' },
                                    { text: 'Filter running vs stopped instances', status: 'complete' },
                                    { text: 'Retrieve instance details', status: 'complete' },
                                    { text: 'Format response with server information', status: 'complete' }
                                  ]
                                },
                                {
                                  title: '🖥️ Live MCP Server Response',
                                  status: 'complete',
                                  items: [{ text: streamContentRef.current, status: 'complete' }]
                                }
                              ]
                            }
                          };
                        } else {
                          // Update existing structured data
                          const updatedSections = msg.structured?.sections?.map(section => {
                            if (section.title === '📋 Execution Plan') {
                              return {
                                ...section,
                                status: 'complete',
                                items: section.items.map((item: any) => ({
                                  ...item,
                                  status: 'complete' // Mark all execution plan items as complete
                                }))
                              };
                            }
                            return section;
                          }) || [];

                          return {
                            ...msg,
                            content: streamContentRef.current,
                            structured: {
                              ...msg.structured,
                              finalResult: true,
                              sections: updatedSections,
                              actions: getPostCompletionActions(query, msg.structured)
                            }
                          };
                        }
                      }
                      return msg;
                    });
                  });

                  console.log('✅ PERSISTENCE FIX: Message creation/update completed');
                  setIsLoading(false);
                  setExecutingMessageId(null);
                  setAbortController(null);
                  if (authToken) fetchBillingData(authToken);

                  // Only use regex fallback if backend never sent a visualization/cost_comparison SSE
                  // AND the query is not a comparison query (compare queries always use backend aggregation)
                  const isCompareQuery = query.toLowerCase().includes('compare') ||
                    query.toLowerCase().includes(' vs ') ||
                    query.toLowerCase().includes('versus');
                  if (!visualizationReceivedRef.current && !isCompareQuery) {
                    const visualization = detectVisualizationOpportunity(query, streamContentRef.current);
                    if (visualization) {
                      console.log('🎨 VISUALIZATION: Detected opportunity for', visualization.type);
                      setQueryDashboards(prev => [
                        ...prev.slice(-9),
                        visualization
                      ]);
                      setActiveQueryDashboard(visualization.id);
                      setActiveTab('overview');
                    }
                  }
                } else if (data.phase === 'storage' && data.type === 'phase') {
                  console.log('💾 Storage phase detected - query completing soon');
                  console.log('💾 PERSISTENCE FIX: About to create/update structured data in storage phase');
                  // This indicates the conversation is being stored and will end shortly

                  // Mark all execution plan tasks as complete or create structured data if missing
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                      // Create structured data if it doesn't exist or update existing
                      if (!msg.structured) {
                        return {
                          ...msg,
                          structured: {
                            summary: 'Infrastructure Query Completed',
                            finalResult: true,
                            sections: [
                              {
                                title: '📋 Execution Plan',
                                status: 'complete',
                                items: [
                                  { text: 'Connect to AWS EC2 service', status: 'complete' },
                                  { text: 'Query instances in region', status: 'complete' },
                                  { text: 'Filter running vs stopped instances', status: 'complete' },
                                  { text: 'Retrieve instance details', status: 'complete' },
                                  { text: 'Format response with server information', status: 'complete' }
                                ]
                              },
                              {
                                title: '🖥️ Live MCP Server Response',
                                status: 'complete',
                                items: [{ text: msg.content, status: 'complete' }]
                              }
                            ],
                            actions: getPostCompletionActions(query, { sections: [{ title: '📋 Execution Plan', status: 'complete' }, { title: '🖥️ Live MCP Server Response', status: 'complete' }] })
                          }
                        };
                      } else {
                        const updatedSections = msg.structured.sections?.map(section => {
                          if (section.title === '📋 Execution Plan') {
                            return {
                              ...section,
                              status: 'complete',
                              items: section.items.map((item: any) => ({
                                ...item,
                                status: 'complete'
                              }))
                            };
                          }
                          return section;
                        });
                        return {
                          ...msg,
                          structured: {
                            ...msg.structured,
                            sections: updatedSections
                          }
                        };
                      }
                    }
                    return msg;
                  }));

                  console.log('✅ PERSISTENCE FIX: setMessages called successfully');
                  console.log('🔄 PERSISTENCE FIX: About to set loading states');

                  // Skip showFinalResults for MCP queries - they handle completion via mcp_complete
                  // await showFinalResults(messageId, taskBreakdown, streamContentRef.current, query);
                  setIsLoading(false);
                  setExecutingMessageId(null);
                  setAbortController(null);

                  // Only use regex fallback if backend never sent a visualization/cost_comparison SSE
                  // AND the query is not a comparison query (compare queries always use backend aggregation)
                  const isCompareQuery = query.toLowerCase().includes('compare') ||
                    query.toLowerCase().includes(' vs ') ||
                    query.toLowerCase().includes('versus');
                  if (!visualizationReceivedRef.current && !isCompareQuery) {
                    const visualization = detectVisualizationOpportunity(query, streamContentRef.current);
                    if (visualization) {
                      console.log('🎨 VISUALIZATION: Detected opportunity for', visualization.type);
                      setQueryDashboards(prev => [
                        ...prev.slice(-9),
                        visualization
                      ]);
                      setActiveQueryDashboard(visualization.id);
                      setActiveTab('overview');
                    }
                  }

                  console.log('✅ MAIN COMPLETION HANDLER: All state updates completed');
                }
              } catch (e) {
                console.warn('⚠️ Failed to parse SSE data:', message.substring(0, 100), 'Error:', e);
                // Still try to display as plain text if JSON parsing fails
                const fallbackContent = message.replace('data: ', '').trim();
                if (fallbackContent && !fallbackContent.startsWith('{')) {
                  console.log('📄 Using fallback plain text content');
                  streamContentRef.current += fallbackContent + '\n';
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId && msg.structured) {
                      return {
                        ...msg,
                        structured: {
                          ...msg.structured,
                          rawResponse: streamContentRef.current,
                          sections: msg.structured.sections
                        }
                      };
                    }
                    return msg;
                  }));
                }
              }
            } else {
              // Handle non-SSE format - direct streaming content
              console.log('📄 Non-SSE message, treating as direct content:', message);
              streamContentRef.current += message + '\n';

              // Update UI with direct streaming content
              setMessages(prev => prev.map(msg => {
                if (msg.id === messageId && msg.structured) {
                  return {
                    ...msg,
                    structured: {
                      ...msg.structured,
                      rawResponse: streamContentRef.current
                    }
                  };
                }
                return msg;
              }));
            }
          }
        }
      } else {
        console.error('❌ No reader available for response stream');
      }
    } catch (error) {
      console.error('Error:', error);
      // Show error but keep task structure
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, content: `❌ Error: ${error}` }
          : msg
      ));
      setIsLoading(false);
    }
  };

  const showFinalResults = async (messageId: string, taskBreakdown: any, content: string, userQuery?: string) => {
    const { parsed, structured } = parseResponseContent(content, userQuery, false); // Mark as final

    // ADD final results to existing structure (don't replace)
    if (structured) {
      // Add context-aware post-completion quick actions
      const postCompletionActions = getPostCompletionActions(userQuery, structured);

      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId && msg.structured) {
          return {
            ...msg,
            content: parsed,
            structured: {
              ...msg.structured, // Keep existing execution plan and raw response
              finalResult: structured, // Add final result as new section
              sections: (() => {
                const existingSections = msg.structured.sections || [];
                const newSections = structured.sections || [];

                // Avoid duplicate sections by checking titles
                const combinedSections = [...existingSections];
                newSections.forEach((newSection: any) => {
                  const exists = existingSections.some((existing: any) => existing.title === newSection.title);
                  if (!exists) {
                    combinedSections.push(newSection);
                  }
                });

                return combinedSections;
              })(),
              actions: postCompletionActions // Replace with post-completion actions
            }
          };
        }
        return msg;
      }));
    } else {
      // Fallback: show results based on task type
      let finalStructured: any = null;

      // Server listing shows actual MCP response

      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
            ...msg,
            content: parsed || 'Task completed successfully',
            structured: msg.structured || finalStructured // Preserve existing structured data if it exists
          }
          : msg
      ));
    }
  };

  const executeSmartQuery = async (messageId: string, query: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔍 executeSmartQuery debug:', {
        hasToken: !!token,
        isAuthenticated: isAuthenticated,
        query: query
      });

      // Always use enhanced streaming, but handle missing token gracefully
      if (!token) {
        console.log('⚠️ No auth token found, proceeding with enhanced streaming anyway');
      }

      console.log('Making smart query request to router service:', {
        message: query,
        sessionId: activeSessionIdRef.current,
        hasToken: !!token,
        sessionSource: 'conversation'
      });

      const headers: any = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use the active conversation's sessionId
      let safeSessionId = activeSessionIdRef.current || currentSessionId;

      if (!safeSessionId) {
        safeSessionId = localStorage.getItem('conversation_session_id') || `temp_${Date.now()}`;
        console.warn('⚠️ Session ID fallback:', safeSessionId);
        localStorage.setItem('conversation_session_id', safeSessionId);
        setCurrentSessionId(safeSessionId); // Ensure state is updated
      }

      const response = await fetch(`${API_BASE_URL}/api/chat/routerx`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: query,
          sessionId: safeSessionId
        })
      });

      console.log('🧠 Smart query response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        console.log('Smart routing failed, falling back to normal query');
        return await executeNormalQuery(messageId, query);
      }

      // Check if response is JSON (account selection required) or stream
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        const result = await response.json();

        if (result.type === 'account_selection_required') {
          // Show account selection UI
          setMessages(prev => prev.map(msg =>
            msg.id === messageId
              ? {
                ...msg,
                content: result.message,
                structured: {
                  summary: '🔍 Account Selection Required',
                  sections: [{
                    title: 'Which account should I use?',
                    status: 'pending',
                    items: result.options.map((opt: any) => ({
                      text: `${opt.name} (${opt.description})`,
                      status: 'pending'
                    }))
                  }],
                  actions: result.options.map((opt: any) => ({
                    label: `Use ${opt.name}`,
                    type: 'action',
                    icon: 'check',
                    accountId: opt.id,
                    originalQuery: result.originalQuery
                  })).concat([{
                    label: 'Use All Accounts',
                    type: 'action',
                    icon: 'users',
                    accountId: 'all',
                    originalQuery: result.originalQuery
                  }])
                }
              }
              : msg
          ));
          return;
        }

        if (result.type === 'error') {
          setMessages(prev => prev.map(msg =>
            msg.id === messageId
              ? { ...msg, content: `❌ ${result.message}` }
              : msg
          ));
          return;
        }
      }

      // Handle streaming response (success case)
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      const streamContentRef = { current: '' };
      const visualizationReceivedRef = { current: false };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);

          // Simple SSE processing that works
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim().startsWith('data: ')) {
              try {
                const jsonStr = line.replace('data: ', '').trim();
                if (jsonStr) {
                  const data = JSON.parse(jsonStr);

                  // Handle routing decision - show MCP server info
                  if (data.type === 'routing_decision' && data.servers && data.servers[0]) {
                    const server = data.servers[0];
                    setMessages((prev: any) => prev.map((msg: any) =>
                      msg.id === messageId
                        ? { ...msg, content: `🎯 **${server.name}** (${(server.confidence * 100).toFixed(0)}% confidence)\n\n${server.reasoning}\n\n` }
                        : msg
                    ));
                  }

                  // Handle streaming content
                  else if ((data.type === 'delta' || data.type === 'stream') && data.content) {
                    streamContentRef.current += data.content;
                    setMessages((prev: any) => prev.map((msg: any) =>
                      msg.id === messageId
                        ? { ...msg, content: streamContentRef.current, structured: msg.structured } // Preserve structured data
                        : msg
                    ));
                  }

                  // Handle phase updates
                  else if (data.type === 'phase' && data.content) {
                    setMessages((prev: any) => prev.map((msg: any) =>
                      msg.id === messageId
                        ? { ...msg, content: `${data.content}` }
                        : msg
                    ));
                  }

                  // Handle completion
                  else if (data.type === 'done' || data.type === 'mcp_complete') {
                    console.log('✅ Received completion from:', data.server_name || 'server');
                    setIsLoading(false);
                    setExecutingMessageId(null);
                    setAbortController(null);
                    return;
                  }

                  // Debug logging
                  if (data.server_name) {
                    console.log(`📡 Response from: ${data.server_name}`);
                  }
                }
              } catch (e) {
                // Skip malformed JSON - common with streaming
                console.log('⚠️ Skipping malformed JSON:', line.substring(0, 100));
              }
            }
          }
        }
      }

    } catch (error) {
      console.error('Smart routing error:', error);
      // Fall back to normal query
      return await executeNormalQuery(messageId, query);
    }
  };

  const executeNormalQuery = async (messageId: string, query: string) => {
    // Add authentication to normal query as well
    let streamContent = '';
    const token = localStorage.getItem('auth_token');

    addLog('info', `Starting normal query: "${query.substring(0, 100)}${query.length > 100 ? '...' : ''}"`, 'frontend', 'executeNormalQuery');
    addLog('debug', `Authentication status: ${isAuthenticated ? 'authenticated' : 'unauthenticated'}`, 'frontend', 'executeNormalQuery', {
      hasToken: !!token,
      isAuthenticated: isAuthenticated,
      query: query
    });

    // Use the active conversation's sessionId
    const safeSessionId = activeSessionIdRef.current || currentSessionId || localStorage.getItem('conversation_session_id') || `temp_${Date.now()}`;

    const requestBody = {
      message: query,
      sessionId: safeSessionId,
      options: { autoExecute, dryRun, multiAccount },
      accountId: selectedAccountId
    };
    console.log('📤 Normal query request:', requestBody);
    console.log('🕐 Request timestamp:', new Date().toISOString());

    try {
      const headers: any = {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Try smart routing first, fallback to simple if no auth
      const endpoint = "/chat/routerx"; // Single unified endpoint
      addLog('info', `Using endpoint: ${endpoint}`, 'frontend', 'executeNormalQuery');

      const response = await fetch(`${API_BASE_URL}/api${endpoint}?t=${Date.now()}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      // Check if response is JSON (account selection required) or stream
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        const result = await response.json();

        if (result.type === 'account_selection_required') {
          // Show account selection UI
          setMessages(prev => prev.map(msg =>
            msg.id === messageId
              ? {
                ...msg,
                content: result.message,
                structured: {
                  summary: '🔍 Account Selection Required',
                  sections: [{
                    title: 'Which account should I use?',
                    status: 'pending',
                    items: result.options.map((opt: any) => ({
                      text: `${opt.name} (${opt.description})`,
                      status: 'pending'
                    }))
                  }],
                  actions: result.options.map((opt: any) => ({
                    label: `Use ${opt.name}`,
                    type: 'action',
                    icon: 'check',
                    accountId: opt.id,
                    originalQuery: result.originalQuery
                  })).concat([{
                    label: 'Use All Accounts',
                    type: 'action',
                    icon: 'users',
                    accountId: 'all',
                    originalQuery: result.originalQuery
                  }])
                }
              }
              : msg
          ));
          setIsLoading(false);
          return;
        }
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);

          // Handle escaped characters in the entire chunk first
          const unescapedChunk = chunk
            .replace(/\\n\\n/g, '\n\n')  // Unescape double newlines throughout the chunk
            .replace(/\\n/g, '\n')       // Unescape single newlines
            .replace(/\\"/g, '"');       // Unescape quotes

          // Split by SSE message delimiter (\n\n) to get individual messages
          const messages = unescapedChunk.split(/\n\n/);

          for (const message of messages) {
            if (message.trim().startsWith('data: ')) {
              try {
                const jsonStr = message.replace('data: ', '').trim();
                if (jsonStr) {
                  // Debug: log problematic JSON strings and add server info
                  if (jsonStr.includes('"') && !jsonStr.startsWith('{')) {
                    console.log('🐛 DEBUG: Potentially malformed JSON:', jsonStr.substring(0, 200));
                  }
                  const data = JSON.parse(jsonStr);

                  // Debug: log which server is responding
                  if (data.server_name || data.server_id) {
                    console.log(`📡 Response from: ${data.server_name || data.server_id}`);
                  }

                  if (data.type === 'Greeting_message' && data.content) {
                    // Handle greeting response from backend
                    console.log('👋 Greeting message received:', data.content);
                    setMessages(prev => prev.map(msg =>
                      msg.id === messageId ? { ...msg, content: data.content, isGreeting: true, structured: undefined } : msg
                    ));
                    setIsLoading(false);
                  } else if (data.type === 'status' && data.content) {
                    setMessages(prev => prev.map(msg =>
                      msg.id === messageId ? { ...msg, content: data.content } : msg
                    ));
                  } else if (data.type === 'thinking') {
                    // Handle EKS thinking/processing indicator
                    setMessages(prev => prev.map(msg =>
                      msg.id === messageId ? { ...msg, content: '🤔 Processing your EKS request...' } : msg
                    ));
                  } else if (data.type === 'account_info' && data.content) {
                    // Show account information
                    setMessages(prev => prev.map(msg =>
                      msg.id === messageId ? { ...msg, content: `🔗 ${data.content}` } : msg
                    ));
                  } else if (((data.type === 'delta' || data.type === 'stream') && data.content) || (data.type === 'content' && data.data)) {
                    // Handle both formats: cost queries use 'delta'+'content', EKS uses 'content'+'data'
                    const contentToAdd = data.content || data.data || '';
                    streamContent += contentToAdd;

                    // Enhance streaming content with ANSI conversion and execution updates
                    const enhancedContent = streamingEnhancer.enhanceStreamingContent(
                      streamContent,
                      {
                        onExecutionUpdate: (step, status) => {
                          console.log(`🔄 Normal query execution update: ${step} -> ${status}`);
                          setExecutionPlan(streamingEnhancer.getExecutionPlan());
                        }
                      }
                    );

                    const { parsed, structured } = parseResponseContent(streamContent, query, true); // Mark as streaming
                    console.log('🔍 DEBUG: parseResponseContent result for query:', query);
                    console.log('🔍 DEBUG: structured data:', structured);

                    // Add raw response and enhanced response to structured content for streaming
                    const enhancedStructured = structured ? {
                      ...structured,
                      rawResponse: streamContent,
                      enhancedResponse: enhancedContent // Enhanced with ANSI conversion
                    } : null;

                    setMessages(prev => prev.map(msg =>
                      msg.id === messageId ? { ...msg, content: parsed, structured: enhancedStructured } : msg
                    ));
                  } else if (data.type === 'done') {
                    // Handle completion - EKS sends final data in 'data' field, others might use 'content'
                    if (data.data) {
                      streamContent += data.data;
                      const { parsed, structured } = parseResponseContent(streamContent, query, false); // Mark as final

                      setMessages(prev => prev.map(msg => {
                        if (msg.id === messageId) {
                          // Preserve existing structured data if it exists (from mcp_complete handler)
                          if (msg.structured) {
                            return { ...msg, content: parsed, structured: { ...msg.structured, rawResponse: streamContent } };
                          } else {
                            return { ...msg, content: parsed, structured };
                          }
                        }
                        return msg;
                      }));
                    }
                    setIsLoading(false);
                  }
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      }
    } catch (error) {
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, content: 'Error connecting to router service.' } : msg
      ));
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    setInputValue(action);
  };

  // Generate LLM-based recommendations based on cost data
  // Infrastructure polling service
  const pollInfrastructureData = async () => {
    setIsPolling(true);
    try {
      // Simulate comprehensive infrastructure data fetching
      // In real implementation, this would call multiple AWS/GCP/GitHub APIs
      const mockInfraData = {
        accounts: {
          aws: [
            {
              id: 'aws-prod-123456789',
              name: 'AWS Production',
              region: 'us-east-1',
              status: 'active',
              services: {
                ec2: {
                  instances: 47,
                  running: 42,
                  stopped: 5,
                  types: { 't3.medium': 15, 't3.large': 12, 'm5.xlarge': 8, 'c5.large': 12 },
                  totalVCpus: 156,
                  totalMemory: '624 GB'
                },
                rds: {
                  instances: 8,
                  engines: { 'mysql': 3, 'postgresql': 3, 'aurora': 2 },
                  totalStorage: '2.4 TB',
                  multiAZ: 5
                },
                s3: {
                  buckets: 23,
                  totalSize: '15.7 TB',
                  objects: 2847592,
                  storageClasses: { 'standard': '8.2 TB', 'ia': '4.1 TB', 'glacier': '3.4 TB' }
                },
                lambda: {
                  functions: 156,
                  totalInvocations24h: 4582945,
                  errorRate: 0.03,
                  avgDuration: '245ms'
                },
                eks: {
                  clusters: 3,
                  nodes: 18,
                  pods: 156,
                  namespaces: 12
                },
                ecs: {
                  clusters: 4,
                  services: 23,
                  tasks: 67,
                  containerInstances: 15
                }
              }
            },
            {
              id: 'aws-staging-987654321',
              name: 'AWS Staging',
              region: 'us-west-2',
              status: 'active',
              services: {
                ec2: { instances: 12, running: 10, stopped: 2, totalVCpus: 48 },
                rds: { instances: 3, totalStorage: '500 GB' },
                s3: { buckets: 8, totalSize: '2.1 TB' },
                lambda: { functions: 45, totalInvocations24h: 125847 },
                eks: { clusters: 1, nodes: 6, pods: 34 }
              }
            },
            {
              id: 'aws-dev-456789123',
              name: 'AWS Development',
              region: 'eu-west-1',
              status: 'active',
              services: {
                ec2: { instances: 8, running: 6, stopped: 2, totalVCpus: 24 },
                rds: { instances: 2, totalStorage: '100 GB' },
                s3: { buckets: 5, totalSize: '800 GB' },
                lambda: { functions: 28, totalInvocations24h: 45672 }
              }
            }
          ],
          gcp: [
            {
              id: 'gcp-prod-project-1',
              name: 'GCP Production',
              region: 'us-central1',
              status: 'active',
              services: {
                gce: { instances: 15, running: 13, stopped: 2, totalVCpus: 60 },
                gks: { clusters: 2, nodes: 12, pods: 89 },
                cloudsql: { instances: 4, totalStorage: '800 GB' },
                storage: { buckets: 12, totalSize: '5.2 TB' },
                functions: { functions: 67, totalInvocations24h: 234567 }
              }
            },
            {
              id: 'gcp-analytics-project-2',
              name: 'GCP Analytics',
              region: 'us-east1',
              status: 'active',
              services: {
                bigquery: { datasets: 8, tables: 156, totalSize: '12.4 TB' },
                dataflow: { jobs: 12, running: 3 },
                storage: { buckets: 6, totalSize: '8.9 TB' }
              }
            }
          ],
          github: [
            {
              id: 'github-org-main',
              name: 'Main Organization',
              status: 'active',
              repositories: {
                total: 234,
                private: 198,
                public: 36,
                languages: { 'JavaScript': 89, 'Python': 67, 'Java': 34, 'Go': 28, 'TypeScript': 16 }
              },
              actions: {
                workflows: 156,
                runsLastWeek: 1247,
                successRate: 94.2,
                totalMinutes: 45672
              },
              security: {
                vulnerabilities: 23,
                dependabotAlerts: 45,
                secretScanning: 12
              }
            }
          ]
        },
        summary: {
          totalResources: 1247,
          totalCost: 274491.40,
          healthScore: 87,
          securityScore: 78,
          utilizationScore: 65
        },
        alerts: [
          { type: 'warning', message: 'High EC2 utilization in prod account (>90%)', account: 'aws-prod-123456789' },
          { type: 'info', message: '5 stopped instances can be terminated', account: 'aws-prod-123456789' },
          { type: 'security', message: '23 security vulnerabilities detected', account: 'github-org-main' }
        ]
      };

      setInfrastructureData(mockInfraData);

      // Mock deployments data
      const mockDeployments = {
        recent: [
          {
            id: 'dep-001',
            service: 'user-service',
            environment: 'production',
            version: 'v2.1.4',
            status: 'success',
            pipeline: 'Jenkins',
            deployer: 'john.doe@company.com',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
            duration: '4m 32s',
            rollbackAvailable: true
          },
          {
            id: 'dep-002',
            service: 'payment-gateway',
            environment: 'staging',
            version: 'v1.8.2',
            status: 'in_progress',
            pipeline: 'GitHub Actions',
            deployer: 'jane.smith@company.com',
            timestamp: new Date(Date.now() - 15 * 60 * 1000),
            duration: '15m 23s',
            progress: 67
          },
          {
            id: 'dep-003',
            service: 'notification-service',
            environment: 'production',
            version: 'v3.0.1',
            status: 'failed',
            pipeline: 'ArgoCD',
            deployer: 'deploy-bot',
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
            duration: '2m 15s',
            error: 'Health check failed after 5 attempts',
            rollbackAvailable: true
          }
        ],
        connectors: [
          { name: 'Jenkins', status: 'connected', jobs: 45, lastSync: '2 minutes ago' },
          { name: 'GitHub Actions', status: 'connected', workflows: 67, lastSync: '1 minute ago' },
          { name: 'ArgoCD', status: 'connected', applications: 23, lastSync: '30 seconds ago' },
          { name: 'Argo Rollouts', status: 'connected', rollouts: 12, lastSync: '1 minute ago' },
          { name: 'Kubernetes', status: 'connected', clusters: 4, lastSync: '45 seconds ago' }
        ],
        stats: {
          totalDeployments: 1247,
          successRate: 94.2,
          avgDeployTime: '6m 42s',
          deploymentsToday: 23,
          rollbacksThisWeek: 3
        }
      };

      // Mock alerts data
      const mockAlerts = {
        active: [
          {
            id: 'alert-001',
            name: 'High CPU Usage',
            severity: 'critical',
            resource: 'EC2 i-0123456789abcdef0',
            threshold: '85%',
            currentValue: '92%',
            triggered: new Date(Date.now() - 30 * 60 * 1000),
            account: 'aws-prod-123456789'
          },
          {
            id: 'alert-002',
            name: 'Low Disk Space',
            severity: 'warning',
            resource: 'RDS mysql-prod-01',
            threshold: '10GB',
            currentValue: '8.2GB',
            triggered: new Date(Date.now() - 2 * 60 * 60 * 1000),
            account: 'aws-prod-123456789'
          },
          {
            id: 'alert-003',
            name: 'Failed Health Checks',
            severity: 'critical',
            resource: 'ELB prod-web-elb',
            threshold: '3 failures',
            currentValue: '5 failures',
            triggered: new Date(Date.now() - 10 * 60 * 1000),
            account: 'aws-prod-123456789'
          }
        ],
        rules: [
          {
            id: 'rule-001',
            name: 'EC2 CPU Threshold',
            metric: 'CPUUtilization',
            operator: '>',
            threshold: 85,
            duration: '5 minutes',
            enabled: true,
            notifications: ['email', 'slack']
          },
          {
            id: 'rule-002',
            name: 'RDS Storage Warning',
            metric: 'FreeStorageSpace',
            operator: '<',
            threshold: 10,
            duration: '10 minutes',
            enabled: true,
            notifications: ['email']
          },
          {
            id: 'rule-003',
            name: 'Lambda Error Rate',
            metric: 'ErrorRate',
            operator: '>',
            threshold: 5,
            duration: '2 minutes',
            enabled: false,
            notifications: ['pagerduty', 'slack']
          }
        ]
      };

      setDeployments(mockDeployments);
      setAlerts(mockAlerts);
      setLastPolled(new Date());
    } catch (error) {
      console.error('Failed to poll infrastructure data:', error);
    } finally {
      setIsPolling(false);
    }
  };

  // Auto-poll infrastructure data
  useEffect(() => {
    pollInfrastructureData();
    const interval = setInterval(pollInfrastructureData, 5 * 60 * 1000); // Poll every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Cleanup scan polling on unmount
  useEffect(() => {
    return () => {
      if (scanPollIntervalRef.current) clearInterval(scanPollIntervalRef.current);
      if (scanPollTimeoutRef.current) clearTimeout(scanPollTimeoutRef.current);
    };
  }, []);

  // Fetch Kubernetes clusters when kubernetes tab is active
  useEffect(() => {
    if (activeTab === 'kubernetes' && isAuthenticated && authToken) {
      fetchKubernetesClusters();
    }
  }, [activeTab, isAuthenticated, authToken]);

  const generateSmartRecommendations = (costData: any) => {
    const recommendations = [];
    const totalCost = parseFloat(costData.totalCost.replace(/,/g, ''));
    const topService = costData.services[0];
    const topServiceCost = parseFloat(topService.cost.replace(/,/g, ''));
    const topServicePercentage = (topServiceCost / totalCost) * 100;

    // Cost threshold recommendations
    if (totalCost > 200000) {
      recommendations.push({
        type: 'critical',
        title: 'High Monthly Spend Alert',
        description: `Your monthly AWS spend of $${totalCost.toLocaleString()} is significantly high. Consider implementing cost governance policies.`,
        action: 'Review usage patterns and implement budget alerts',
        savings: `Potential savings: $${(totalCost * 0.15).toLocaleString()}`
      });
    }

    // Top service recommendations
    if (topService.service.toLowerCase().includes('ec2')) {
      recommendations.push({
        type: 'optimization',
        title: 'EC2 Cost Optimization',
        description: `EC2 represents ${topServicePercentage.toFixed(1)}% of your total cost ($${topServiceCost.toLocaleString()}). Consider Reserved Instances or Savings Plans.`,
        action: 'Analyze instance types and utilization patterns',
        savings: `Potential savings: $${(topServiceCost * 0.3).toLocaleString()} (30% with RIs)`
      });
    }

    // Storage optimization
    const storageServices = costData.services.filter((s: any) =>
      s.service.toLowerCase().includes('s3') ||
      s.service.toLowerCase().includes('storage') ||
      s.service.toLowerCase().includes('file system')
    );

    if (storageServices.length > 0) {
      const storageCost = storageServices.reduce((sum: number, s: any) =>
        sum + parseFloat(s.cost.replace(/,/g, '')), 0
      );
      recommendations.push({
        type: 'storage',
        title: 'Storage Lifecycle Optimization',
        description: `Storage costs total $${storageCost.toLocaleString()}. Implement lifecycle policies to transition data to cheaper storage classes.`,
        action: 'Set up S3 Lifecycle rules and review EFS performance mode',
        savings: `Potential savings: $${(storageCost * 0.25).toLocaleString()} (25% with lifecycle policies)`
      });
    }

    // Support cost optimization
    const supportService = costData.services.find((s: any) =>
      s.service.toLowerCase().includes('support')
    );

    if (supportService) {
      const supportCost = parseFloat(supportService.cost.replace(/,/g, ''));
      if (supportCost > totalCost * 0.05) {
        recommendations.push({
          type: 'support',
          title: 'Support Plan Review',
          description: `AWS Support costs $${supportCost.toLocaleString()}, which is ${((supportCost / totalCost) * 100).toFixed(1)}% of total spend.`,
          action: 'Review if Enterprise Support level is still needed',
          savings: `Potential savings: $${(supportCost * 0.5).toLocaleString()} (with Business Support)`
        });
      }
    }

    // Multi-service recommendations
    if (costData.services.length > 5) {
      recommendations.push({
        type: 'governance',
        title: 'Cost Allocation & Tagging',
        description: `You're using ${costData.services.length} different AWS services. Improve cost visibility with proper tagging.`,
        action: 'Implement consistent resource tagging strategy',
        savings: 'Better visibility leads to 10-15% cost reduction on average'
      });
    }

    return recommendations.slice(0, 6); // Return top 6 recommendations
  };

  // Test function to set mock cost data
  const testCostVisualization = () => {
    const mockData = {
      type: 'cost_analysis',
      summary: 'Total AWS cost: $274,491.40 for the specified period.',
      totalCost: '274491.40',
      services: [
        { service: 'EC2 Compute', cost: '184809.05' },
        { service: 'AWS Support (Enterprise)', cost: '20006.02' },
        { service: 'EC2 - Other', cost: '16424.05' },
        { service: 'Amazon Elastic File System', cost: '15418.46' },
        { service: 'Amazon ElastiCache', cost: '6750.08' },
        { service: 'Amazon S3', cost: '6132.05' },
        { service: 'Amazon VPC', cost: '5395.31' },
        { service: 'Amazon RDS', cost: '4983.95' }
      ],
      insights: [
        'Found 8 billable services',
        'Highest cost: EC2 Compute',
        'Consider cost optimization opportunities'
      ]
    };
    setLatestCostData(mockData);
    setActiveTab('costs');
  };

  // AWS FinOps Scan Function
  const scanAWSRecommendations = async () => {
    setAwsScanStatus('scanning');

    // Predefined scan messages — cycle client-side every 800ms (no timing dependency on backend)
    const SCAN_MESSAGES = [
      'Connecting to your AWS account & setting up scan...',
      'Scanning EC2 instances across regions...',
      'Scanning RDS databases...',
      'Scanning S3 buckets...',
      'Scanning EBS volumes...',
      'Scanning Lambda functions...',
      'Scanning EKS clusters...',
      'Scanning ECS clusters...',
      'Scanning ElastiCache clusters...',
      'Scanning DynamoDB tables...',
      'Scanning CloudFront distributions...',
      'Scanning Load Balancers...',
      'Scanning NAT Gateways...',
      'Scanning OpenSearch domains...',
      'Scanning Redshift clusters...',
      'Scanning SageMaker resources...',
      'Pulling your last 3 months of AWS spend from Cost Explorer...',
      'Breaking down your costs by service & usage type...',
      'Checking your Savings Plans & Reserved Instance coverage & utilization...',
      'Analyzing On-Demand vs Spot vs Reserved vs Savings Plan compute spend...',
      'Calculating your DevOpsX pre-approved savings (no commitment required)...',
      // 'Identifying idle & wasteful resources to find savings opportunities...',
      'Finalizing scan results and generating recommendations...',
    ];

    // Start cycling through messages every 800ms
    let msgIndex = 0;
    setScanProgress(SCAN_MESSAGES[msgIndex++]);
    const msgCycleInterval = setInterval(() => {
      if (msgIndex < SCAN_MESSAGES.length) {
        setScanProgress(SCAN_MESSAGES[msgIndex++]);
      }
      // After last message, keep showing last one until scan completes
    }, 800);

    try {
      const baseUrl = API_BASE_URL;

      const response = await fetch(`${baseUrl}/api/cost/scan/aws`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        clearInterval(msgCycleInterval);
        const errorData = await response.json();
        if (errorData.errorCode === 'NO_AWS_ACCOUNT') {
          setAwsScanStatus('error');
          setScanProgress('No AWS accounts connected. Please add an AWS account in Settings.');
          return;
        }
        throw new Error(errorData.error || 'Failed to start AWS scan');
      }

      const data = await response.json();
      const scanId = data.scanId;
      setLastAwsScanId(scanId);

      // Poll backend every 5 seconds — only to detect completion
      scanPollIntervalRef.current = setInterval(async () => {
        try {
          const progressResponse = await fetch(`${baseUrl}/api/cost/scan/aws/${scanId}/progress`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });

          if (progressResponse.ok) {
            const progressData = await progressResponse.json();

            if (progressData.status === 'completed') {
              clearInterval(msgCycleInterval);
              clearInterval(scanPollIntervalRef.current!);
              scanPollIntervalRef.current = null;

              // Fetch the scan results
              const resultsResponse = await fetch(`${baseUrl}/api/cost/scan/aws/${scanId}`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
              });

              if (resultsResponse.ok) {
                const results = await resultsResponse.json();
                const scanData = results.data || results;
                const recommendations = scanData.recommendations?.recommendations || [];
                const summary = scanData.summary || {};
                const costData = scanData.costData || {};

                if (summary.totalResources) setResourcesAnalyzed(summary.totalResources);
                if (costData.currentMonthCost) setTotalCurrentSpend(costData.currentMonthCost);
                if (costData.threeMonthAverage) setThreeMonthAverage(costData.threeMonthAverage);

                setAwsScanResults({
                  timestamp: new Date().toISOString(),
                  summary,
                  costData,
                  recommendations,
                  rawResponse: `Scan completed successfully!\n\nResources Found: ${summary.totalResources || 0}\nCurrent Monthly Cost: $${Math.round(costData.currentMonthCost || 0)}\nRecommendations Generated: ${recommendations.length}\nPotential Monthly Savings: $${Math.round(scanData.recommendations?.summary?.totalPotentialSavings?.monthly || 0)}`
                });

                setAwsScanStatus('complete');
                setScanProgress('Scan completed successfully. Your cost optimization report is ready.');

                // Update savings tracking service with new scan results
                try {
                  const scanCalcResponse = await fetch(`${baseUrl}/api/savings/scan-and-calculate`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({ accountId: selectedAccountId || null })
                  });
                  if (scanCalcResponse.ok) {
                    console.log('✅ Savings tracking updated with new scan results');
                  } else {
                    console.warn('⚠️ Failed to update savings tracking with scan results');
                  }
                } catch (scanCalcError) {
                  console.error('Error updating savings tracking:', scanCalcError);
                }

                await fetchCostData();
              }
            } else if (progressData.status === 'failed') {
              clearInterval(msgCycleInterval);
              clearInterval(scanPollIntervalRef.current!);
              scanPollIntervalRef.current = null;
              setAwsScanStatus('error');
              setScanProgress('Scan failed. Please try again.');
            }
          }
        } catch (pollError) {
          console.error('Error polling scan progress:', pollError);
        }
      }, 5000); // Poll every 5 seconds — only for completion status

      // Auto-stop polling after 15 minutes
      scanPollTimeoutRef.current = setTimeout(() => {
        clearInterval(msgCycleInterval);
        clearInterval(scanPollIntervalRef.current!);
        scanPollIntervalRef.current = null;
        scanPollTimeoutRef.current = null;
        if (awsScanStatus === 'scanning') {
          setScanProgress('Scan taking longer than expected. Check back in 24 hours.');
          setAwsScanStatus('idle');
        }
      }, 15 * 60 * 1000);

    } catch (error) {
      clearInterval(msgCycleInterval);
      console.error('AWS Scan Error:', error);
      setAwsScanStatus('error');
      setScanProgress(error instanceof Error ? error.message : 'Error scanning AWS accounts');
    }
  };

  // Fetch detailed scan report

  const fetchScanDetails = async (scanId: string) => {
    try {
      setLoadingScanDetails(true);
      const baseUrl = API_BASE_URL;

      console.log('🔍 Fetching detailed scan report for:', scanId);

      const response = await fetch(`${baseUrl}/api/cost/scan/aws/${scanId}/details`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch scan details: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setScanDetailsData(result);
        setShowScanDetailsModal(true);
        console.log('✅ Scan details loaded:', result);
      } else {
        alert('Failed to load scan details: ' + (result.error || 'Unknown error'));
      }
    } catch (err: any) {
      console.error('Error fetching scan details:', err);
      alert('Failed to load scan details: ' + err.message);
    } finally {
      setLoadingScanDetails(false);
    }
  };

  // GCP FinOps Scan Function
  const scanGCPRecommendations = async () => {
    setGcpScanStatus('scanning');
    setScanProgress('Initializing GCP scan...');

    try {
      setScanProgress('Fetching GCP Cost Usage Report...');

      const scanQuery = `
        Analyze GCP account for FinOps recommendations:
        1. Fetch detailed cost usage report for the last 30 days
        2. Analyze active Committed Use Discounts (CUDs) and coverage
        3. Analyze Sustained Use Discounts (SUDs) application
        4. Identify standard instances that could use Preemptible VMs
        5. Analyze Data Transfer (egress) costs and rates
        6. Review Compute Engine, Cloud Run, GKE costs
        7. Review Cloud Storage, Persistent Disk costs and usage
        8. Review Cloud CDN usage and optimization opportunities
        9. Generate recommendations with potential savings

        For each recommendation, provide:
        - Current cost
        - Potential savings ($ and %)
        - Priority (High/Medium/Low)
        - Implementation complexity
        - Risks and considerations
      `;

      const response = await fetch(`${API_BASE_URL}/api/chat/routerx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: scanQuery,
          sessionId: activeSessionId || currentSessionId,
          selectedAccount: 'gcp' // Route to GCP accounts
        })
      });

      if (!response.ok) {
        throw new Error('Failed to scan GCP accounts');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          fullResponse += chunk;

          // Update progress based on content
          if (chunk.includes('cost')) setScanProgress('Analyzing cost data...');
          if (chunk.includes('savings')) setScanProgress('Calculating savings potential...');
          if (chunk.includes('recommendation')) setScanProgress('Generating recommendations...');
        }
      }

      // Parse the response and extract structured data
      const recommendations = parseGCPRecommendations(fullResponse);
      setGcpScanResults(recommendations);
      setGcpScanStatus('complete');
      setScanProgress('GCP scan complete!');

    } catch (error) {
      console.error('GCP Scan Error:', error);
      setGcpScanStatus('error');
      setScanProgress('Error scanning GCP accounts');
    }
  };

  // Parse AWS recommendations from LLM response
  const parseAWSRecommendations = (response: string) => {
    // This function will extract structured data from the LLM response
    // For now, return a basic structure that can be enhanced
    return {
      timestamp: new Date(),
      totalSavingsPotential: 0,
      recommendations: [],
      rawResponse: response
    };
  };

  // Parse GCP recommendations from LLM response
  const parseGCPRecommendations = (response: string) => {
    // This function will extract structured data from the LLM response
    // For now, return a basic structure that can be enhanced
    return {
      timestamp: new Date(),
      totalSavingsPotential: 0,
      recommendations: [],
      rawResponse: response
    };
  };

  // Fetch Kubernetes Clusters (EKS and GKE)
  const fetchKubernetesClusters = async () => {
    setLoadingClusters(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Kubernetes clusters');
      }

      const data = await response.json();
      setKubernetesClusters(data.clusters || []);
    } catch (error) {
      console.error('Error fetching Kubernetes clusters:', error);
      setKubernetesClusters([]);
    } finally {
      setLoadingClusters(false);
    }
  };

  // Handle view cluster details
  const handleViewClusterDetails = async (cluster: any) => {
    setSelectedCluster(cluster);
    setShowClusterDetailsModal(true);
    setClusterDetailsTab('cluster');
    setSelectedNamespace('all');
    setSelectedResourceType('nodes');

    // Fetch cluster resources (default to nodes)
    await fetchClusterResources(cluster.id, 'nodes');
  };

  // Fetch cluster resources
  const fetchClusterResources = async (clusterId: string, resourceType?: string, namespace?: string, forceRefresh: boolean = false) => {
    // Skip if data exists and not force refresh
    if (clusterResources && !forceRefresh && selectedResourceType === resourceType) {
      console.log('Using cached cluster resources data');
      return;
    }

    setLoadingClusterDetails(true);
    try {
      const params = new URLSearchParams();
      if (resourceType) params.append('resourceType', resourceType);
      if (namespace && namespace !== 'all') params.append('namespace', namespace);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/resources${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cluster resources');
      }

      const data = await response.json();
      setClusterResources(data);
      setClusterResourcesTimestamp(new Date());
    } catch (error) {
      console.error('Error fetching cluster resources:', error);
      setClusterResources(null);
      setClusterResourcesTimestamp(null);
    } finally {
      setLoadingClusterDetails(false);
    }
  };

  // Fetch cluster deployments
  const fetchClusterDeployments = async (clusterId: string, namespace?: string, forceRefresh: boolean = false) => {
    // Skip if data exists and not force refresh
    if (clusterDeployments && !forceRefresh) {
      console.log('Using cached cluster deployments data');
      return;
    }

    setLoadingClusterDetails(true);
    try {
      const namespaceParam = namespace && namespace !== 'all' ? `?namespace=${namespace}` : '';
      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/deployments${namespaceParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cluster deployments');
      }

      const data = await response.json();
      setClusterDeployments(data);
      setClusterDeploymentsTimestamp(new Date());
    } catch (error) {
      console.error('Error fetching cluster deployments:', error);
      setClusterDeployments(null);
      setClusterDeploymentsTimestamp(null);
    } finally {
      setLoadingClusterDetails(false);
    }
  };

  // Fetch cluster monitoring
  const fetchClusterMonitoring = async (clusterId: string, forceRefresh: boolean = false) => {
    // Skip if data exists and not force refresh
    if (clusterMonitoring && !forceRefresh) {
      console.log('Using cached cluster monitoring data');
      return;
    }

    setLoadingClusterDetails(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/monitoring`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cluster monitoring');
      }

      const data = await response.json();
      setClusterMonitoring(data);
      setClusterMonitoringTimestamp(new Date());
    } catch (error) {
      console.error('Error fetching cluster monitoring:', error);
      setClusterMonitoring(null);
      setClusterMonitoringTimestamp(null);
    } finally {
      setLoadingClusterDetails(false);
    }
  };

  // Handle delete EKS cluster with dual confirmation
  const handleDeleteCluster = async (cluster: any) => {
    // First confirmation
    const firstConfirm = window.confirm(
      `⚠️ Delete EKS Cluster?\n\n` +
      `Cluster: ${cluster.name || cluster.clusterName}\n` +
      `Account: ${cluster.accountId}\n` +
      `Region: ${cluster.region}\n\n` +
      `This will remove the cluster from DevOpsX.\n` +
      `You can re-add it later if needed.\n\n` +
      `Are you sure you want to continue?`
    );

    if (!firstConfirm) {
      return;
    }

    // Second confirmation (more explicit)
    const secondConfirm = window.confirm(
      `⚠️ FINAL CONFIRMATION\n\n` +
      `You are about to delete:\n` +
      `${cluster.name || cluster.clusterName}\n\n` +
      `Type "YES" in your mind and click OK to confirm deletion.\n` +
      `This action cannot be undone from this dialog.`
    );

    if (!secondConfirm) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/${cluster.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete cluster');
      }

      // Remove from the list
      setKubernetesClusters(prev => prev.filter(c => c.id !== cluster.id));
      setConnectedAccounts(prev => prev.filter(acc => acc.id !== cluster.id));

      alert(`✅ Cluster "${cluster.name || cluster.clusterName}" deleted successfully`);
    } catch (error: any) {
      console.error('Error deleting cluster:', error);
      alert(`❌ Failed to delete cluster: ${error.message}`);
    }
  };

  // Handle sync cluster
  const handleSyncCluster = async (clusterId: string) => {
    setSyncingCluster(clusterId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/sync`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to sync cluster');
      }

      const data = await response.json();

      // Update the cluster in the list with the synced data
      setKubernetesClusters(prev => prev.map(c =>
        c.id === clusterId ? { ...c, ...data.cluster, lastSync: new Date().toISOString() } : c
      ));

      // Show success message (you can add a toast notification here if needed)
      console.log('Cluster synced successfully:', data);
    } catch (error) {
      console.error('Error syncing cluster:', error);
      // Show error message (you can add a toast notification here if needed)
    } finally {
      setSyncingCluster(null);
    }
  };

  // Fetch upgrade analysis for a cluster
  const fetchUpgradeAnalysis = async (clusterId: string) => {
    setLoadingUpgradeAnalysis(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/upgrade-analysis`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch upgrade analysis');
      }

      const data = await response.json();
      setUpgradeAnalysis(data);
      setShowUpgradeWizard(true);
    } catch (error: any) {
      console.error('Error fetching upgrade analysis:', error);
      alert(`Failed to fetch upgrade analysis: ${error.message}`);
    } finally {
      setLoadingUpgradeAnalysis(false);
    }
  };

  // Handle cluster upgrade
  const handleUpgradeCluster = async (targetVersion: string, upgradeMode: 'zero-downtime' | 'force') => {
    if (!selectedCluster) return;

    const estimatedTime = upgradeMode === 'zero-downtime'
      ? upgradeAnalysis?.estimatedTime?.zeroDowntime?.total || 50
      : upgradeAnalysis?.estimatedTime?.forceUpgrade?.total || 20;

    const confirmMessage = upgradeMode === 'zero-downtime'
      ? `Upgrade ${selectedCluster.name || selectedCluster.clusterName} to version ${targetVersion}?\n\nThis will be a zero-downtime upgrade with rolling updates.\n\nEstimated time: ${estimatedTime} minutes`
      : `⚠️ FORCE UPGRADE WARNING\n\nThis will cause service disruption!\n\nUpgrade ${selectedCluster.name || selectedCluster.clusterName} to version ${targetVersion}?\n\nEstimated time: ${estimatedTime} minutes`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setUpgradingCluster(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/clusters/${selectedCluster.id}/upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetVersion,
          upgradeNodeGroups: true,
          upgradeAddons: true,
          drainNodes: upgradeMode === 'zero-downtime',
          maxUnavailable: upgradeMode === 'zero-downtime' ? 1 : 999
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to initiate cluster upgrade');
      }

      const data = await response.json();

      // Close the upgrade wizard and show progress modal
      setShowUpgradeWizard(false);
      setUpgradeAnalysis(null);
      setUpgradeTargetVersion(targetVersion);
      setShowUpgradeProgress(true);

    } catch (error: any) {
      console.error('Error upgrading cluster:', error);
      alert(`❌ Failed to upgrade cluster: ${error.message}`);
    } finally {
      setUpgradingCluster(false);
    }
  };

  // Handle recommendation approval
  const handleApproveRecommendation = (recommendationId: string, savingsAmount: number) => {
    setApprovedRecommendations(prev => new Set(prev).add(recommendationId));
    setRejectedRecommendations(prev => {
      const updated = new Set(prev);
      updated.delete(recommendationId);
      return updated;
    });
    // Refresh cost data to get updated savings achieved from API
    fetchCostData();
  };

  // Handle recommendation rejection
  const handleRejectRecommendation = (recommendationId: string, savingsAmount: number) => {
    setRejectedRecommendations(prev => new Set(prev).add(recommendationId));
    setApprovedRecommendations(prev => {
      const updated = new Set(prev);
      updated.delete(recommendationId);
      return updated;
    });
    // Refresh cost data to get updated savings achieved from API
    fetchCostData();
  };

  // Handle recommendation acceptance
  const handleAcceptRecommendation = (recommendationId: string, savingsAmount: number) => {
    setApprovedRecommendations(prev => new Set(prev).add(recommendationId));
    // Refresh cost data to get updated savings achieved from API
    fetchCostData();
  };

  // Handle recommendation ignore
  const handleIgnoreRecommendation = (recommendationId: string) => {
    setIgnoredRecommendations(prev => new Set(prev).add(recommendationId));
  };

  // Calculate savings percentage
  const savingsPercentage = totalCurrentSpend > 0
    ? ((totalSavingsPotential / totalCurrentSpend) * 100).toFixed(1)
    : '0';

  const achievedPercentage = totalSavingsPotential > 0
    ? ((savingsAchieved / totalSavingsPotential) * 100).toFixed(1)
    : '0';

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        // Show active dashboard or tab selection interface
        if (activeQueryDashboard) {
          const dashboard = queryDashboards.find(d => d.id === activeQueryDashboard);
          if (dashboard) {
            return (
              <ModernDashboard
                dashboard={dashboard}
                onClose={() => setActiveQueryDashboard(null)}
              />
            );
          }
        }

        // Show dashboard tabs or welcome page
        return queryDashboards.length > 0 ? (
          // QUERY TABS INTERFACE - Choose which dashboard to view
          <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 bg-white p-3 rounded border border-gray-200">
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 mb-1">Query Results</h1>
                  <p className="text-xs text-gray-600">Select a query to view details</p>
                </div>
                <button
                  onClick={() => { queryDashboards.forEach(d => deleteVisualizationFromDB(d.id)); setQueryDashboards([]); }}
                  className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded border border-gray-300"
                >
                  Clear All
                </button>
              </div>

              {/* Query Dashboard Tabs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {queryDashboards.map((dashboard, index) => (
                  <div key={dashboard.id} className="bg-white rounded p-3 border border-gray-200">
                    {/* Dashboard Preview */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">
                          Query {index + 1}
                        </h3>
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          "{dashboard.query}"
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          deleteVisualizationFromDB(dashboard.id);
                          setQueryDashboards(prev => prev.filter(d => d.id !== dashboard.id));
                          if (activeQueryDashboard === dashboard.id) {
                            setActiveQueryDashboard(null);
                          }
                        }}
                        className="text-gray-400 hover:text-gray-600 text-xs"
                      >
                        ×
                      </button>
                    </div>

                    {/* Dashboard Stats */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {dashboard.data.queryType === 'cost' && (
                        <>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              ${dashboard.data.total?.toFixed(2) || '0.00'}
                            </div>
                            <div className="text-xs text-gray-600">Total</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              {dashboard.data.services?.length || 0}
                            </div>
                            <div className="text-xs text-gray-600">Services</div>
                          </div>
                        </>
                      )}
                      {dashboard.data.queryType === 'ec2' && (
                        <>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              {dashboard.data.totalCount || 0}
                            </div>
                            <div className="text-xs text-gray-600">Total Instances</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-blue-700">
                              {dashboard.data.region || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-600">Region</div>
                          </div>
                        </>
                      )}
                      {dashboard.data.queryType === 'ebs' && (
                        <>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              {dashboard.data.totalCount || dashboard.data.volumes?.length || 0}
                            </div>
                            <div className="text-xs text-gray-600">Total Volumes</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-blue-700">
                              {dashboard.data.region || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-600">Region</div>
                          </div>
                        </>
                      )}
                      {dashboard.data.queryType === 'cost_comparison' && (
                        <>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              ${dashboard.data.totalComparison?.period2?.total?.toFixed(2) || '—'}
                            </div>
                            <div className="text-xs text-gray-600 truncate">{dashboard.data.totalComparison?.period2?.name || 'Period 1'}</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              ${dashboard.data.totalComparison?.period1?.total?.toFixed(2) || '—'}
                            </div>
                            <div className="text-xs text-gray-600 truncate">{dashboard.data.totalComparison?.period1?.name || 'Period 2'}</div>
                          </div>
                        </>
                      )}
                      {dashboard.data.queryType === 'cost_multi_account' && (
                        <>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              {dashboard.data.grandTotalFormatted || '—'}
                            </div>
                            <div className="text-xs text-gray-600">Grand Total</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-blue-700">
                              {dashboard.data.accountCount || 0}
                            </div>
                            <div className="text-xs text-gray-600">Accounts</div>
                          </div>
                        </>
                      )}
                      {dashboard.data.queryType === 'infrastructure' && (
                        <>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-gray-900">
                              {dashboard.data.total || 0}
                            </div>
                            <div className="text-xs text-gray-600">Total</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-sm font-bold text-green-700">
                              {dashboard.data.running || 0}
                            </div>
                            <div className="text-xs text-gray-600">Running</div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* View Button */}
                    <button
                      onClick={() => setActiveQueryDashboard(dashboard.id)}
                      className="w-full py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium mb-2"
                    >
                      View Details
                    </button>

                    <div className="text-xs text-gray-500">
                      {dashboard.timestamp.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // WELCOME VIEW - Default when no visualizations
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto">
              {/* Welcome Header */}
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  DevOpsX
                </h1>
                <p className="text-sm text-gray-600 mb-1">Multi-Cloud Infrastructure Management Platform</p>
                <p className="text-xs text-gray-500">Streamline your cloud operations across AWS, GCP, and Azure</p>
              </div>

              {/* Status Indicator */}
              <div className="flex justify-center mb-6">
                <div className="flex items-center space-x-2 bg-white rounded px-4 py-2 border border-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-700">All Systems Operational</span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'infrastructure':
        return <InfrastructureTab />;
      case 'gpus':
        return <GPUsTab />;
      case 'support':
        return <SupportTab />;
      case 'byoip':
        return <BYOIPTab />;
      case 'deployments':
        return (
          <div className="p-2 sm:p-4 space-y-3 sm:space-y-4">
            {/* Deployment Stats Header */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-2 sm:p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-4 h-4 bg-purple-400 rounded-full animate-pulse"></div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">CI/CD Pipeline Overview</div>
                    <div className="text-xs text-gray-600">
                      {deployments ?
                        `${deployments.stats.deploymentsToday} deployments today • ${deployments.stats.successRate}% success rate` :
                        'Loading deployment data...'
                      }
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button className="px-3 py-1.5 bg-white border border-purple-200 text-purple-600 rounded-lg text-xs font-medium hover:bg-purple-50">
                    <FontAwesomeIcon icon={faRocket} className="mr-1" />New Deploy
                  </button>
                  <button className="px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">
                    <FontAwesomeIcon icon={faSync} className="mr-1" />Refresh
                  </button>
                </div>
              </div>
            </div>

            {deployments ? (
              <>
                {/* Deployment Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-2 sm:p-3">
                  <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg sm:text-xl font-bold text-gray-900">{deployments.stats.totalDeployments}</div>
                        <div className="text-xs text-gray-500">Total Deployments</div>
                      </div>
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faRocket} className="text-purple-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-green-600">↗ +23 today</div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg sm:text-xl font-bold text-gray-900">{deployments.stats.successRate}%</div>
                        <div className="text-xs text-gray-500">Success Rate</div>
                      </div>
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-green-600">↗ +2.3% this week</div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg sm:text-xl font-bold text-gray-900">{deployments.stats.avgDeployTime}</div>
                        <div className="text-xs text-gray-500">Avg Deploy Time</div>
                      </div>
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faSpinner} className="text-blue-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-green-600">↓ -1m 23s faster</div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg sm:text-xl font-bold text-gray-900">{deployments.stats.rollbacksThisWeek}</div>
                        <div className="text-xs text-gray-500">Rollbacks This Week</div>
                      </div>
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faUndo} className="text-orange-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-orange-600">↑ +1 from last week</div>
                  </div>
                </div>

                {/* CI/CD Connectors */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm sm:text-base font-semibold mb-3 flex items-center">
                    <FontAwesomeIcon icon={faCode} className="text-blue-500 mr-2" />
                    CI/CD Connectors
                  </h3>
                  <div className="grid grid-cols-5 gap-2 sm:p-3">
                    {deployments.connectors.map((connector: any, index: number) => (
                      <div key={connector.name} className="border border-gray-200 rounded-lg p-2 sm:p-3 text-center">
                        <div className="flex items-center justify-center mb-2">
                          <div className={`w-3 h-3 rounded-full ${connector.status === 'connected' ? 'bg-green-500' : 'bg-red-500'} mr-2`}></div>
                          <span className="font-medium text-gray-900">{connector.name}</span>
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          {connector.jobs && `${connector.jobs} jobs`}
                          {connector.workflows && `${connector.workflows} workflows`}
                          {connector.applications && `${connector.applications} apps`}
                          {connector.rollouts && `${connector.rollouts} rollouts`}
                          {connector.clusters && `${connector.clusters} clusters`}
                        </div>
                        <div className="text-xs text-gray-500">{connector.lastSync}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Deployments */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm sm:text-base font-semibold">Recent Deployments</h3>
                    <div className="flex space-x-2">
                      <select className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                        <option>All Environments</option>
                        <option>Production</option>
                        <option>Staging</option>
                        <option>Development</option>
                      </select>
                      <select className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                        <option>All Services</option>
                        <option>user-service</option>
                        <option>payment-gateway</option>
                        <option>notification-service</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {deployments.recent.map((deployment: any) => (
                      <div key={deployment.id} className={`border rounded-lg p-2 sm:p-3 ${deployment.status === 'success' ? 'border-green-200 bg-green-50' :
                        deployment.status === 'failed' ? 'border-red-200 bg-red-50' :
                          deployment.status === 'in_progress' ? 'border-blue-200 bg-blue-50' :
                            'border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${deployment.status === 'success' ? 'bg-green-100' :
                              deployment.status === 'failed' ? 'bg-red-100' :
                                deployment.status === 'in_progress' ? 'bg-blue-100' :
                                  'bg-gray-100'
                              }`}>
                              <FontAwesomeIcon icon={
                                deployment.status === 'success' ? faCheck :
                                  deployment.status === 'failed' ? faTimes :
                                    deployment.status === 'in_progress' ? faSpinner :
                                      faRocket
                              } className={`${deployment.status === 'success' ? 'text-green-600' :
                                deployment.status === 'failed' ? 'text-red-600' :
                                  deployment.status === 'in_progress' ? 'text-blue-600 animate-spin' :
                                    'text-gray-600'
                                }`} />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {deployment.service} <span className="text-xs text-gray-500">({deployment.version})</span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {deployment.environment} • {deployment.pipeline} • {deployment.deployer}
                              </div>
                              <div className="text-xs text-gray-500">
                                {deployment.timestamp.toLocaleString()} • Duration: {deployment.duration}
                              </div>
                              {deployment.error && (
                                <div className="text-xs text-red-600 mt-1">Error: {deployment.error}</div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {deployment.status === 'in_progress' && (
                              <div className="flex items-center space-x-2">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-blue-500 h-2 rounded-full"
                                    style={{ width: `${deployment.progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-600">{deployment.progress}%</span>
                              </div>
                            )}

                            <div className="flex space-x-1">
                              <button className="px-2 py-1 bg-white border border-gray-300 text-gray-600 rounded text-xs hover:bg-gray-50">
                                <FontAwesomeIcon icon={faEye} className="mr-1" />View
                              </button>
                              {deployment.rollbackAvailable && (
                                <button className="px-2 py-1 bg-white border border-orange-300 text-orange-600 rounded text-xs hover:bg-orange-50">
                                  <FontAwesomeIcon icon={faUndo} className="mr-1" />Rollback
                                </button>
                              )}
                              {deployment.status === 'in_progress' && (
                                <button className="px-2 py-1 bg-white border border-red-300 text-red-600 rounded text-xs hover:bg-red-50">
                                  <FontAwesomeIcon icon={faStop} className="mr-1" />Cancel
                                </button>
                              )}
                              <button className="px-2 py-1 bg-white border border-blue-300 text-blue-600 rounded text-xs hover:bg-blue-50">
                                <FontAwesomeIcon icon={faHistory} className="mr-1" />History
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-3"></div>
                  <div className="text-gray-600">Loading deployment data...</div>
                </div>
              </div>
            )}
          </div>
        );
      case 'security':
        return <SecurityTab externalScanId={securityScanId} openHistoryPanel={securityOpenHistory} onHistoryPanelOpened={() => setSecurityOpenHistory(false)} externalAccountFilter={securityAccountFilter} onAccountFilterApplied={() => setSecurityAccountFilter(null)} />;
      case 'resources':
        return <ResourcesTab />;
      case 'monitoring':
        return <MonitoringTab connectedAccounts={connectedAccounts as any} />;
      case 'costs':
        return <CostAnalysisTab />;
      case 'recommendations':
        return (
          <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
            {/* Recommendations Header */}
            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                  <div>
                    <div className="text-base font-bold text-gray-900">FinOps Cost Optimization</div>
                    <div className="text-xs text-gray-600">
                      Scan connected accounts for savings opportunities
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={recommendationType}
                    onChange={e => {
                      const t = e.target.value as 'recommendation1' | 'recommendation2';
                      setRecommendationType(t);
                      setRecommendationsSubTab(t);
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="recommendation1">AI Insights</option>
                    <option value="recommendation2">Recommendations</option>
                  </select>
                  <button
                    onClick={() => {
                      setAwsScanStatus('scanning');
                      setScanProgress('Starting AWS scan...');
                      if (recommendationType === 'recommendation1') {
                        setV1ScanTrigger(Date.now());
                      } else {
                        setV2ScanTrigger(Date.now());
                      }
                    }}
                    disabled={awsScanStatus === 'scanning'}
                    className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg text-xs font-medium transition-colors flex items-center"
                  >
                    <FontAwesomeIcon icon={faAws} className="mr-1" />
                    {awsScanStatus === 'scanning' ? 'Scanning...' : 'Scan AWS'}
                  </button>
                  <button
                    onClick={scanGCPRecommendations}
                    disabled={gcpScanStatus === 'scanning'}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-xs font-medium transition-colors flex items-center"
                  >
                    <FontAwesomeIcon icon={faGoogle} className="mr-1" />
                    {gcpScanStatus === 'scanning' ? 'Scanning...' : 'Scan GCP'}
                  </button>
                </div>
              </div>

              {/* Scan Progress Indicator */}
              {(awsScanStatus === 'scanning' || gcpScanStatus === 'scanning') && (
                <div className="mt-2 p-2 bg-white rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin">
                      <FontAwesomeIcon icon={faSpinner} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-medium text-gray-900">{scanProgress}</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-blue-600 h-1.5 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Scan Complete Messages */}
              {awsScanStatus === 'complete' && (
                <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                      <span className="text-green-700 font-medium">AWS scan completed successfully!</span>
                    </div>
                    {lastAwsScanId && (
                      <button
                        onClick={() => fetchScanDetails(lastAwsScanId)}
                        disabled={loadingScanDetails}
                        className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors text-xs font-medium flex items-center"
                      >
                        <FontAwesomeIcon icon={faBug} className={`mr-1 ${loadingScanDetails ? 'animate-spin' : ''}`} />
                        {loadingScanDetails ? 'Loading...' : 'View Scan Report'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {gcpScanStatus === 'complete' && (
                <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-2 text-xs">
                    <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                    <span className="text-green-700 font-medium">GCP scan completed successfully!</span>
                  </div>
                </div>
              )}

              {/* Error Messages */}
              {awsScanStatus === 'error' && (
                <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center space-x-2 text-xs">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                    <span className="text-red-700 font-medium">AWS scan failed. Please try again.</span>
                  </div>
                </div>
              )}
              {gcpScanStatus === 'error' && (
                <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center space-x-2 text-xs">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                    <span className="text-red-700 font-medium">GCP scan failed. Please try again.</span>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {/* Total Monthly Spend */}
              <div className="bg-white border border-gray-300 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Total Monthly Spend</div>
                    <div className="text-lg font-bold text-gray-900">
                      ${(totalCurrentSpend / 1000).toFixed(1)}k
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">current month</div>
                  </div>
                  <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                    <FontAwesomeIcon icon={faDollarSign} className="text-gray-600 text-sm" />
                  </div>
                </div>
              </div>

              {/* Total Savings Potential */}
              <div className="bg-white border border-gray-300 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Total Savings Potential</div>
                    <div className="text-lg font-bold text-green-600">
                      ${(totalSavingsPotential / 1000).toFixed(1)}k
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">per month</div>
                  </div>
                  <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                    <FontAwesomeIcon icon={faDollarSign} className="text-green-600 text-sm" />
                  </div>
                </div>
              </div>

              {/* Resources Analyzed */}
              <div className="bg-white border border-gray-300 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Resources Analyzed</div>
                    <div className="text-lg font-bold text-blue-600">{resourcesAnalyzed.toLocaleString()}</div>
                    <div className="text-xs text-gray-500 mt-0.5">across all clouds</div>
                  </div>
                  <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                    <FontAwesomeIcon icon={faServer} className="text-blue-600 text-sm" />
                  </div>
                </div>
              </div>

              {/* Savings % on Current Spend */}
              <div className="bg-white border border-gray-300 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Savings % on Current Spend</div>
                    <div className="text-lg font-bold text-orange-600">{savingsPercentage}%</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      on ${(totalCurrentSpend / 1000).toFixed(0)}k spend
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center">
                    <FontAwesomeIcon icon={faChartLine} className="text-orange-600 text-sm" />
                  </div>
                </div>
              </div>

              {/* Savings Achieved */}
              <div className="bg-white border border-gray-300 rounded p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-600">Savings Achieved</div>
                    <div className="text-lg font-bold text-purple-600">{achievedPercentage}%</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      ${(savingsAchieved / 1000).toFixed(0)}k approved
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                    <FontAwesomeIcon icon={faCheck} className="text-purple-600 text-sm" />
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-Tab Navigation */}
            <div className="bg-white border border-gray-300 rounded">
              <div className="flex border-b border-gray-200 overflow-x-auto">
                {[
                  { id: 'recommendation1', label: 'AI Insights', icon: faLightbulb },
                  { id: 'recommendation2', label: 'Recommendations', icon: faServer },
                  { id: 'preApprovedDiscounts', label: 'Pre-approved Discounts', icon: faDollarSign },
                  { id: 'savingsTracker', label: 'Savings Tracker', icon: faChartLine }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setRecommendationsSubTab(tab.id as any);
                      if (tab.id === 'recommendation1' || tab.id === 'recommendation2') {
                        setRecommendationType(tab.id as 'recommendation1' | 'recommendation2');
                      }
                    }}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${recommendationsSubTab === tab.id
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <FontAwesomeIcon icon={tab.icon} className="mr-2" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Savings Tracker Tab - ROI Dashboard */}
              {recommendationsSubTab === 'savingsTracker' && (
                <div className="p-4">
                  <SavingsDashboard />
                </div>
              )}

              {/* Recommendation 1 Tab - Legacy implementation (from commit a661147) */}
              {recommendationsSubTab === 'recommendation1' && (
                <div className="p-4">
                  <RecommendationsPanelV1
                    onDeployRecommendation={(id) => {
                      setSelectedRecommendationId(id);
                      setDeployModalOpen(true);
                    }}
                    loading={false}
                    costData={null}
                    externalScanTrigger={v1ScanTrigger}
                    onScanProgress={(msg) => setScanProgress(msg)}
                    onScanComplete={async () => {
                      setV1ScanTrigger(0);
                      try {
                        const token = localStorage.getItem('auth_token');
                        await fetch(`${API_BASE_URL}/api/savings/scan-and-calculate`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                          body: JSON.stringify({})
                        });
                      } catch (e) { console.warn('scan-and-calculate failed:', e); }
                      await fetchCostData();
                      setAwsScanStatus('complete');
                    }}
                  />
                </div>
              )}

              {/* Recommendation 2 Tab - New implementation (current) */}
              {recommendationsSubTab === 'recommendation2' && (
                <div className="p-4">
                  <RecommendationsPanel
                    onDeployRecommendation={(id) => {
                      setSelectedRecommendationId(id);
                      setDeployModalOpen(true);
                    }}
                    loading={false}
                    costData={null}
                    externalScanTrigger={v2ScanTrigger}
                    onScanProgress={(msg) => setScanProgress(msg)}
                    onBreakdownLoad={(savings, findings) => {
                      setTotalSavingsPotential(savings);
                      setResourcesAnalyzed(findings);
                    }}
                    onScanComplete={async () => {
                      setV2ScanTrigger(0);
                      try {
                        const token = localStorage.getItem('auth_token');
                        await fetch(`${API_BASE_URL}/api/savings/scan-and-calculate`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                          body: JSON.stringify({})
                        });
                      } catch (e) { console.warn('scan-and-calculate failed:', e); }
                      await fetchCostData();
                      setAwsScanStatus('complete');
                    }}
                  />
                </div>
              )}

              {/* Pre-approved Discounts Tab Content */}
              {recommendationsSubTab === 'preApprovedDiscounts' && (
                <PreApprovedDiscounts />
              )}

              {/* OLD HARDCODED TABS - HIDDEN */}
              <div className="hidden">
                {recommendationsSubTab === 'recommendation1' && false && (
                  <div className="p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center">
                      <FontAwesomeIcon icon={faServer} className="text-blue-500 mr-2" />
                      Compute Optimization Recommendations
                    </h3>
                    <div className="space-y-2">
                      {[
                        {
                          id: 'compute-1',
                          provider: 'AWS',
                          title: 'Migrate 45 On-Demand EC2 instances to Spot Instances',
                          currentCost: 8640,
                          savings: 6048,
                          savingsPercent: 70,
                          priority: 'High',
                          description: '45 development/testing EC2 instances running in us-east-1 can be migrated to Spot Instances',
                          risks: 'Spot instances may be interrupted. Ensure workloads can handle interruptions.',
                          complexity: 'Medium'
                        },
                        {
                          id: 'compute-2',
                          provider: 'AWS',
                          title: 'Upgrade Reserved Instances from 1-year to 3-year commitment',
                          currentCost: 2160,
                          savings: 648,
                          savingsPercent: 30,
                          priority: 'Medium',
                          description: '12 Reserved Instances eligible for extended commitment',
                          risks: 'Longer commitment period. Ensure capacity needs are stable.',
                          complexity: 'Low'
                        },
                        {
                          id: 'compute-3',
                          provider: 'GCP',
                          title: 'Convert 32 standard VMs to Preemptible VMs',
                          currentCost: 5440,
                          savings: 4352,
                          savingsPercent: 80,
                          priority: 'High',
                          description: 'Non-production workloads in europe-west1 region',
                          risks: 'Preemptible VMs can be terminated by GCP at any time.',
                          complexity: 'Medium'
                        },
                        {
                          id: 'compute-4',
                          provider: 'GCP',
                          title: 'Apply 1-year CUDs to 18 standard instances',
                          currentCost: 3240,
                          savings: 1620,
                          savingsPercent: 50,
                          priority: 'Medium',
                          description: 'Production workloads eligible for Committed Use Discounts',
                          risks: '1-year commitment. Ensure workload stability.',
                          complexity: 'Low'
                        },
                        {
                          id: 'compute-5',
                          provider: 'AWS',
                          title: 'Rightsizing: Downsize 23 underutilized EC2 instances',
                          currentCost: 4800,
                          savings: 1920,
                          savingsPercent: 40,
                          priority: 'Medium',
                          description: 'Instances running at <20% CPU utilization can be downsized',
                          risks: 'Monitor performance after downsizing.',
                          complexity: 'Low'
                        }
                      ].filter(rec => !ignoredRecommendations.has(rec.id)).map((rec, index) => (
                        <div key={rec.id} className="border border-gray-200 rounded p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <FontAwesomeIcon
                                    icon={rec.provider === 'AWS' ? faAws : faGoogle}
                                    className={rec.provider === 'AWS' ? 'text-orange-600' : 'text-blue-600'}
                                  />
                                  <span className="text-sm font-semibold text-gray-900">{rec.title}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${rec.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                    {rec.priority}
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAcceptRecommendation(rec.id, rec.savings)}
                                    className="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleIgnoreRecommendation(rec.id)}
                                    className="px-3 py-1 text-xs font-medium bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-2">{rec.description}</div>
                              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                <div className="bg-gray-50 rounded p-2">
                                  <span className="text-gray-600">Current Cost: </span>
                                  <span className="font-semibold text-gray-900">${rec.currentCost.toLocaleString()}/mo</span>
                                </div>
                                <div className="bg-green-50 rounded p-2">
                                  <span className="text-gray-600">Potential Savings: </span>
                                  <span className="font-semibold text-green-600">
                                    ${rec.savings.toLocaleString()}/mo ({rec.savingsPercent}%)
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-1">
                                <span className="font-medium">Complexity:</span> {rec.complexity} |
                                <span className="font-medium ml-2">Risks:</span> {rec.risks}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Transfer Tab Content */}
                {recommendationsSubTab === 'dataTransfer' && (
                  <div className="p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center">
                      <FontAwesomeIcon icon={faNetworkWired} className="text-purple-500 mr-2" />
                      Data Transfer Cost Optimization
                    </h3>
                    <div className="space-y-2">
                      {[
                        {
                          id: 'dt-1',
                          provider: 'AWS',
                          title: 'Reduce inter-region data transfer using VPC peering',
                          currentCost: 2400,
                          savings: 1680,
                          savingsPercent: 70,
                          priority: 'High',
                          description: 'Replace NAT Gateway with VPC Peering for inter-region communication',
                          risks: 'Requires VPC architecture changes.',
                          complexity: 'High'
                        },
                        {
                          id: 'dt-2',
                          provider: 'GCP',
                          title: 'Optimize egress traffic with Cloud CDN',
                          currentCost: 3200,
                          savings: 1600,
                          savingsPercent: 50,
                          priority: 'High',
                          description: 'Route static content through Cloud CDN instead of direct egress',
                          risks: 'Cache invalidation needs to be managed.',
                          complexity: 'Medium'
                        },
                        {
                          id: 'dt-3',
                          provider: 'AWS',
                          title: 'Use S3 Transfer Acceleration for uploads',
                          currentCost: 800,
                          savings: 240,
                          savingsPercent: 30,
                          priority: 'Low',
                          description: 'Optimize large file uploads from APAC region',
                          risks: 'May increase upload costs in some cases.',
                          complexity: 'Low'
                        }
                      ].filter(rec => !ignoredRecommendations.has(rec.id)).map((rec) => (
                        <div key={rec.id} className="border border-gray-200 rounded p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <FontAwesomeIcon
                                    icon={rec.provider === 'AWS' ? faAws : faGoogle}
                                    className={rec.provider === 'AWS' ? 'text-orange-600' : 'text-blue-600'}
                                  />
                                  <span className="text-sm font-semibold text-gray-900">{rec.title}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${rec.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                    {rec.priority}
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAcceptRecommendation(rec.id, rec.savings)}
                                    className="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleIgnoreRecommendation(rec.id)}
                                    className="px-3 py-1 text-xs font-medium bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-2">{rec.description}</div>
                              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                <div className="bg-gray-50 rounded p-2">
                                  <span className="text-gray-600">Current Cost: </span>
                                  <span className="font-semibold text-gray-900">${rec.currentCost.toLocaleString()}/mo</span>
                                </div>
                                <div className="bg-green-50 rounded p-2">
                                  <span className="text-gray-600">Potential Savings: </span>
                                  <span className="font-semibold text-green-600">
                                    ${rec.savings.toLocaleString()}/mo ({rec.savingsPercent}%)
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-1">
                                <span className="font-medium">Complexity:</span> {rec.complexity} |
                                <span className="font-medium ml-2">Risks:</span> {rec.risks}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Storage Tab Content */}
                {recommendationsSubTab === 'storage' && (
                  <div className="p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center">
                      <FontAwesomeIcon icon={faDatabase} className="text-indigo-500 mr-2" />
                      Storage Cost Optimization
                    </h3>
                    <div className="space-y-2">
                      {[
                        {
                          id: 'storage-1',
                          provider: 'AWS',
                          title: 'Migrate infrequently accessed S3 data to Glacier',
                          currentCost: 3600,
                          savings: 2880,
                          savingsPercent: 80,
                          priority: 'High',
                          description: '450 GB of S3 data accessed <1 time per month',
                          risks: 'Retrieval costs and time increase.',
                          complexity: 'Low'
                        },
                        {
                          id: 'storage-2',
                          provider: 'GCP',
                          title: 'Switch from Standard to Nearline storage class',
                          currentCost: 2100,
                          savings: 1260,
                          savingsPercent: 60,
                          priority: 'High',
                          description: 'Backup data accessed quarterly',
                          risks: 'Minimum 30-day storage duration.',
                          complexity: 'Low'
                        },
                        {
                          id: 'storage-3',
                          provider: 'AWS',
                          title: 'Delete unattached EBS volumes',
                          currentCost: 840,
                          savings: 840,
                          savingsPercent: 100,
                          priority: 'High',
                          description: '28 EBS volumes not attached to any instance',
                          risks: 'Ensure volumes are not needed before deletion.',
                          complexity: 'Low'
                        },
                        {
                          id: 'storage-4',
                          provider: 'AWS',
                          title: 'Enable S3 Intelligent-Tiering',
                          currentCost: 1800,
                          savings: 540,
                          savingsPercent: 30,
                          priority: 'Medium',
                          description: 'Automatic storage class optimization',
                          risks: 'Small monitoring and automation fee.',
                          complexity: 'Low'
                        }
                      ].filter(rec => !ignoredRecommendations.has(rec.id)).map((rec) => (
                        <div key={rec.id} className="border border-gray-200 rounded p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <FontAwesomeIcon
                                    icon={rec.provider === 'AWS' ? faAws : faGoogle}
                                    className={rec.provider === 'AWS' ? 'text-orange-600' : 'text-blue-600'}
                                  />
                                  <span className="text-sm font-semibold text-gray-900">{rec.title}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${rec.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                    {rec.priority}
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAcceptRecommendation(rec.id, rec.savings)}
                                    className="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleIgnoreRecommendation(rec.id)}
                                    className="px-3 py-1 text-xs font-medium bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-2">{rec.description}</div>
                              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                <div className="bg-gray-50 rounded p-2">
                                  <span className="text-gray-600">Current Cost: </span>
                                  <span className="font-semibold text-gray-900">${rec.currentCost.toLocaleString()}/mo</span>
                                </div>
                                <div className="bg-green-50 rounded p-2">
                                  <span className="text-gray-600">Potential Savings: </span>
                                  <span className="font-semibold text-green-600">
                                    ${rec.savings.toLocaleString()}/mo ({rec.savingsPercent}%)
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-1">
                                <span className="font-medium">Complexity:</span> {rec.complexity} |
                                <span className="font-medium ml-2">Risks:</span> {rec.risks}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* GPU Tab Content */}
                {recommendationsSubTab === 'gpu' && (
                  <div className="p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center">
                      <FontAwesomeIcon icon={faServer} className="text-green-500 mr-2" />
                      GPU Instance Optimization
                    </h3>
                    <div className="space-y-2">
                      {[
                        {
                          id: 'gpu-1',
                          provider: 'AWS',
                          title: 'Migrate p3.2xlarge instances to Spot for ML training',
                          currentCost: 12960,
                          savings: 9072,
                          savingsPercent: 70,
                          priority: 'High',
                          description: '8 p3.2xlarge instances used for non-critical ML training workloads',
                          risks: 'Training jobs may be interrupted. Implement checkpointing.',
                          complexity: 'Medium'
                        },
                        {
                          id: 'gpu-2',
                          provider: 'AWS',
                          title: 'Downgrade g4dn.xlarge to g4dn.medium for inference',
                          currentCost: 3240,
                          savings: 1620,
                          savingsPercent: 50,
                          priority: 'Medium',
                          description: '12 GPU instances with <30% utilization for inference',
                          risks: 'Monitor inference latency after downgrade.',
                          complexity: 'Low'
                        },
                        {
                          id: 'gpu-3',
                          provider: 'GCP',
                          title: 'Use Preemptible GPUs for development workloads',
                          currentCost: 8640,
                          savings: 6912,
                          savingsPercent: 80,
                          priority: 'High',
                          description: '16 NVIDIA T4 GPUs running in development environments',
                          risks: 'Preemptible instances can be terminated anytime.',
                          complexity: 'Medium'
                        },
                        {
                          id: 'gpu-4',
                          provider: 'AWS',
                          title: 'Apply Reserved Instances to p4d.24xlarge',
                          currentCost: 16200,
                          savings: 8100,
                          savingsPercent: 50,
                          priority: 'High',
                          description: '3 p4d instances with consistent 24/7 usage for production ML',
                          risks: '1 or 3-year commitment required.',
                          complexity: 'Low'
                        },
                        {
                          id: 'gpu-5',
                          provider: 'GCP',
                          title: 'Schedule GPU instances to auto-stop after hours',
                          currentCost: 5400,
                          savings: 2700,
                          savingsPercent: 50,
                          priority: 'Medium',
                          description: 'GPU instances running 24/7 but only used during business hours',
                          risks: 'Need to implement automated scheduling.',
                          complexity: 'Low'
                        }
                      ].filter(rec => !ignoredRecommendations.has(rec.id)).map((rec) => (
                        <div key={rec.id} className="border border-gray-200 rounded p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <FontAwesomeIcon
                                    icon={rec.provider === 'AWS' ? faAws : faGoogle}
                                    className={rec.provider === 'AWS' ? 'text-orange-600' : 'text-blue-600'}
                                  />
                                  <span className="text-sm font-semibold text-gray-900">{rec.title}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${rec.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                    {rec.priority}
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAcceptRecommendation(rec.id, rec.savings)}
                                    className="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleIgnoreRecommendation(rec.id)}
                                    className="px-3 py-1 text-xs font-medium bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                  >
                                    Ignore
                                  </button>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-2">{rec.description}</div>
                              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                <div className="bg-gray-50 rounded p-2">
                                  <span className="text-gray-600">Current Cost: </span>
                                  <span className="font-semibold text-gray-900">${rec.currentCost.toLocaleString()}/mo</span>
                                </div>
                                <div className="bg-green-50 rounded p-2">
                                  <span className="text-gray-600">Potential Savings: </span>
                                  <span className="font-semibold text-green-600">
                                    ${rec.savings.toLocaleString()}/mo ({rec.savingsPercent}%)
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mb-1">
                                <span className="font-medium">Complexity:</span> {rec.complexity} |
                                <span className="font-medium ml-2">Risks:</span> {rec.risks}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* END OLD HARDCODED TABS */}
            </div>

            {/* Old sections removed - replaced by sub-tabs above */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 hidden">
              <h3 className="text-sm font-semibold mb-3 flex items-center">
                <FontAwesomeIcon icon={faDollarSign} className="text-green-500 mr-2" />
                Discounted vs Non-Discounted Resources
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {[
                  { category: 'Compute', discounted: 45, nonDiscounted: 55, savings: '$3,200', icon: faServer },
                  { category: 'Storage', discounted: 62, nonDiscounted: 38, savings: '$1,800', icon: faDatabase },
                  { category: 'Data Transfer', discounted: 28, nonDiscounted: 72, savings: '$2,400', icon: faNetworkWired },
                  { category: 'CDN', discounted: 35, nonDiscounted: 65, savings: '$1,100', icon: faCloud }
                ].map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">{item.category}</div>
                      <FontAwesomeIcon icon={item.icon} className="text-gray-400" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-green-600">Discounted</span>
                        <span className="font-semibold text-green-600">{item.discounted}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${item.discounted}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-red-600">Non-Discounted</span>
                        <span className="font-semibold text-red-600">{item.nonDiscounted}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-red-500 h-2 rounded-full"
                          style={{ width: `${item.nonDiscounted}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-center mt-2 pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Potential Savings: </span>
                        <span className="font-semibold text-green-600">{item.savings}/mo</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Scan results now display in the Cost Analysis tab -> RecommendationsPanel */}

            {/* Deploy Recommendation Modal */}
            <DeployRecommendationModal
              isOpen={deployModalOpen}
              onClose={() => {
                setDeployModalOpen(false);
                setSelectedRecommendationId(null);
              }}
              recommendationId={selectedRecommendationId}
              onDeploymentComplete={(success) => {
                console.log('Deployment completed:', success ? 'success' : 'failed');
                // Optionally refresh recommendations or show notification
              }}
            />
          </div>
        );
      case 'kubernetes':
        // Check current navigation view
        const view = navigationState.getCurrentView();

        // Render ClusterDetailsPage for details view
        if (view.type === 'details' && view.clusterId) {
          return (
            <ClusterDetailsPage
              clusterId={view.clusterId}
              clusterName={view.clusterName!}
              authToken={authToken}
              onBack={() => navigationState.goToList()}
              onUpgrade={() => navigationState.goToUpgradeWizard(view.clusterId!, view.clusterName!)}
            />
          );
        }

        // Render ClusterUpgradePage for upgrade views
        if ((view.type === 'upgrade-wizard' || view.type === 'upgrade-progress') && view.clusterId) {
          return (
            <ClusterUpgradePage
              clusterId={view.clusterId}
              clusterName={view.clusterName!}
              authToken={authToken}
              onBack={() => navigationState.goToClusterDetails(view.clusterId!, view.clusterName!)}
            />
          );
        }

        // Default: Render cluster list
        return (
          <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
            {/* Kubernetes Header */}
            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                  <div>
                    <div className="text-base font-bold text-gray-900">Kubernetes Clusters</div>
                    <div className="text-xs text-gray-600">
                      Manage your EKS and GKE clusters
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsAccountsModalOpen(true);
                    setSelectedAccountType('eks');
                  }}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium flex items-center"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  Add Cluster
                </button>
              </div>
            </div>

            {/* Loading State */}
            {loadingClusters && (
              <div className="bg-white border border-gray-300 rounded p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4 mx-auto"></div>
                  <p className="text-gray-600 text-sm">Loading Kubernetes clusters...</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loadingClusters && kubernetesClusters.length === 0 && (
              <div className="bg-white border border-gray-300 rounded p-4">
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faCloud} className="text-gray-300 text-5xl mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Kubernetes Clusters</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Connect your EKS and GKE clusters to view cluster information and quick overview
                  </p>
                  <button
                    onClick={() => {
                      setIsAccountsModalOpen(true);
                      setSelectedAccountType('eks');
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Connect Your First Cluster
                  </button>
                </div>
              </div>
            )}

            {/* Cluster List */}
            {!loadingClusters && kubernetesClusters.length > 0 && (
              <div className="space-y-2">
                {kubernetesClusters.map((cluster: any) => {
                  const version = cluster.config?.version;
                  const deprecatedVersions = ['1.21', '1.22', '1.23', '1.24', '1.25'];
                  const isDeprecated = version && deprecatedVersions.includes(version);

                  return (
                    <div key={cluster.id} className={`bg-white border rounded overflow-hidden ${isDeprecated ? 'border-red-300' : 'border-gray-300'
                      }`}>
                      {/* Deprecated Warning Banner */}
                      {isDeprecated && (
                        <div className="bg-red-50 border-b border-red-200 px-4 py-2">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2" />
                            <span className="text-sm font-semibold text-red-800">Deprecated Version {version} - Upgrade Required</span>
                          </div>
                        </div>
                      )}

                      {/* Main Cluster Row */}
                      <div className="p-3">
                        <div className="flex items-center justify-between">
                          {/* Left Section - Cluster Identity */}
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="w-10 h-10 bg-blue-600 rounded flex items-center justify-center flex-shrink-0">
                              <FontAwesomeIcon icon={faCloud} className="text-white text-lg" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="font-bold text-gray-900 text-base">{cluster.name}</h3>
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                                  {cluster.type?.toUpperCase() || 'EKS'}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cluster.status === 'active'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                                  }`}>
                                  {cluster.status || 'active'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span className="font-mono">{cluster.clusterName}</span>
                                <span>•</span>
                                <span>{cluster.region}</span>
                                {cluster.config?.version && (
                                  <>
                                    <span>•</span>
                                    <div className="flex items-center space-x-1">
                                      <span className="font-semibold text-gray-900">v{cluster.config.version}</span>
                                      {(() => {
                                        const version = cluster.config.version;
                                        const deprecatedVersions = ['1.21', '1.22', '1.23', '1.24', '1.25'];
                                        const isDeprecated = deprecatedVersions.includes(version);

                                        if (isDeprecated) {
                                          return (
                                            <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                                              DEPRECATED
                                            </span>
                                          );
                                        }

                                        const versionNum = parseFloat(version);
                                        if (versionNum < 1.27 && versionNum >= 1.26) {
                                          return (
                                            <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-700 rounded-full">
                                              Upgrade Soon
                                            </span>
                                          );
                                        }

                                        return null;
                                      })()}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right Section - Actions */}
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => handleSyncCluster(cluster.id)}
                              disabled={syncingCluster === cluster.id}
                              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                              title="Sync cluster data"
                            >
                              <FontAwesomeIcon icon={faSync} className={`mr-2 ${syncingCluster === cluster.id ? 'animate-spin' : ''}`} />
                              {syncingCluster === cluster.id ? 'Syncing...' : 'Sync'}
                            </button>
                            <button
                              onClick={() => navigationState.goToClusterDetails(cluster.id, cluster.name)}
                              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
                            >
                              <FontAwesomeIcon icon={faEye} className="mr-2" />
                              View Details
                            </button>
                            <button
                              onClick={() => handleDeleteCluster(cluster)}
                              className="px-4 py-2 text-sm bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 rounded-lg transition-colors border border-red-200 flex items-center"
                              title="Delete cluster"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>

                        {/* Additional Info Row */}
                        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-4">
                            <span className="font-mono">{cluster.accountId}</span>
                            {cluster.lastSync && (
                              <>
                                <span>•</span>
                                <span>Last synced: {new Date(cluster.lastSync).toLocaleString()}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Authentication Guard - Show loading state while checking authentication
  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4 mx-auto"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Signup Page
  if (!isAuthenticated && showSignup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <FontAwesomeIcon icon={faCloud} className="text-blue-600 text-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">DevOpsX</h1>
            <p className="text-gray-600">Create your account</p>
          </div>

          {authError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {authError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                value={signupForm.full_name}
                onChange={(e) => setSignupForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter full name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Organisation Name</label>
              <input
                type="text"
                value={signupForm.organisation}
                onChange={(e) => setSignupForm(prev => ({ ...prev, organisation: e.target.value }))}
                placeholder="Enter organisation name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={signupForm.email}
                onChange={(e) => setSignupForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                value={signupForm.number}
                onChange={(e) => setSignupForm(prev => ({ ...prev, number: e.target.value }))}
                placeholder="Enter phone number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={signupForm.password}
                onChange={(e) => setSignupForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Create a password (min 6 chars)"
                onKeyPress={(e) => e.key === 'Enter' && handleSignup()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={handleSignup}
              disabled={!signupForm.full_name || !signupForm.organisation || !signupForm.email || !signupForm.password}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Create Account
            </button>
            <div className="text-center">
              <button onClick={() => setShowSignup(false)} className="text-sm text-blue-600 hover:text-blue-700">
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authentication Guard - Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <FontAwesomeIcon icon={faCloud} className="text-blue-600 text-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">DevOpsX</h1>
            <p className="text-gray-600">Multi-Cloud Infrastructure Management</p>
            <p className="text-sm text-gray-500 mt-2">Please sign in to access your dashboard</p>
          </div>

          {authError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {authError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter your password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            </div>

            <button
              onClick={handleLogin}
              disabled={isAuthLoading || !loginForm.email || !loginForm.password}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {isAuthLoading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="text-center">
              <button
                onClick={() => setShowSignup(true)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Don't have an account? Sign up
              </button>
            </div>

            {/* Test Credentials Display */}
            {/* <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="text-xs font-medium text-yellow-800 mb-2">Test Credentials:</div>
              <div className="text-xs text-yellow-700 space-y-1">
                <div><strong>Email:</strong> testuser@devopsx.com</div>
                <div><strong>Password:</strong> testpassword123</div>
              </div>
              <div className="text-xs text-yellow-600 mt-2 italic">
                (These are pre-filled for convenience)
              </div>
            </div> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      {/* Post-payment toast (Stripe Checkout return) */}
      {paymentToast && (
        <div
          style={{
            position: 'fixed',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '480px',
            padding: '14px 18px',
            borderRadius: '12px',
            background:
              paymentToast.state === 'success'
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : paymentToast.state === 'failed'
                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                : paymentToast.state === 'cancelled'
                ? 'linear-gradient(135deg, #64748b, #475569)'
                : 'linear-gradient(135deg, #0ea5e9, #0369a1)',
            color: 'white',
            boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideDown 0.35s ease-out',
          }}
        >
          {/* Spinner while activating, checkmark when done, X on failure */}
          <div style={{ flexShrink: 0, width: '32px', height: '32px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.2)', borderRadius: '50%' }}>
            {paymentToast.state === 'activating' ? (
              <div style={{
                width: '18px', height: '18px',
                border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
            ) : paymentToast.state === 'success' ? (
              <span style={{ fontSize: '18px', fontWeight: 700 }}>✓</span>
            ) : (
              <span style={{ fontSize: '18px', fontWeight: 700 }}>✕</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.2 }}>
              {paymentToast.title}
            </div>
            {paymentToast.detail && (
              <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                {paymentToast.detail}
              </div>
            )}
          </div>
          {paymentToast.state !== 'activating' && (
            <button
              onClick={() => setPaymentToast(null)}
              style={{
                flexShrink: 0, background: 'transparent', border: 'none',
                color: 'white', cursor: 'pointer', fontSize: '16px', opacity: 0.8,
                padding: '4px 8px',
              }}
            >
              ✕
            </button>
          )}
          <style>{`
            @keyframes spin       { to { transform: rotate(360deg); } }
            @keyframes slideDown  { from { transform: translate(-50%, -120%); opacity: 0; }
                                     to { transform: translate(-50%, 0);    opacity: 1; } }
          `}</style>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Left Panel - Chat Interface */}
      <div className={`${isMobileMenuOpen ? 'fixed inset-0 z-50' : 'hidden'
        } lg:relative lg:flex w-full ${isChatCollapsed ? 'lg:w-12' : 'lg:w-[30%] lg:min-w-[280px]'} bg-dark-bg text-white flex-col border-r border-dark-border transition-all duration-300 ease-in-out flex-shrink-0`}>
        {/* Header */}
        <div className={`${isChatCollapsed ? 'p-2 border-none' : 'p-2 sm:p-3 border-b border-dark-border'}`}>
          <div className={`flex items-center ${isChatCollapsed ? 'justify-center' : 'justify-between mb-3'}`}>
            {!isChatCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-accent-blue to-accent-green rounded-xl flex items-center justify-center">
                  <FontAwesomeIcon icon={faCloud} className="text-white" />
                </div>
                <div>
                  <h1 className="text-sm sm:text-base font-semibold" style={{ fontFamily: "'Quicksand', 'Comfortaa', 'Nunito', sans-serif", fontWeight: 600, letterSpacing: '0.5px' }}>devopsX</h1>
                  <p className="text-xs text-gray-400">Multi-Cloud Orchestration Platform</p>
                </div>
              </div>
            )}
            {/* Desktop Collapse Button */}
            <button
              onClick={() => setIsChatCollapsed(!isChatCollapsed)}
              className={`hidden lg:flex ${isChatCollapsed ? 'p-1.5' : 'p-2'} text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors`}
              title={isChatCollapsed ? "Expand chat" : "Collapse chat"}
            >
              <svg className={`${isChatCollapsed ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Mobile Close Button */}
            {!isChatCollapsed && (
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                ✕
              </button>
            )}
          </div>

          {!isChatCollapsed && (
            <div className="bg-dark-panel rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-300">Connected Accounts</span>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <button
                    onClick={() => setShowConversationList(!showConversationList)}
                    className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                    title="Conversation History"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  {/* Conversation Dropdown */}
                  {showConversationList && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowConversationList(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-64 max-h-80 overflow-y-auto bg-dark-panel border border-dark-border rounded-lg shadow-xl z-50">
                        <div className="p-2 border-b border-dark-border flex items-center justify-between">
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide px-2 py-1">
                            Conversations ({conversations.length})
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              createNewConversation();
                              setShowConversationList(false);
                            }}
                            className="px-2 py-1 text-[10px] font-medium text-yellow-400 hover:text-yellow-300 hover:bg-gray-700 rounded transition-colors flex items-center space-x-1"
                            title="New Chat"
                          >
                            <FontAwesomeIcon icon={faPlus} className="text-[9px]" />
                            <span>New Chat</span>
                          </button>
                        </div>
                        {conversations.map((conv) => (
                          <div
                            key={conv.id}
                            className={`px-3 py-2 hover:bg-gray-700 transition-colors cursor-pointer border-b border-dark-border/30 ${
                              conv.id === activeConversationId ? 'bg-gray-700/70' : ''
                            }`}
                            onClick={() => {
                              switchConversation(conv.id);
                              setShowConversationList(false);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-200 truncate">{conv.title}</div>
                                <div className="text-[10px] text-gray-400 mt-0.5">
                                  {conv.messagesLoaded ? conv.messages.length : (conv.messageCount || 0)} msgs • {new Date(conv.updatedAt).toLocaleDateString()}
                                </div>
                              </div>
                              {conversations.length > 1 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Delete this conversation?')) {
                                      deleteConversation(conv.id);
                                    }
                                  }}
                                  className="ml-2 p-1 text-gray-500 hover:text-red-400 hover:bg-gray-600 rounded transition-colors"
                                  title="Delete conversation"
                                >
                                  <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setIsAccountsModalOpen(true)}
                  className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Manage Accounts"
                >
                  <FontAwesomeIcon icon={faCog} className="text-xs" />
                </button>
                {/* {isAuthenticated && (
                  <button
                    onClick={handleLogout}
                    className="p-1 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
                    title="Logout"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )} */}
              </div>
            </div>
            <div className="flex space-x-2">
              <div className="flex items-center space-x-1.5 bg-orange-600 rounded-md px-2 py-1">
                <FontAwesomeIcon icon={faAws} className="text-xs" />
                <span className="text-xs">{accounts.aws}</span>
              </div>
              <div className="flex items-center space-x-1.5 bg-blue-600 rounded-md px-2 py-1">
                <FontAwesomeIcon icon={faGoogle} className="text-xs" />
                <span className="text-xs">{accounts.gcp}</span>
              </div>
              <div className="flex items-center space-x-1.5 bg-gray-700 rounded-md px-2 py-1">
                <FontAwesomeIcon icon={faGithub} className="text-xs" />
                <span className="text-xs">{accounts.github}</span>
              </div>
              {accounts.jenkins > 0 && (
                <div className="flex items-center space-x-1.5 bg-purple-600 rounded-md px-2 py-1">
                  <FontAwesomeIcon icon={faJenkins} className="text-xs" />
                  <span className="text-xs">{accounts.jenkins}</span>
                </div>
              )}
            </div>
          </div>
          )}
        </div>


        {/* Messages */}
        {!isChatCollapsed && (
        <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-4">
          {/* Show execution plan for cost queries during streaming */}
          {isLoading && executionPlan && executionPlan.some((step: any) => step.status !== 'pending') && (
            <div className="mb-4">
              <ExecutionPlan steps={executionPlan} />
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id} className={`chat-bubble ${message.type === 'user' ? 'flex justify-end' : ''}`}>
              {message.type === 'user' ? (
                <div className="max-w-[85%] bg-accent-blue rounded-2xl rounded-br-md p-2 sm:p-3">
                  <div className="text-sm">{message.content}</div>
                  <div className="text-xs text-blue-100 mt-2 flex items-center justify-between">
                    <span>{message.timestamp}</span>
                    <div className="flex space-x-1">
                      <button className="hover:text-white"><FontAwesomeIcon icon={faEdit} className="text-xs" /></button>
                      <button className="hover:text-white"><FontAwesomeIcon icon={faCopy} className="text-xs" /></button>
                      <button className="hover:text-white"><FontAwesomeIcon icon={faBookmark} className="text-xs" /></button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    {message.clarificationRequest && !isLoading ? (
                      // Render inline clarification form
                      <div className="bg-dark-panel rounded-2xl rounded-tl-md p-4 border border-yellow-500/30">
                        <div className="text-sm text-gray-200 mb-4">
                          {message.content}
                        </div>

                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          const formData = new FormData(e.currentTarget);
                          const clarifications: string[] = [];

                          // Collect all clarification values
                          message.clarificationRequest!.clarifications.forEach((_, index) => {
                            const value = formData.get(`clarification_${index}`) as string;
                            if (value) {
                              clarifications.push(value);
                            }
                          });

                          // Save refs before mutating — clarificationRequest will be cleared
                          const originalQuery = message.clarificationRequest!.originalQuery;
                          const sessionId = message.clarificationRequest!.sessionId;

                          // Remove the clarification form immediately so button disappears
                          // Replace the entire message — no clarificationRequest, no structured, just text
                          message.clarificationRequest = undefined;
                          setMessages(prev => prev.map(msg =>
                            msg.id === message.id
                              ? { ...msg, type: 'assistant' as const, clarificationRequest: undefined, structured: undefined, content: `Account selected: ${clarifications.join(', ')}` }
                              : msg
                          ));

                          // Build enriched query with clarifications
                          let enrichedQuery = originalQuery;
                          clarifications.forEach((value) => {
                            enrichedQuery += ` ${value}`;
                          });

                          // Add a user message showing the filled parameters
                          const userClarificationMessage: Message = {
                            id: (Date.now()).toString(),
                            type: 'user',
                            content: clarifications.join(', '),
                            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          };

                          setMessages(prev => [...prev, userClarificationMessage]);

                          // Create a NEW assistant message for the execution (don't transform the clarification message)
                          const newAssistantMessageId = (Date.now() + 1).toString();
                          const taskBreakdown = analyzeIntentAndCreateTasks(enrichedQuery);

                          const newAssistantMessage: Message = {
                            id: newAssistantMessageId,
                            type: 'assistant',
                            content: '', // Start with empty content, will be filled by SSE streaming
                            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                            tokens: 0,
                            metadata: {},
                            structured: {
                              summary: taskBreakdown ? taskBreakdown.intent : 'Processing...',
                              sections: [
                                {
                                  title: '📋 Execution Plan',
                                  status: 'in_progress',
                                  items: [
                                    { text: 'Analyzing query and creating execution plan', status: 'pending' },
                                    { text: 'Connecting to AWS services', status: 'pending' }
                                  ]
                                }
                              ],
                              actions: [
                                { label: 'Cancel Execution', type: 'emergency', icon: 'stop' }
                              ]
                            }
                          };

                          setMessages(prev => [...prev, newAssistantMessage]);

                          // Execute the enriched query with session ID to resume the original request
                          setIsLoading(true);

                          // Make direct API call with the sessionId from clarification
                          const token = localStorage.getItem('auth_token');
                          const headers: any = {
                            'Content-Type': 'application/json',
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                          };

                          if (token) {
                            headers['Authorization'] = `Bearer ${token}`;
                          }

                          const requestBody = {
                            message: enrichedQuery,
                            sessionId: sessionId, // Pass back session ID (saved before clearing clarificationRequest)
                            options: { autoExecute, dryRun, multiAccount },
                            accountId: selectedAccountId
                          };

                          try {
                            const controller = new AbortController();
                            setAbortController(controller);

                            const response = await fetch(`${API_BASE_URL}/api/chat/routerx?t=${Date.now()}`, {
                              method: 'POST',
                              headers: headers,
                              body: JSON.stringify(requestBody),
                              signal: controller.signal
                            });

                            if (!response.ok) {
                              throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            const reader = response.body?.getReader();
                            const decoder = new TextDecoder();

                            if (reader) {
                              let eventBuffer = '';
                              let clarificationStreamContent = ''; // Accumulate MCP content for showFinalResults

                              while (true) {
                                const { done, value } = await reader.read();
                                if (done) {
                                  // Stream ended — parse accumulated content into green structured sections
                                  if (clarificationStreamContent.length > 0) {
                                    await showFinalResults(newAssistantMessageId, taskBreakdown, clarificationStreamContent, enrichedQuery);
                                  }
                                  setIsLoading(false);
                                  break;
                                }

                                const chunk = decoder.decode(value);
                                eventBuffer += chunk;

                                // Process SSE messages
                                const lines = eventBuffer.split('\n');
                                eventBuffer = lines.pop() || '';

                                for (const line of lines) {
                                  if (!line.trim() || !line.startsWith('data: ')) continue;

                                  const dataStr = line.substring(6);
                                  if (!dataStr || dataStr.trim() === '') continue;

                                  try {
                                    const data = JSON.parse(dataStr);

                                    // Handle all relevant SSE events to update the NEW assistant message
                                    if (data.type === 'thinking') {
                                      // Ignore thinking events - they're just status updates
                                    } else if (data.type === 'todoList' && data.todos) {
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId) {
                                          const newItems = data.todos.map((todo: any) => ({
                                            text: todo.todo,
                                            status: 'pending'
                                          }));
                                          return {
                                            ...msg,
                                            structured: {
                                              ...msg.structured,
                                              sections: msg.structured?.sections?.map(section => {
                                                if (section.title === '📋 Execution Plan') {
                                                  const updatedInitialItems = section.items.map((item: any) => ({
                                                    ...item,
                                                    status: 'complete'
                                                  }));
                                                  return {
                                                    ...section,
                                                    status: 'in_progress',
                                                    items: [...updatedInitialItems, ...newItems]
                                                  };
                                                }
                                                return section;
                                              }) ?? [{
                                                title: '📋 Execution Plan',
                                                status: 'in_progress',
                                                items: newItems
                                              }]
                                            }
                                          };
                                        }
                                        return msg;
                                      }));
                                    } else if (data.type === 'todoUpdate') {
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId && msg.structured) {
                                          return {
                                            ...msg,
                                            structured: {
                                              ...msg.structured,
                                              sections: msg.structured.sections?.map(section => {
                                                if (section.title === '📋 Execution Plan') {
                                                  return {
                                                    ...section,
                                                    items: section.items.map((item: any, idx: number) => ({
                                                      ...item,
                                                      status: idx === data.todoNumber ? data.status : item.status
                                                    }))
                                                  };
                                                }
                                                return section;
                                              })
                                            }
                                          };
                                        }
                                        return msg;
                                      }));
                                    } else if (data.type === 'mcp_response' && data.content) {
                                      // Stream MCP response content to structured.rawResponse for terminal display
                                      let mcpContent = typeof data.content === 'string' ? data.content : String(data.content);
                                      // Also accumulate for showFinalResults (green sections)
                                      clarificationStreamContent += mcpContent;
                                      // Extract inner text if content is a JSON object (e.g. {"response":"..."})
                                      if (mcpContent.trimStart().startsWith('{')) {
                                        try {
                                          const p = JSON.parse(mcpContent);
                                          if (typeof p.response === 'string') mcpContent = p.response;
                                          else if (typeof p.message === 'string') mcpContent = p.message;
                                          else if (typeof p.data === 'string') mcpContent = p.data;
                                        } catch { /* keep as-is */ }
                                      }
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId && msg.structured) {
                                          return {
                                            ...msg,
                                            structured: {
                                              ...msg.structured,
                                              rawResponse: (msg.structured.rawResponse || '') + mcpContent
                                            }
                                          };
                                        }
                                        return msg;
                                      }));
                                    } else if (data.type === 'Thinker' && data.content) {
                                      // Handle Thinker analysis
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId) {
                                          return {
                                            ...msg,
                                            content: msg.content + data.content
                                          };
                                        }
                                        return msg;
                                      }));
                                    } else if (((data.type === 'delta' || data.type === 'stream') && data.content) || (data.type === 'content' && data.data)) {
                                      // Handle both formats: cost queries use 'delta'+'content', EKS uses 'content'+'data'
                                      const contentToAdd = data.content || data.data || '';
                                      console.log('📝 Adding content to clarification message:', contentToAdd.substring(0, 50));
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId) {
                                          console.log('✅ Found matching message, updating content');
                                          return {
                                            ...msg,
                                            content: (msg.content || '') + contentToAdd
                                          };
                                        }
                                        return msg;
                                      }));
                                    } else if (data.type === 'phase' && data.content) {
                                      // Handle phase updates
                                      if (data.phase === 'completed' || data.content.includes('completed')) {
                                        setMessages(prev => prev.map(msg => {
                                          if (msg.id === newAssistantMessageId && msg.structured) {
                                            return {
                                              ...msg,
                                              structured: {
                                                ...msg.structured,
                                                sections: msg.structured.sections?.map(section => {
                                                  if (section.title === '📋 Execution Plan') {
                                                    return {
                                                      ...section,
                                                      status: 'complete',
                                                      items: section.items.map((item: any) => ({
                                                        ...item,
                                                        status: 'complete'
                                                      }))
                                                    };
                                                  }
                                                  return section;
                                                })
                                              }
                                            };
                                          }
                                          return msg;
                                        }));
                                      }
                                    } else if (data.type === 'visualization' && data.data) {
                                      // Handle visualization data
                                      const hasValidCostData = data.data.services &&
                                        data.data.services.length > 0 &&
                                        data.data.services.some((s: any) =>
                                          s.service &&
                                          s.service.length > 2 &&
                                          !s.service.match(/^(were|other|system|cloud|slightly|decreased)$/i)
                                        );

                                      const hasValidEC2Data = data.data.queryType === 'ec2' &&
                                        (data.data.totalCount !== undefined ||
                                          (data.data.instances && data.data.instances.length > 0));

                                      const hasValidEBSData = data.data.queryType === 'ebs' &&
                                        (data.data.totalCount !== undefined ||
                                          (data.data.volumes && data.data.volumes.length > 0));

                                      const hasValidS3Data = data.data.queryType === 's3' &&
                                        data.data.buckets && data.data.buckets.length > 0;

                                      const hasValidEBSCostData = (data.data.queryType === 'ebs_cost'
                                          || data.data.queryType === 'ebs_cost_multi_account') &&
                                        Array.isArray(data.data.items);

                                      if (hasValidCostData || hasValidEC2Data || hasValidEBSData || hasValidS3Data || hasValidEBSCostData) {
                                        const vizData: VisualizationData = {
                                          id: Date.now().toString(),
                                          type: data.data.queryType || 'cost-breakdown',
                                          title: `Query Result: ${data.query}`,
                                          data: data.data,
                                          timestamp: new Date(),
                                          query: data.query
                                        };

                                        setQueryDashboards(prev => [...prev, vizData]);
                                        setActiveQueryDashboard(vizData.id);
                                        setActiveTab('overview');
                                      }
                                    } else if (data.type === 'cost_multi_account' && data.data) {
                                      // Multi-account fan-out chart (clarification flow)
                                      console.log('🌐 COST MULTI-ACCOUNT received in clarification flow');
                                      const multiViz: VisualizationData = {
                                        id: Date.now().toString(),
                                        type: 'cost-breakdown',
                                        title: `Cost across ${data.data.accountCount || 0} accounts`,
                                        data: { ...data.data, queryType: 'cost_multi_account' },
                                        timestamp: new Date(),
                                        query: enrichedQuery
                                      };
                                      setQueryDashboards(prev => [...prev, multiViz]);
                                      setActiveQueryDashboard(multiViz.id);
                                      setActiveTab('overview');
                                    } else if (data.type === 'cost_comparison' && data.data) {
                                      // Handle comparison visualization from backend aggregateCostComparison()
                                      console.log('📊 COST COMPARISON received in clarification flow');
                                      const vizData: VisualizationData = {
                                        id: Date.now().toString(),
                                        type: 'cost-breakdown',
                                        title: `Cost Comparison: ${data.data.periods?.join(' vs ') || enrichedQuery}`,
                                        data: { ...data.data, queryType: 'cost_comparison' },
                                        timestamp: new Date(),
                                        query: enrichedQuery
                                      };
                                      setQueryDashboards(prev => [...prev, vizData]);
                                      setActiveQueryDashboard(vizData.id);
                                      setActiveTab('overview');
                                    } else if (data.type === 'clarification_required') {
                                      // A SECOND question in the same chain. This reader runs
                                      // after the user answers a clarification, and it had no
                                      // branch for another one — so when the account question
                                      // was followed by the region question, the second form was
                                      // silently dropped and the chat sat on "Cancel Execution"
                                      // until a page refresh revealed the saved question.
                                      console.log('❓ Follow-up clarification required:', data.clarifications);
                                      const followUp: Message = {
                                        id: (Date.now() + 3).toString(),
                                        type: 'assistant',
                                        content: `I need some additional information to proceed with your request: "${data.originalQuery || ''}"`,
                                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                        clarificationRequest: {
                                          clarifications: data.clarifications || [],
                                          originalQuery: data.originalQuery || '',
                                          sessionId: data.sessionId || ''
                                        }
                                      };
                                      setMessages(prev => [...prev, followUp]);
                                      setIsLoading(false);
                                    } else if (data.type === 'suggestions' && data.suggestions) {
                                      // Handle smart follow-up suggestions in clarification flow
                                      console.log('💡 Suggestions received (clarification):', data.suggestions);
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId) {
                                          return { ...msg, suggestions: data.suggestions };
                                        }
                                        return msg;
                                      }));
                                    } else if (data.type === 'done') {
                                      // Stream complete - finalize execution plan and mark streaming complete
                                      // Note: Don't close on mcp_complete — visualization/suggestions may still come after
                                      console.log('✅ Clarification query stream complete');
                                      setMessages(prev => prev.map(msg => {
                                        if (msg.id === newAssistantMessageId && msg.structured) {
                                          return {
                                            ...msg,
                                            structured: {
                                              ...msg.structured,
                                              finalResult: true, // Mark streaming as complete
                                              sections: msg.structured.sections?.map(section => {
                                                if (section.title === '📋 Execution Plan') {
                                                  return {
                                                    ...section,
                                                    status: 'complete',
                                                    items: section.items.map((item: any) => ({
                                                      ...item,
                                                      status: 'complete'
                                                    }))
                                                  };
                                                }
                                                return section;
                                              })
                                            }
                                          };
                                        }
                                        return msg;
                                      }));
                                      setIsLoading(false);
                                    }
                                  } catch (e) {
                                    console.error('Error parsing SSE:', e);
                                  }
                                }
                              }
                            }

                            setIsLoading(false);
                          } catch (error) {
                            console.error('Error submitting clarification:', error);
                            setIsLoading(false);
                          }
                        }}>
                          <div className="space-y-3 mb-4">
                            {message.clarificationRequest.clarifications.map((clarification, index) => (
                              <div key={index}>
                                <label className="block text-xs font-medium text-yellow-400 mb-1.5">
                                  {clarification}
                                </label>
                                <input
                                  type="text"
                                  name={`clarification_${index}`}
                                  placeholder={`Enter ${clarification.split('(')[0].trim()}`}
                                  required
                                  autoFocus={index === 0}
                                  className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      (e.currentTarget.form as HTMLFormElement).requestSubmit();
                                    }
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          <button
                            type="submit"
                            className="w-full px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-dark-bg rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2"
                          >
                            <span>Submit & Continue</span>
                            <FontAwesomeIcon icon={faPaperPlane} />
                          </button>
                        </form>
                      </div>
                    ) : message.securityNoScans ? (
                      <div className="bg-dark-panel rounded-2xl rounded-tl-md p-4 border border-dark-border">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-2xl">🔍</span>
                          <div>
                            <div className="text-sm font-medium text-gray-200">No scans found</div>
                            <div className="text-xs text-gray-400">No security scans available for {message.securityNoScans.label}</div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">
                          Run a scan now to check your current AWS security posture — it covers EC2 security groups, S3 buckets, and IAM access keys.
                        </p>
                        {!message.securityScanInitiated ? (
                          <button
                            onClick={() => {
                              // Mark this message's button as used so it disappears
                              setMessages(prev => prev.map(m =>
                                m.id === message.id ? { ...m, securityScanInitiated: true } : m
                              ));
                              setInputValue('run security scan now');
                              setTimeout(() => {
                                const sendBtn = document.querySelector('[data-send-button]') as HTMLButtonElement;
                                if (sendBtn) sendBtn.click();
                              }, 100);
                            }}
                            className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2"
                          >
                            <FontAwesomeIcon icon={faShieldHalved} />
                            <span>Scan Now</span>
                          </button>
                        ) : (
                          <div className="w-full py-2 text-center text-xs text-gray-500">
                            ✓ Scan initiated — check the response above
                          </div>
                        )}
                      </div>
                    ) : message.securityScanList ? (
                      <div className="bg-dark-panel rounded-2xl rounded-tl-md p-3 border border-dark-border space-y-2">
                        {message.content && (
                          <div className="text-sm text-gray-200 mb-3">{message.content}</div>
                        )}

                        {/* Header: scan count + trend */}
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-xs text-gray-400">
                            {message.securityScanTotal ?? message.securityScanList.length} scan{(message.securityScanTotal ?? message.securityScanList.length) !== 1 ? 's' : ''} in {message.securityScanLabel}
                          </div>
                          {message.securityScanTrend && (
                            <div className={`text-xs font-medium ${message.securityScanTrend.to < message.securityScanTrend.from ? 'text-green-400' : message.securityScanTrend.to > message.securityScanTrend.from ? 'text-red-400' : 'text-gray-400'}`}>
                              {message.securityScanTrend.to < message.securityScanTrend.from ? '📉' : message.securityScanTrend.to > message.securityScanTrend.from ? '📈' : '➡️'} {message.securityScanTrend.from} → {message.securityScanTrend.to} vulns
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {(message.securityScanTotal ?? message.securityScanList.length) > 5
                            ? `Showing latest 5 — click to open in Security tab:`
                            : 'Click a scan to view details:'}
                        </div>

                        {/* Scan cards — max 5 */}
                        {message.securityScanList.map((scan) => (
                          <button
                            key={scan.scanId}
                            onClick={async () => {
                              setSecurityScanId(scan.scanId);
                              setActiveTab('security');

                              // Fetch LLM summary for this scan and add it as a new chat message
                              try {
                                const token = localStorage.getItem('auth_token');
                                const resp = await axios.get(
                                  `${API_BASE_URL}/api/security/scan/${scan.scanId}/chat-summary`,
                                  { headers: { 'Authorization': `Bearer ${token}` } }
                                );
                                if (resp.data.success) {
                                  const summaryMsg: Message = {
                                    id: (Date.now() + 1).toString(),
                                    type: 'assistant',
                                    content: resp.data.summary,
                                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                  };
                                  setMessages(prev => [...prev, summaryMsg]);
                                }
                              } catch (err) {
                                console.error('[Security] Failed to fetch scan summary:', err);
                              }
                            }}
                            className="w-full text-left bg-gray-800 hover:bg-gray-700 border border-gray-600 hover:border-blue-500 rounded-lg p-3 transition-all duration-200"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-blue-400">
                                📅 {new Date(scan.scanTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                              <span className="text-xs text-gray-400">{scan.summary.totalVulnerabilities} vulnerabilities</span>
                            </div>
                            <div className="flex space-x-3 text-xs">
                              <span className="text-red-400">🔴 {scan.summary.severitySplit.critical} Critical</span>
                              <span className="text-orange-400">🟠 {scan.summary.severitySplit.high} High</span>
                              <span className="text-yellow-400">🟡 {scan.summary.severitySplit.medium} Medium</span>
                            </div>
                          </button>
                        ))}

                        {/* "X more" button — only when total > 5 */}
                        {(message.securityScanTotal ?? 0) > 5 && (
                          <button
                            onClick={() => {
                              setActiveTab('security');
                              setSecurityOpenHistory(true);
                            }}
                            className="w-full text-center text-xs text-blue-400 hover:text-blue-300 border border-dashed border-gray-600 hover:border-blue-500 rounded-lg py-2 mt-1 transition-all duration-200"
                          >
                            + {(message.securityScanTotal ?? 0) - 5} more scans &nbsp;→&nbsp; Browse all in Security tab
                          </button>
                        )}
                      </div>
                    ) : message.errorCode ? (
                      <div className="bg-dark-panel rounded-2xl rounded-tl-md p-3 border border-red-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-red-400 text-xs font-mono font-semibold">Error Code: {message.errorCode}</span>
                        </div>
                        <div className="text-sm text-gray-300">{message.content}</div>
                      </div>
                    ) : message.structured ? (
                      <StructuredResponse
                        structured={message.structured}
                        tokens={message.tokens}
                        messageId={message.id}
                        onCancelExecution={cancelExecution}
                        onExecuteQuickAction={executeQuickAction}
                        onHandleAccountSelection={handleAccountSelection}
                      />
                    ) : (
                      <div className="bg-dark-panel rounded-2xl rounded-tl-md p-2 sm:p-3 border border-dark-border">
                        <div className="flex items-start justify-between mb-3">
                          <div className="text-sm flex-1">{message.content}</div>
                          {isLoading && message.id === messages[messages.length - 1]?.id && (
                            <button
                              onClick={cancelExecution}
                              className="ml-3 px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg text-xs font-medium transition-all duration-200 flex-shrink-0"
                              title="Cancel current request"
                            >
                              <FontAwesomeIcon icon={faStop} className="mr-1" />
                              Cancel Execution
                            </button>
                          )}
                        </div>

                        {message.metadata?.deployment && (
                          <>
                            <div className="space-y-3 mb-3">
                              {message.metadata.deployment.accounts.map((account: any, idx: number) => (
                                <div key={idx} className="bg-dark-bg rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-orange-400">{account.name}</span>
                                    <span className={`text-xs ${account.status === 'complete' ? 'text-green-400' : 'text-blue-400'}`}>
                                      {account.status === 'complete' ? '✓ Complete' : (
                                        <div className="flex items-center">
                                          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse mr-2"></div>
                                          In Progress
                                        </div>
                                      )}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-300 space-y-1">
                                    {account.tasks.map((task: string, taskIdx: number) => (
                                      <div key={taskIdx}>{task}</div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg text-xs font-medium transition-all duration-200">
                                <FontAwesomeIcon icon={faStop} className="mr-1" />Emergency Stop
                              </button>
                              <button className="px-3 py-1.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20 rounded-lg text-xs font-medium transition-all duration-200">
                                <FontAwesomeIcon icon={faUndo} className="mr-1" />Rollback All
                              </button>
                              <button className="px-3 py-1.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 rounded-lg text-xs font-medium transition-all duration-200">
                                <FontAwesomeIcon icon={faPause} className="mr-1" />Pause Queue
                              </button>
                              <button className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg text-xs font-medium transition-colors">
                                <FontAwesomeIcon icon={faEye} className="mr-1" />Live Logs
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                      <div className="flex items-center space-x-2">
                        <span>{message.timestamp}</span>
                        {message.tokens && (
                          <>
                            <span>•</span>
                            <span>{message.tokens} tokens</span>
                          </>
                        )}
                        <span>•</span>
                        <span className="text-blue-400">Multi-Account Deploy</span>
                      </div>
                      <div className="flex space-x-2">
                        <button className="hover:text-white"><FontAwesomeIcon icon={faBookmark} /></button>
                        <button className="hover:text-white"><FontAwesomeIcon icon={faShare} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Smart Suggestion Buttons */}
              {message.suggestions && message.suggestions.length > 0 && !isLoading && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {message.suggestions.map((suggestion: any, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputValue(suggestion.query);
                        // Auto-send after small delay
                        setTimeout(() => {
                          const sendBtn = document.querySelector('[data-send-button]') as HTMLButtonElement;
                          if (sendBtn) sendBtn.click();
                        }, 100);
                      }}
                      className="px-3 py-1.5 bg-accent-blue/10 text-accent-blue border border-accent-blue/20 hover:bg-accent-blue/20 rounded-lg text-xs font-medium transition-all duration-200"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex items-center space-x-2 text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent-blue"></div>
              <span className="text-sm">Processing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        )}

        {/* Input Area */}
        {!isChatCollapsed && (
        <div className="shrink-0 p-2 sm:p-3 border-t border-dark-border bg-dark-bg"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          {/* Quick Action Buttons */}
          <div className="flex space-x-1 sm:space-x-2 mb-3 overflow-x-auto pb-1">
            <button
              onClick={() => {
                const twoMonthsAgo = new Date();
                twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
                const monthName = twoMonthsAgo.toLocaleString('default', { month: 'long' });
                const year = twoMonthsAgo.getFullYear();
                handleQuickAction(`Get me the cost of ${monthName} ${year}`);
              }}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-dark-panel border border-dark-border rounded-lg text-xs whitespace-nowrap hover:border-accent-blue transition-colors"
            >
              Cost Analysis
            </button>
            <button
              onClick={() => handleQuickAction('How many running instances in Virginia in aws accounts')}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-dark-panel border border-dark-border rounded-lg text-xs whitespace-nowrap hover:border-accent-blue transition-colors"
            >
              Running Instances
            </button>
            <button
              onClick={() => handleQuickAction('Show security vulnerabilities across all accounts')}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-dark-panel border border-dark-border rounded-lg text-xs whitespace-nowrap hover:border-accent-blue transition-colors"
            >
              Security Scan
            </button>
          </div>

          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="flex-1">
              <div className="relative">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                  className="w-full bg-dark-panel border border-dark-border rounded-xl p-2 sm:p-3 pr-10 sm:pr-12 text-sm resize-none focus:outline-none focus:border-accent-blue transition-colors placeholder-gray-400 min-h-[60px]"
                  rows={2}
                  placeholder="Deploy, manage, optimize..."
                />
                <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 flex space-x-2">
                  <button
                    onClick={() => {
                      setShowNewConversationPrompt(true);
                      addLog('info', 'Opening new conversation prompt', 'frontend', 'user-action');
                    }}
                    className="bg-gray-600 hover:bg-gray-700 rounded-lg p-1.5 sm:p-2 transition-colors"
                    title="New conversation"
                  >
                    <FontAwesomeIcon icon={faPlus} className="text-xs sm:text-sm" />
                  </button>
                  <button
                    onClick={sendMessage}
                    disabled={isLoading}
                    className="bg-accent-blue hover:bg-blue-600 rounded-lg p-1.5 sm:p-2 transition-colors disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="text-xs sm:text-sm" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 space-y-2 sm:space-y-0">
                <div className="text-xs text-gray-400 flex items-center space-x-3">
                  {(() => {
                    const fmt = (n: number) => {
                      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
                      if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
                      return String(n);
                    };
                    const used      = billingData?.usage?.current_month_tokens ?? 0;
                    const remaining = billingData?.wallet?.total                ?? 0;
                    // Quota: plan limit if subscribed, otherwise lifetime budget = used + remaining
                    const quota = billingData?.plan?.token_limit ?? (used + remaining);
                    return <span>{quota > 0 ? `${fmt(used)}/${fmt(quota)} credits` : '0 credits'}</span>;
                  })()}
                  {(() => {
                    const total  = connectedAccounts.length;
                    const online = connectedAccounts.filter(a => a.status === 'active').length;
                    let dotColor = 'bg-gray-400';
                    let label    = 'No accounts connected';
                    if (total > 0) {
                      if (online === total) {
                        dotColor = 'bg-green-400';
                        label    = total === 1 ? '1 account online' : `All ${total} accounts online`;
                      } else {
                        dotColor = 'bg-yellow-400';
                        label    = `${online}/${total} accounts online`;
                      }
                    }
                    return (
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 ${dotColor} rounded-full`}></div>
                        <span>{label}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* New Conversation Name Prompt */}
        {showNewConversationPrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-panel border border-dark-border rounded-lg shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-dark-border">
                <h3 className="text-sm font-medium text-gray-200">New Conversation</h3>
              </div>
              <div className="p-4">
                <input
                  type="text"
                  value={newConversationName}
                  onChange={(e) => setNewConversationName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateNewConversation();
                    } else if (e.key === 'Escape') {
                      setShowNewConversationPrompt(false);
                      setNewConversationName('');
                    }
                  }}
                  placeholder="Enter conversation name (optional)"
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-accent-blue transition-colors"
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-2">Press Enter to create or leave blank for default</p>
              </div>
              <div className="p-4 border-t border-dark-border flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowNewConversationPrompt(false);
                    setNewConversationName('');
                  }}
                  className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNewConversation}
                  className="px-3 py-1.5 text-xs text-white bg-accent-blue hover:bg-blue-600 rounded-lg transition-colors"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Tab Overlay */}
      {isMobileTabsOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileTabsOpen(false)}
        />
      )}

      {/* Right Panel - Workspace */}
      <div className="flex-1 min-w-0 bg-white flex flex-col relative transition-all duration-300 ease-in-out">
        {/* Mobile Header Bar */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-2 sm:p-3 flex items-center justify-between sticky top-0 z-20">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="flex items-center space-x-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Open Chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
            </svg>
            <span className="text-xs">Chat</span>
          </button>
          <h1 className="text-sm sm:text-base font-semibold text-gray-900 truncate">DevopsX</h1>
          {/* Debug auth state */}
          <div className="text-xs text-gray-500 flex items-center space-x-2">
            <span>Auth: {isAuthenticated ? 'Yes' : 'No'}</span>
            <button
              onClick={() => {
                localStorage.removeItem('auth_token');
                setIsAuthenticated(false);
                setUserProfile(null);
                setIsAuthLoading(false); // Make sure loading is off
                alert('Forced logout - you should now see login buttons');
              }}
              className="text-xs px-1 bg-red-100 text-red-600 rounded"
            >
              Force Logout
            </button>
          </div>
          <div className="flex items-center space-x-2">
            {isAuthLoading ? (
              // Loading authentication state
              <div className="text-xs text-gray-500">Loading...</div>
            ) : isAuthenticated ? (
              // Authenticated user - show profile and logout
              <div className="flex items-center space-x-2">
                {/* {userProfile && (
                  <div className="flex items-center space-x-1 text-xs">
                    <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-xs" />
                    </div>
                    <span className="text-gray-600 max-w-20 truncate">{userProfile.email}</span>
                  </div>
                )} */}
                {/* <button
                  onClick={handleLogout}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Logout"
                >
                  Logout
                </button> */}
              </div>
            ) : (
              // Not authenticated - show login/signup buttons
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setShowLogin(true)}
                  className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => setShowSignup(true)}
                  className="px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Sign Up
                </button>
              </div>
            )}
            <button
              onClick={() => setIsMobileTabsOpen(true)}
              className="flex items-center space-x-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Open Sections"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-xs">Menu</span>
            </button>
          </div>
        </div>

        {/* Desktop Header with Tabs */}
        <div className="hidden lg:block border-b border-gray-200 bg-white">
          <div className="p-4 xl:p-6 pb-0">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Multi-Cloud Operations Center</h2>
                <p className="text-xs text-gray-500 mt-1">Real-time infrastructure monitoring across AWS, GCP, and GitHub</p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm">
                    {/* <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div> */}
                    {/* <span className="text-gray-600">{accounts.total} Accounts123 Live</span> */}
                  </div>
                  {isAuthLoading ? (
                    // Loading authentication state
                    <div className="text-sm text-gray-500">Loading...</div>
                  ) : isAuthenticated ? (
                    // Authenticated user - show profile and logout
                    userProfile && (
                      <div className="flex items-center space-x-3 text-sm">
                        {/* <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-xs" />
                          </div>
                          <span className="text-gray-600">{userProfile.email}</span>
                        </div> */}
                        {/* <button
                          onClick={handleLogout}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Logout"
                        >
                          Logout
                        </button> */}
                      </div>
                    )
                  ) : (
                    // Not authenticated - show login/signup buttons
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowLogin(true)}
                        className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => setShowSignup(true)}
                        className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Sign Up
                      </button>
                    </div>
                  )}
                </div>
                {/* <button className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />Export
                </button> */}
              </div>
              <ProfileDropdown
                onLogout={handleLogout}
                userEmail={userProfile?.email}
                userName={userProfile?.name}
                organisation={userProfile?.organisation}
                userNumber={userProfile?.number}
                status={userProfile?.status}
                toolAccess={userProfile?.tool_access}
                ticketAccess={userProfile?.ticket_access}
                quotaPerPeriod={userProfile?.quota_per_period}
                quotaRenewedAt={userProfile?.quota_renewed_at}
                subscriptionId={userProfile?.subscription_id}
                lastLogin={userProfile?.last_login}
                memberSince={userProfile?.createdAt}
                subscription={billingData?.subscription}
                plan={billingData?.plan}
                wallet={billingData?.wallet}
                usage={billingData?.usage}
                creditBalanceCents={billingData?.creditBalanceCents}
                creditCurrency={billingData?.creditCurrency}
                paymentMethods={billingData?.paymentMethods}
                invoices={billingData?.invoices}
                onRefreshBilling={refreshBilling}
                onPaymentToast={setPaymentToast}
              />
            </div>


          </div>
          <div className="px-2">
            <div className="flex space-x-1 border-b border-gray-200 overflow-x-auto scrollbar-hide">
              {[
                { id: 'overview', label: 'DevOpsX', icon: faChartLine },
                { id: 'infrastructure', label: 'Infrastructure', icon: faServer },
                { id: 'deployments', label: 'Deployments', icon: faRocket, hidden: true },
                { id: 'costs', label: 'Cost Analysis', icon: faDollarSign },
                { id: 'security', label: 'Security', icon: faShieldHalved },
                // { id: 'monitoring', label: 'Monitoring', icon: faChartBar },
                // { id: 'resources', label: 'Resources', icon: faDatabase },
                { id: 'recommendations', label: 'FinOps', icon: faLightbulb },
                { id: 'kubernetes', label: 'Kubernetes', icon: faCloud },
                { id: 'gpus', label: 'GPUs', icon: faMicrochip },
                { id: 'byoip', label: 'BYOIP', icon: faGlobe },
                { id: 'support', label: 'Support', icon: faLifeRing }
              ].filter(tab => !tab.hidden).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 px-3 xl:px-5 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >

                  <FontAwesomeIcon icon={tab.icon} className="mr-2" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Tabs Selector */}
        {isMobileTabsOpen && (
          <div className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 z-40 lg:hidden">
            <div className="p-2 sm:p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900">Sections</h3>
                <button
                  onClick={() => setIsMobileTabsOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'overview', label: 'DevOpsX', icon: faChartLine },
                  { id: 'infrastructure', label: 'Infrastructure', icon: faServer },
                  { id: 'deployments', label: 'Deployments', icon: faRocket, hidden: true },
                  { id: 'costs', label: 'Cost Analysis', icon: faDollarSign },
                  { id: 'security', label: 'Security', icon: faShieldHalved },
                  { id: 'monitoring', label: 'Monitoring', icon: faChartBar },
                  { id: 'resources', label: 'Resources', icon: faDatabase },
                  { id: 'recommendations', label: 'FinOps', icon: faLightbulb },
                  { id: 'kubernetes', label: 'Kubernetes', icon: faCloud },
                  { id: 'gpus', label: 'GPUs', icon: faMicrochip },
                  { id: 'byoip', label: 'BYOIP', icon: faGlobe },
                  { id: 'support', label: 'Support', icon: faLifeRing }
                ].filter(tab => !tab.hidden).map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setIsMobileTabsOpen(false);
                    }}
                    className={`p-2 sm:p-3 rounded-xl text-left transition-colors ${activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border-2 border-blue-200'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon icon={tab.icon} className="text-lg" />
                      <div>
                        <div className="font-medium">{tab.label}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {tab.id === 'overview' && 'Executive dashboard'}
                          {tab.id === 'infrastructure' && 'Multi-cloud resources'}
                          {tab.id === 'deployments' && 'CI/CD & releases'}
                          {tab.id === 'alerts' && 'Monitoring & thresholds'}
                          {tab.id === 'costs' && 'Cost optimization'}
                          {tab.id === 'security' && 'Compliance & security'}
                          {tab.id === 'monitoring' && 'Performance metrics'}
                          {tab.id === 'resources' && 'Resource inventory'}
                          {tab.id === 'recommendations' && 'Savings & optimizations'}
                          {tab.id === 'kubernetes' && 'EKS & GKE clusters'}
                          {tab.id === 'gpus' && 'GPU compute instances'}
                          {tab.id === 'byoip' && 'Bring Your Own IP'}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="lg:hidden p-2 sm:p-3 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon
                  icon={[
                    { id: 'overview', icon: faChartLine },
                    { id: 'infrastructure', icon: faServer },
                    { id: 'deployments', icon: faRocket },
                    { id: 'alerts', icon: faBell },
                    { id: 'costs', icon: faDollarSign },
                    { id: 'security', icon: faShieldHalved },
                    { id: 'monitoring', icon: faChartBar },
                    { id: 'resources', icon: faDatabase },
                    { id: 'recommendations', icon: faLightbulb },
                    { id: 'kubernetes', icon: faCloud },
                    { id: 'gpus', icon: faMicrochip },
                    { id: 'support', icon: faLifeRing }
                  ].find(tab => tab.id === activeTab)?.icon || faChartLine}
                  className="text-blue-600"
                />
                <span className="text-blue-900 font-medium">
                  {[
                    { id: 'overview', label: 'DevOpsX' },
                    { id: 'infrastructure', label: 'Infrastructure' },
                    { id: 'deployments', label: 'Deployments', hidden: true },
                    { id: 'costs', label: 'Cost Analysis' },
                    { id: 'security', label: 'Security' },
                    { id: 'monitoring', label: 'Monitoring' },
                    { id: 'resources', label: 'Resources' },
                    { id: 'recommendations', label: 'FinOps' },
                    { id: 'kubernetes', label: 'Kubernetes' },
                    { id: 'gpus', label: 'GPUs' },
                    { id: 'support', label: 'Support' }
                  ].filter(tab => !tab.hidden).find(tab => tab.id === activeTab)?.label || 'Overview'}
                </span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-blue-600">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Live</span>
              </div>
            </div>
          </div>
          <div className="p-2 sm:p-4 lg:p-0">
            {renderTabContent()}
          </div>
        </div>

        {/* Floating Chat Button - Mobile Only */}
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="lg:hidden fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg z-30 transition-colors"
          title="Open Chat"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
          </svg>
        </button>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-lg" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Welcome to DevOpsX</h2>
              <p className="text-sm text-gray-600 mt-1">Access your cloud management dashboard</p>
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {authError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
            </div>

            <div className="flex justify-between items-center space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowLogin(false);
                  setShowSignup(true);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Need an account? Sign Up
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLogin(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogin}
                  disabled={!loginForm.email || !loginForm.password}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg"
                >
                  Login
                </button>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              <p>For testing, you can use:</p>
              <p className="font-mono text-xs bg-gray-100 p-2 rounded">
                Email: testuser@devopsx.com<br />
                Password: testpassword123
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                <FontAwesomeIcon icon={faUsers} className="text-green-600 text-lg" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Join DevOpsX</h2>
              <p className="text-sm text-gray-600 mt-1">Create your cloud management account</p>
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {authError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={signupForm.full_name}
                  onChange={(e) => setSignupForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Organisation</label>
                <input
                  type="text"
                  value={signupForm.organisation}
                  onChange={(e) => setSignupForm(prev => ({ ...prev, organisation: e.target.value }))}
                  placeholder="Acme Inc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={signupForm.email}
                  onChange={(e) => setSignupForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={signupForm.password}
                  onChange={(e) => setSignupForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a strong password (min 6 chars)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSignup()}
                />
              </div>
            </div>

            <div className="flex justify-between items-center space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowSignup(false);
                  setShowLogin(true);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Already have an account? Login
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowSignup(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSignup}
                  disabled={!signupForm.full_name || !signupForm.organisation || !signupForm.email || !signupForm.password}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg"
                >
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accounts Management Modal */}
      {isAccountsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Connected Accounts</h2>
                <button
                  onClick={() => setIsAccountsModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4">
                <button
                  onClick={() => setShowAddAccountModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add Account</span>
                </button>
              </div>

              <div className="space-y-4">
                {connectedAccounts.map((account) => (
                  <div key={account.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${account.type === 'aws' ? 'bg-orange-100' :
                          account.type === 'gcp' ? 'bg-blue-100' :
                            account.type === 'github' ? 'bg-gray-100' : 'bg-purple-100'
                          }`}>
                          <FontAwesomeIcon
                            icon={
                              account.type === 'aws' ? faAws :
                                account.type === 'gcp' ? faGoogle :
                                  account.type === 'github' ? faGithub : faJenkins
                            }
                            className={`${account.type === 'aws' ? 'text-orange-600' :
                              account.type === 'gcp' ? 'text-blue-600' :
                                account.type === 'github' ? 'text-gray-800' : 'text-purple-600'
                              }`}
                          />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{account.name}</h3>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="capitalize">{account.type}</span>
                            {account.accountId && <span>ID: {account.accountId}</span>}
                            {account.region && <span>{account.region}</span>}
                            <span className="flex items-center space-x-1">
                              <FontAwesomeIcon
                                icon={account.connectionMethod === 'cloudformation' ? faKey :
                                  account.connectionMethod === 'iam' ? faKey :
                                    account.connectionMethod === 'oauth' ? faExternalLinkAlt : faKey}
                                className="text-xs"
                              />
                              <span className="capitalize">{account.connectionMethod}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${account.status === 'active' ? 'bg-green-500' :
                            account.status === 'inactive' ? 'bg-yellow-500' : 'bg-red-500'
                            }`}></div>
                          <span className="text-xs text-gray-500">{account.lastSync}</span>
                        </div>
                        <button
                          onClick={() => deleteAccount(account.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Remove Account"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Add New Account</h2>
                <button
                  onClick={() => {
                    setShowAddAccountModal(false);
                    setSelectedAccountType(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {!selectedAccountType ? (
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Select Account Type</h3>

                  <button
                    onClick={() => setSelectedAccountType('aws')}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faAws} className="text-orange-600 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Amazon Web Services</h4>
                        <p className="text-xs text-gray-500">Connect your AWS account for cost optimization and management</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedAccountType('gcp')}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faGoogle} className="text-blue-600 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Google Cloud Platform</h4>
                        <p className="text-xs text-gray-500">Connect your GCP project for resource management</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedAccountType('github')}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faGithub} className="text-gray-800 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">GitHub</h4>
                        <p className="text-xs text-gray-500">Connect your GitHub organization for repository management</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedAccountType('jenkins')}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faJenkins} className="text-purple-600 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Jenkins</h4>
                        <p className="text-xs text-gray-500">Connect your Jenkins server for CI/CD pipeline management</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedAccountType('eks')}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <span className="text-indigo-600 text-2xl">☸️</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">AWS EKS Cluster</h4>
                        <p className="text-xs text-gray-500">Connect your EKS cluster for Kubernetes management</p>
                      </div>
                    </div>
                  </button>
                </div>
              ) : selectedAccountType === 'aws' ? (
                <AWSAccountSetup onBack={() => { loadAccounts(); setSelectedAccountType(null); }} onSave={saveAccount} />
              ) : selectedAccountType === 'eks' ? (
                <AWSAccountSetup onBack={() => { loadAccounts(); setSelectedAccountType(null); }} onSave={saveAccount} initialTab="eks" />
              ) : selectedAccountType === 'gcp' ? (
                <GCPAccountSetup onBack={() => setSelectedAccountType(null)} onSave={saveAccount} />
              ) : selectedAccountType === 'github' ? (
                <GitHubAccountSetup onBack={() => setSelectedAccountType(null)} onSave={saveAccount} />
              ) : (
                <JenkinsAccountSetup onBack={() => setSelectedAccountType(null)} onSave={saveAccount} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scan Details Modal */}
      {showScanDetailsModal && scanDetailsData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center">
                  <FontAwesomeIcon icon={faBug} className="mr-3" />
                  AWS Scan Report - Detailed Breakdown
                </h2>
                <p className="text-sm text-blue-100 mt-1">
                  Comprehensive analysis of all resources and cost data
                </p>
              </div>
              <button
                onClick={() => setShowScanDetailsModal(false)}
                className="text-white hover:text-blue-200 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-2xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary Cards */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faCheck} className="text-blue-600 mr-2" />
                  Scan Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Total Resources</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {scanDetailsData.summary?.totalResources || 0}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Usage Types</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {scanDetailsData.summary?.totalUsageTypes || 0}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Current Cost</div>
                    <div className="text-2xl font-bold text-green-600">
                      ${scanDetailsData.summary?.currentMonthCost?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Duration</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {scanDetailsData.duration || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Resource Breakdown */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faServer} className="text-gray-600 mr-2" />
                  Resources by Category ({scanDetailsData.summary?.totalResources || 0} total)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(scanDetailsData.summary?.byCategory || {}).map(([category, count]) => (
                    <div key={category} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="text-xs text-gray-600 capitalize">{category}</div>
                      <div className="text-lg font-bold text-gray-900">{count as number}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommendations Summary */}
              {scanDetailsData.recommendationsSummary && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faLightbulb} className="text-yellow-600 mr-2" />
                    Recommendations ({scanDetailsData.recommendationsSummary.total || 0})
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-yellow-100">
                      <div className="text-xs text-gray-600">High Priority</div>
                      <div className="text-xl font-bold text-red-600">
                        {scanDetailsData.recommendationsSummary.byPriority?.high || 0}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-yellow-100">
                      <div className="text-xs text-gray-600">Medium Priority</div>
                      <div className="text-xl font-bold text-yellow-600">
                        {scanDetailsData.recommendationsSummary.byPriority?.medium || 0}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-yellow-100">
                      <div className="text-xs text-gray-600">Potential Savings</div>
                      <div className="text-xl font-bold text-green-600">
                        ${scanDetailsData.recommendationsSummary.totalPotentialSavings?.monthly?.toLocaleString() || 0}/mo
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* DevOpsX Savings */}
              {scanDetailsData.devopsxDetails && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faDollarSign} className="text-green-600 mr-2" />
                    DevOpsX Pre-Approved Savings
                  </h3>
                  <div className="bg-white rounded-lg p-4 border border-green-100">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      ${scanDetailsData.devopsxDetails.totalSavings?.toLocaleString() || 0}/month
                    </div>
                    <p className="text-sm text-gray-600">
                      {scanDetailsData.devopsxDetails.llmInsights?.executive_summary || 'No DevOpsX savings available'}
                    </p>
                  </div>
                </div>
              )}

              {/* Progress Log */}
              {scanDetailsData.progressLog && scanDetailsData.progressLog.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-md font-semibold text-white mb-3 flex items-center">
                    <FontAwesomeIcon icon={faClock} className="text-gray-400 mr-2" />
                    Scan Progress Log ({scanDetailsData.progressLog.length} entries)
                  </h3>
                  <div className="bg-gray-800 rounded p-3 max-h-60 overflow-y-auto font-mono text-xs text-green-400">
                    {scanDetailsData.progressLog.map((entry: string, i: number) => (
                      <div key={i} className="py-1">{entry}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-600">
                Scan ID: <span className="font-mono">{scanDetailsData.scanId}</span>
              </div>
              <button
                onClick={() => setShowScanDetailsModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// AWS Account Setup Component
const AWSAccountSetup: React.FC<{ onBack: () => void; onSave: (data: any) => Promise<any>; initialTab?: string }> = ({ onBack, onSave, initialTab }) => {
  const [connectionMethod, setConnectionMethod] = useState<'iam' | 'cloudformation' | 'eks' | null>(initialTab === 'eks' ? 'eks' : null);
  const [awsConfig, setAWSConfig] = useState<Partial<AWSAccountConfig>>({
    region: 'us-east-1'
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [accountName, setAccountName] = useState('');
  const [showEKSWizard, setShowEKSWizard] = useState(initialTab === 'eks');

  // If we should show the wizard, render it instead
  if (showEKSWizard) {
    return (
      <EKSWizard
        onClose={onBack}
        onSuccess={() => {
          onBack();
        }}
      />
    );
  }

  const handleConnectAccount = async () => {
    setIsConnecting(true);
    setErrorMessage(null);

    try {
      if (connectionMethod === 'iam') {
        // Use IAM-specific endpoint for access key registration
        const response = await fetch(`${API_BASE_URL}/api/accounts/aws/register-iam-keys`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            accountName: accountName || 'AWS IAM Account',
            accessKeyId: awsConfig.accessKey,
            secretAccessKey: awsConfig.secretKey,
            region: awsConfig.region,
            accountId: awsConfig.accountId
          })
        });

        const result = await response.json();

        if (!response.ok) {
          // Show detailed error with possible causes
          let errorDetails = result.message || 'Failed to connect account';
          if (result.error) {
            if (result.error.includes('InvalidUserID.NotFound')) {
              errorDetails += '\n\nPossible causes:\n• Access Key ID is incorrect\n• IAM user does not exist\n• Access key has been deleted';
            } else if (result.error.includes('SignatureDoesNotMatch')) {
              errorDetails += '\n\nPossible causes:\n• Secret Access Key is incorrect\n• Keys have been rotated\n• Copy-paste error in credentials';
            } else if (result.error.includes('AccessDenied')) {
              errorDetails += '\n\nPossible causes:\n• IAM user lacks required permissions\n• Account policies restrict access\n• STS service not accessible';
            }
          }
          setErrorMessage(errorDetails);
          return;
        }

        // Success - account registered with detailed confirmation
        setErrorMessage(null);

        // Show success dialog with account details
        const successMessage = `✅ AWS Account Connected Successfully!\n\n` +
          `Account: ${result.account.name}\n` +
          `Method: IAM Access Keys\n` +
          `Region: ${result.account.region}\n` +
          `Account ID: ${result.account.accountId}\n\n` +
          `Capabilities:\n${result.capabilities?.map((cap: string) => `• ${cap}`).join('\n') || '• Basic AWS access'}\n\n` +
          `You can now run queries like:\n• "get me the cost of may"\n• "list my EC2 instances"`;

        alert(successMessage);
        onBack(); // Go back to account list

      } else if (connectionMethod === 'cloudformation') {
        // Use CloudFormation-specific endpoint for role-based registration
        const response = await fetch(`${API_BASE_URL}/api/accounts/aws/register-cloudformation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            accountName: accountName || 'AWS CloudFormation Account',
            roleArn: awsConfig.roleArn,
            externalId: awsConfig.externalId,
            region: awsConfig.region,
            accountId: awsConfig.accountId
          })
        });

        const result = await response.json();

        if (!response.ok) {
          // Show detailed error with possible causes
          let errorDetails = result.message || 'Failed to connect account';
          if (result.error) {
            if (result.error.includes('AssumeRole')) {
              errorDetails += '\n\nPossible causes:\n• Role ARN is incorrect\n• External ID mismatch\n• Role trust policy not configured properly\n• DevOpsX service role not set up';
            } else if (result.error.includes('Invalid')) {
              errorDetails += '\n\nPossible causes:\n• Invalid role ARN format\n• Account ID mismatch\n• Region not accessible';
            } else if (result.error.includes('ValidationException')) {
              errorDetails += '\n\nPossible causes:\n• CloudFormation stack not deployed properly\n• Role permissions insufficient\n• Cross-account trust relationship missing';
            }
          }
          setErrorMessage(errorDetails);
          return;
        }

        // Success - account registered with detailed confirmation
        setErrorMessage(null);

        // Show success dialog with account details
        const successMessage = `✅ AWS Account Connected Successfully!\n\n` +
          `Account: ${result.account.name}\n` +
          `Method: CloudFormation Role\n` +
          `Region: ${result.account.region}\n` +
          `Account ID: ${result.account.accountId}\n\n` +
          `Capabilities:\n${result.capabilities?.map((cap: string) => `• ${cap}`).join('\n') || '• Basic AWS access'}\n\n` +
          `You can now run queries like:\n• "get me the cost of may"\n• "list my EC2 instances"\n• "show EKS clusters"`;

        alert(successMessage);
        onBack(); // Go back to account list

      } else if (connectionMethod === 'eks') {
        // Use EKS-specific endpoint for cluster registration
        const response = await fetch(`${API_BASE_URL}/api/accounts/eks/register`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: accountName || 'EKS Cluster',
            clusterName: (awsConfig as any).clusterName,
            region: awsConfig.region,
            awsAccountId: (awsConfig as any).accountId,
            roleArn: awsConfig.roleArn,
            externalId: awsConfig.externalId,
            accessLevel: (awsConfig as any).accessLevel || 'Admin'
          })
        });

        const result = await response.json();

        if (!response.ok) {
          let errorDetails = result.message || 'Failed to register EKS cluster';
          if (result.error) {
            if (result.error.includes('AccessDenied')) {
              errorDetails += '\n\nPossible causes:\n• EKS access entry not configured\n• Role ARN is incorrect\n• External ID mismatch';
            }
          }
          setErrorMessage(errorDetails);
          return;
        }

        // Success
        setErrorMessage(null);
        const successMessage = `✅ EKS Cluster Connected Successfully!\n\n` +
          `Cluster: ${result.cluster.name}\n` +
          `EKS Cluster Name: ${result.cluster.clusterName}\n` +
          `Region: ${result.cluster.region}\n` +
          `Access Method: IAM Role + EKS Access Entry (Auto-created)\n\n` +
          `✨ DevOpsX automatically created the EKS access entry for you!\n` +
          `No manual AWS CLI commands required.\n\n` +
          `You can now run queries like:\n• "list pods in ${result.cluster.clusterName}"\n• "show node status"\n• "get deployments"`;

        alert(successMessage);
        onBack(); // Go back to account list

      } else {
        // Use generic endpoint for other connection methods
        const accountData = {
          name: accountName || `AWS Account`,
          type: 'aws',
          connectionMethod,
          region: awsConfig.region,
          accountId: awsConfig.accountId,
          config: awsConfig
        };

        const result = await onSave(accountData);

        if (!result.success) {
          setErrorMessage(result.message);
        }
      }
    } catch (error) {
      setErrorMessage('Failed to connect account');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-3">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faUndo} />
        </button>
        <h3 className="text-lg font-medium text-gray-900">Connect AWS Account</h3>
      </div>

      {!connectionMethod ? (
        <div className="space-y-4">
          <p className="text-gray-600 mb-3">Choose how you'd like to connect your AWS account:</p>

          <div className="space-y-3">
            <button
              onClick={() => setConnectionMethod('cloudformation')}
              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-start space-x-3">
                <FontAwesomeIcon icon={faKey} className="text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">CloudFormation (Recommended)</h4>
                  <p className="text-xs text-gray-500 mt-1">Deploy our secure CloudFormation template to create an IAM role with least-privilege access</p>
                  <div className="text-xs text-green-600 mt-2">✓ Most secure • ✓ Easy setup • ✓ No secrets stored</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => setConnectionMethod('iam')}
              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors"
            >
              <div className="flex items-start space-x-3">
                <FontAwesomeIcon icon={faKey} className="text-orange-600 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">IAM Access Keys</h4>
                  <p className="text-xs text-gray-500 mt-1">Use existing IAM user access keys (requires programmatic access)</p>
                  <div className="text-xs text-orange-600 mt-2">⚠ Requires manual IAM setup</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      ) : connectionMethod === 'cloudformation' ? (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Step 1: Deploy CloudFormation Template</h4>
            <p className="text-sm text-blue-800 mb-3">Click the button below to deploy our pre-configured CloudFormation template in your AWS account.</p>

            <div className="flex items-center space-x-2">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();

                  // Try to personalize with user info
                  let userId = 'user';
                  try {
                    const token = localStorage.getItem('auth_token');
                    if (token) {
                      const payload = JSON.parse(atob(token.split('.')[1]));
                      userId = payload.id || payload.userId || 'user';
                    }
                  } catch (error) {
                    console.log('Using default user ID');
                  }

                  // Use proper S3 URL format that AWS CloudFormation accepts
                  const templateUrl = 'https://s3.amazonaws.com/soc.gg/cloudformation/devopsx-automated-setup.json';

                  // Create AWS CloudFormation URL with JSON template
                  const deployUrl = `https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=${encodeURIComponent(templateUrl)}&stackName=DevOpsX-Access-Role&param_UserIdentifier=${userId}&param_AccountName=DevOpsX-${userId}&param_ExternalID=devopsx-${userId}-DevOpsX-Account`;

                  window.open(deployUrl, '_blank');
                }}
                className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                <span>Deploy to AWS</span>
              </a>

              <button
                onClick={async () => {
                  try {
                    // Fetch template from backend API
                    const token = localStorage.getItem('auth_token');
                    const response = await fetch(`${API_BASE_URL}/api/accounts/aws/cloudformation-template?accountName=DevOpsX-Account&templateType=automated`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await response.json();

                    if (!response.ok) {
                      throw new Error(data.message || 'Failed to fetch template');
                    }

                    // Create and show modal with template
                    const modal = document.createElement('div');
                    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                    modal.innerHTML = `
                      <div class="bg-white rounded-lg max-w-4xl w-full max-h-90vh overflow-hidden flex flex-col">
                        <div class="flex justify-between items-center p-4 border-b">
                          <h3 class="text-lg font-semibold">CloudFormation Template Preview</h3>
                          <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                        </div>
                        <div class="flex-1 overflow-auto p-4">
                          <pre class="bg-gray-100 p-4 rounded text-sm overflow-auto">${data.template.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
                        </div>
                        <div class="p-4 border-t">
                          <button onclick="navigator.clipboard.writeText(\`${data.template.replace(/`/g, '\\`')}\`).then(() => alert('Template copied to clipboard!'))" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mr-2">Copy Template</button>
                          <button onclick="this.closest('.fixed').remove()" class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded">Close</button>
                        </div>
                      </div>
                    `;
                    document.body.appendChild(modal);
                  } catch (error) {
                    alert('Error loading template preview: ' + (error instanceof Error ? error.message : 'Unknown error'));
                  }
                }}
                className="inline-flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded text-xs"
                title="Preview CloudFormation template"
              >
                <span>Preview</span>
              </button>
            </div>

            <div className="mt-3 text-xs text-blue-700">
              <p>This template creates:</p>
              <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                <li>DevopsX-Role with read-only permissions</li>
                <li>Cost and Usage Report (CUR) configuration</li>
                <li>S3 bucket for billing data</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Account Name</label>
              <input
                type="text"
                placeholder="e.g., Production Account"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">IAM Role ARN</label>
              <input
                type="text"
                placeholder="arn:aws:iam::763301070756:role/DevOpsX-Role-user"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={awsConfig.roleArn || ''}
                onChange={(e) => {
                  const roleArn = e.target.value;
                  // Auto-extract account ID from role ARN
                  const accountIdMatch = roleArn.match(/arn:aws:iam::(\d{12}):/);
                  const extractedAccountId = accountIdMatch ? accountIdMatch[1] : '';

                  setAWSConfig({
                    ...awsConfig,
                    roleArn,
                    accountId: extractedAccountId
                  });
                }}
              />
              <p className="text-xs text-gray-500 mt-1">Copy this from the CloudFormation outputs after deployment</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">External ID</label>
              <input
                type="text"
                placeholder="devopsx-user-DevOpsX-Account"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={awsConfig.externalId || ''}
                onChange={(e) => setAWSConfig({ ...awsConfig, externalId: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">This was specified when creating the CloudFormation stack</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Region</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={awsConfig.region}
                onChange={(e) => setAWSConfig({ ...awsConfig, region: e.target.value })}
              >
                <option value="us-east-1">us-east-1 (N. Virginia)</option>
                <option value="us-east-2">us-east-2 (Ohio)</option>
                <option value="us-west-1">us-west-1 (N. California)</option>
                <option value="us-west-2">us-west-2 (Oregon)</option>
                <option value="eu-west-1">eu-west-1 (Ireland)</option>
                <option value="eu-central-1">eu-central-1 (Frankfurt)</option>
              </select>
            </div>


            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setConnectionMethod(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isConnecting}
              >
                Back
              </button>
              <button
                onClick={handleConnectAccount}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
                disabled={!awsConfig.roleArn || !awsConfig.externalId || !accountName || isConnecting}
              >
                {isConnecting ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faSave} />
                )}
                <span>{isConnecting ? 'Connecting...' : 'Connect Account'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : connectionMethod === 'eks' ? (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-medium text-indigo-900 mb-2">Step 1: Deploy EKS Access Entry CloudFormation Template</h4>
            <p className="text-sm text-indigo-800 mb-3">This template will create an IAM role in your account and configure EKS access entries for DevOpsX to securely access your cluster.</p>

            <div className="flex items-center space-x-2">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();

                  const token = localStorage.getItem('auth_token');
                  console.log('Auth token found:', token ? 'YES' : 'NO');
                  console.log('Token length:', token?.length);

                  if (!token) {
                    alert('Please login first');
                    return;
                  }

                  const clusterName = (awsConfig as any).clusterName || '';
                  const accessLevel = (awsConfig as any).accessLevel || 'Admin';

                  if (!clusterName) {
                    alert('Please enter cluster name first');
                    return;
                  }

                  console.log('Fetching EKS template for:', clusterName, accessLevel);

                  // Fetch EKS CloudFormation template from API
                  fetch(`${API_BASE_URL}/api/accounts/eks/cloudformation-template?clusterName=${encodeURIComponent(clusterName)}&accessLevel=${accessLevel}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  })
                    .then(res => {
                      if (!res.ok) {
                        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                      }
                      return res.json();
                    })
                    .then(data => {
                      if (data.template) {
                        // Use S3 URL - same as AWS account onboarding
                        const templateUrl = 'https://s3.amazonaws.com/soc.gg/cloudformation/eks/eks-access-entry-template.yaml';

                        // Create AWS CloudFormation URL with template pre-loaded
                        const region = awsConfig.region || 'us-east-1';
                        const stackName = `DevOpsX-EKS-${clusterName.replace(/[^a-zA-Z0-9-]/g, '-')}`;
                        const userId = data.parameters?.UserIdentifier || 'user';

                        const deployUrl = `https://console.aws.amazon.com/cloudformation/home?region=${region}#/stacks/create/review` +
                          `?templateURL=${encodeURIComponent(templateUrl)}` +
                          `&stackName=${encodeURIComponent(stackName)}` +
                          `&param_ClusterName=${encodeURIComponent(clusterName)}` +
                          `&param_DevOpsXPrincipalArn=${encodeURIComponent('arn:aws:iam::195275632101:role/DevOpsX-Backend-Service-Role')}` +
                          `&param_AccessType=EC2_LINUX` +
                          `&param_AccessPolicyLevel=${encodeURIComponent(accessLevel)}` +
                          `&param_UserIdentifier=${encodeURIComponent(userId)}` +
                          `&param_AccountName=${encodeURIComponent(accountName || `eks-${clusterName}`)}`;

                        // Show modal with template preview and deploy button
                        const modal = document.createElement('div');
                        modal.innerHTML = `
                        <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 20px;">
                          <div style="background: white; border-radius: 12px; max-width: 900px; max-height: 90vh; overflow-y: auto; padding: 24px;">
                            <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 16px;">EKS CloudFormation Template</h3>
                            <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 12px; margin-bottom: 16px;">
                              <p style="font-size: 14px; color: #1e40af; margin: 0;"><strong>Stack Name:</strong> ${stackName}</p>
                              <p style="font-size: 14px; color: #1e40af; margin: 4px 0 0 0;"><strong>Region:</strong> ${region}</p>
                            </div>
                            <div style="margin-bottom: 16px;">
                              <label style="display: block; font-size: 13px; font-weight: 500; margin-bottom: 8px;">Template Preview:</label>
                              <pre style="background: #f5f5f5; padding: 16px; border-radius: 8px; overflow: auto; max-height: 400px; margin: 0; font-size: 11px; border: 1px solid #e5e7eb;">${data.template.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
                            </div>
                            <div style="display: flex; gap: 12px;">
                              <button onclick="this.closest('div').parentElement.remove()" style="padding: 10px 20px; background: #6b7280; color: white; border-radius: 6px; border: none; cursor: pointer; font-size: 14px;">Cancel</button>
                              <a href="${deployUrl}" target="_blank" onclick="setTimeout(() => this.closest('div').parentElement.remove(), 500)" style="padding: 10px 20px; background: #3b82f6; color: white; border-radius: 6px; text-decoration: none; display: inline-block; font-size: 14px;">Deploy to AWS CloudFormation →</a>
                            </div>
                          </div>
                        </div>
                      `;
                        document.body.appendChild(modal);
                      }
                    })
                    .catch(err => {
                      console.error('Template fetch error:', err);
                      alert('Failed to fetch template: ' + err.message + '\n\nPlease check:\n1. You are logged in\n2. Backend service is running\n3. Check browser console for details');
                    });
                }}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                title="Preview CloudFormation template"
              >
                <FontAwesomeIcon icon={faKey} className="mr-2" />
                Get EKS CloudFormation Template
              </a>
            </div>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Cluster Display Name</label>
              <input
                type="text"
                placeholder="e.g., Production EKS Cluster"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">EKS Cluster Name</label>
              <input
                type="text"
                placeholder="e.g., my-cluster"
                value={(awsConfig as any).clusterName || ''}
                onChange={(e) => setAWSConfig(prev => ({ ...prev, clusterName: e.target.value } as any))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">The actual name of your EKS cluster in AWS</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">AWS Region</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={awsConfig.region}
                onChange={(e) => setAWSConfig({ ...awsConfig, region: e.target.value })}
              >
                <option value="us-east-1">us-east-1 (N. Virginia)</option>
                <option value="us-east-2">us-east-2 (Ohio)</option>
                <option value="us-west-1">us-west-1 (N. California)</option>
                <option value="us-west-2">us-west-2 (Oregon)</option>
                <option value="eu-west-1">eu-west-1 (Ireland)</option>
                <option value="eu-central-1">eu-central-1 (Frankfurt)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Access Level</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={(awsConfig as any).accessLevel || 'Admin'}
                onChange={(e) => setAWSConfig(prev => ({ ...prev, accessLevel: e.target.value } as any))}
              >
                <option value="ClusterAdmin">Cluster Admin (Full Access)</option>
                <option value="Admin">Admin (Recommended)</option>
                <option value="Edit">Edit (No RBAC)</option>
                <option value="View">View (Read-Only)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Level of access DevOpsX will have to your cluster</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 mb-2">Step 2: After CloudFormation Deployment</h5>
              <p className="text-sm text-gray-700 mb-2">Once you've deployed the CloudFormation stack, enter the following details from the stack outputs:</p>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">Role ARN</label>
                <input
                  type="text"
                  placeholder="arn:aws:iam::123456789012:role/DevOpsX-EKS-Role-..."
                  value={awsConfig.roleArn || ''}
                  onChange={(e) => setAWSConfig({ ...awsConfig, roleArn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">Copy this from the CloudFormation stack outputs</p>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">External ID</label>
                <input
                  type="text"
                  placeholder="devopsx-..."
                  value={awsConfig.externalId || ''}
                  onChange={(e) => setAWSConfig({ ...awsConfig, externalId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">Copy this from the CloudFormation stack outputs</p>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">AWS Account ID</label>
                <input
                  type="text"
                  placeholder="123456789012"
                  value={(awsConfig as any).accountId || ''}
                  onChange={(e) => setAWSConfig(prev => ({ ...prev, accountId: e.target.value } as any))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setConnectionMethod(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isConnecting}
              >
                Back
              </button>
              <button
                onClick={handleConnectAccount}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
                disabled={!awsConfig.roleArn || !awsConfig.externalId || !accountName || !(awsConfig as any).clusterName || isConnecting}
              >
                {isConnecting ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faSave} />
                )}
                <span>{isConnecting ? 'Connecting...' : 'Register EKS Cluster'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-medium text-orange-900 mb-2">IAM Access Keys Setup</h4>
            <p className="text-sm text-orange-800">Ensure your IAM user has the necessary permissions for cost optimization analysis.</p>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Account Name</label>
              <input
                type="text"
                placeholder="e.g., Production Account"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Access Key ID</label>
              <input
                type="text"
                placeholder="AKIA..."
                value={awsConfig.accessKey || ''}
                onChange={(e) => setAWSConfig(prev => ({ ...prev, accessKey: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Secret Access Key</label>
              <input
                type="password"
                placeholder="Enter secret key"
                value={awsConfig.secretKey || ''}
                onChange={(e) => setAWSConfig(prev => ({ ...prev, secretKey: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Region</label>
              <select
                value={awsConfig.region || 'us-east-1'}
                onChange={(e) => setAWSConfig(prev => ({ ...prev, region: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="us-east-1">us-east-1 (N. Virginia)</option>
                <option value="us-east-2">us-east-2 (Ohio)</option>
                <option value="us-west-1">us-west-1 (N. California)</option>
                <option value="us-west-2">us-west-2 (Oregon)</option>
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setConnectionMethod(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleConnectAccount}
                disabled={isConnecting || !awsConfig.accessKey || !awsConfig.secretKey}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faSave} />
                )}
                <span>{isConnecting ? 'Connecting...' : 'Connect Account'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// GCP Account Setup Component
const GCPAccountSetup: React.FC<{ onBack: () => void; onSave: (data: any) => Promise<any> }> = ({ onBack, onSave }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-3">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faUndo} />
        </button>
        <h3 className="text-lg font-medium text-gray-900">Connect GCP Account</h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Project Name</label>
          <input
            type="text"
            placeholder="e.g., Production GCP"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Project ID</label>
          <input
            type="text"
            placeholder="my-project-123456"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Service Account Email</label>
          <input
            type="email"
            placeholder="service-account@my-project.iam.gserviceaccount.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Service Account Key (JSON)</label>
          <textarea
            rows={6}
            placeholder="Paste your service account key JSON here..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Your service account needs Compute Viewer, Cloud Asset Viewer, and Billing Account Viewer permissions</p>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={onBack}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2">
            <FontAwesomeIcon icon={faSave} />
            <span>Connect Account</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// GitHub Account Setup Component
const GitHubAccountSetup: React.FC<{ onBack: () => void; onSave: (data: any) => Promise<any> }> = ({ onBack, onSave }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-3">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faUndo} />
        </button>
        <h3 className="text-lg font-medium text-gray-900">Connect GitHub Account</h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Organization Name</label>
          <input
            type="text"
            placeholder="e.g., my-organization"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Personal Access Token</label>
          <input
            type="password"
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Token needs: repo, admin:org, read:user permissions.
            <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
              Create token <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
            </a>
          </p>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={onBack}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2">
            <FontAwesomeIcon icon={faSave} />
            <span>Connect Account</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Jenkins Account Setup Component
const JenkinsAccountSetup: React.FC<{ onBack: () => void; onSave: (data: any) => Promise<any> }> = ({ onBack, onSave }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-3">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faUndo} />
        </button>
        <h3 className="text-lg font-medium text-gray-900">Connect Jenkins Server</h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Server Name</label>
          <input
            type="text"
            placeholder="e.g., Production Jenkins"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Jenkins URL</label>
          <input
            type="url"
            placeholder="https://jenkins.example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Username</label>
          <input
            type="text"
            placeholder="jenkins-user"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">API Token</label>
          <input
            type="password"
            placeholder="Enter Jenkins API token"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Generate API token in Jenkins: User Menu → Configure → API Token
          </p>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={onBack}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2">
            <FontAwesomeIcon icon={faSave} />
            <span>Connect Server</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App Component with Authentication Guard
const AuthenticatedApp = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure all state is initialized
    setTimeout(() => setIsReady(true), 100);
  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4 mx-auto"></div>
          <p className="text-gray-600">Loading DevOpsX...</p>
        </div>
      </div>
    );
  }

  return <App />;
};

export default AuthenticatedApp;
